import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import ShiftTable from '../components/ShiftTable';
import CriteriaDetail from '../components/CriteriaDetail';
import { fmtShiftType, fmtRequestDate, fmtTimeRange, todayPacific } from '../utils/shiftRequests';

const fmt = (n) => `$${(n || 0).toFixed(2)}`;
// YYYY-MM-DD -> M/D, for renewal dates coming back from the API.
const fmtISO = (d) => { if (!d) return ''; const [y, m, day] = String(d).slice(0, 10).split('-'); return y ? `${parseInt(m)}/${parseInt(day)}` : ''; };

// Days from today until a date, both normalised to UTC so this agrees with the
// backend's expiry math (utils/payPeriod toDateStr / todayUTCStr).
function daysUntil(value) {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  const target = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.round((target - today) / 86400000);
}

// Expiry traffic light: red once inside 3 days (or already gone), yellow inside 10,
// green beyond that. Returns null when no expiry date is set.
function expiryStatus(expdate) {
  const days = daysUntil(expdate);
  if (days === null) return null;
  const when = days < 0 ? `expired ${fmtISO(expdate)}`
    : days === 0 ? `expires today`
    : `exp ${fmtISO(expdate)}`;
  if (days <= 3) return { days, label: when, color: '#c53030', bg: '#fff5f5' };
  if (days <= 10) return { days, label: when, color: '#975a16', bg: '#fefcbf' };
  return { days, label: when, color: '#276749', bg: '#f0fff4' };
}

// Payments are entered in whole dollars, in a text input (not type="number") so no
// browser draws spinner arrows; inputMode keeps the numeric keypad on phones.
//
// A decimal point is never kept, but it cannot simply be deleted either: pasting
// "$710.50" off a Zelle notification would collapse to "71050" and charge 100x. So
// anything with a decimal point is read as a number and rounded, and only a value
// with no point at all is treated as plain digits.
const onlyDigits = (v) => {
  const cleaned = String(v).replace(/[^\d.]/g, '');
  if (!cleaned) return '';
  if (!cleaned.includes('.')) return cleaned;
  const n = Math.round(parseFloat(cleaned));
  return Number.isFinite(n) ? String(Math.max(0, n)) : '';
};

// A client's balance rolls up into a manager's group email via either relationship.
const rollsUpTo = (c) => (c?.managedBy || c?.generatesCommissionFor || '').trim() || null;

// ── Clients Tab ───────────────────────────────────────────────────────────────
const BLANK_FORM = { clientName: '', username: '', billingType: 'perShift', monthlyRate: '', monthlyDueDate: '', hasCommission: false, isCommissionClient: false, password: '', zelleNames: '', active: true, expdate: '', managedBy: '', receivesCommission: false, generatesCommissionFor: '', commissionRate: '', commissionType: '', email: '', emailName: '', autoEmailEnabled: false, reminderSchedule: 'payPeriod', reminderDay: '', renewalLeadDays: '', runAlertEmailEnabled: false, requestsEnabled: false, fleetEnabled: false };

const BLANK_PROP = { clientid: '', user: '', pin: '', version: '6.1.1', Commission: 'No' };

// A collapsible block of the client detail form. Sections keep the long form
// scannable — you open the part you came for instead of reading past all of it.
function Section({ title, hint, badge, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="card" style={{ marginBottom: 10, padding: 0, overflow: 'hidden' }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '11px 16px',
          cursor: 'pointer', userSelect: 'none', background: open ? '#f7f8fa' : '#fff',
          borderBottom: open ? '1px solid #edf2f7' : 'none'
        }}
      >
        <span style={{ fontSize: 12, color: '#a0aec0', width: 10 }}>{open ? '▾' : '▸'}</span>
        <strong style={{ fontSize: 14 }}>{title}</strong>
        {badge}
        {hint && <span style={{ fontSize: 12, color: '#a0aec0', marginLeft: 'auto', textAlign: 'right' }}>{hint}</span>}
      </div>
      {open && <div style={{ padding: 16 }}>{children}</div>}
    </div>
  );
}

const chip = (text, color, bg) => (
  <span style={{ fontSize: 11, fontWeight: 600, color, background: bg, borderRadius: 10, padding: '2px 8px', whiteSpace: 'nowrap' }}>{text}</span>
);

function ClientsTab() {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(BLANK_FORM);
  const [properties, setProperties] = useState([]);
  const [originalUsernames, setOriginalUsernames] = useState([]);
  const [editId, setEditId] = useState(null);
  // null = browsing the list; 'new' or a client id = the detail pane is open.
  const [openFor, setOpenFor] = useState(null);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [msg, setMsg] = useState('');
  const [switchForm, setSwitchForm] = useState({ newBillingType: 'perMonth', oldPlanEndDate: '', newPlanStartDate: '', monthlyRate: '' });
  const [switchMsg, setSwitchMsg] = useState('');
  const [switchBusy, setSwitchBusy] = useState(false);
  const [reminderMsg, setReminderMsg] = useState('');
  const [reminderBusy, setReminderBusy] = useState(false);
  const [reminderInfo, setReminderInfo] = useState(null);

  // Bills carry the amounts block from backend utils/balanceCalc, so due and balance
  // here match the Bills tab, the portal and the reminder emails.
  const [amountsByClient, setAmountsByClient] = useState({});

  useEffect(() => {
    api.getClients().then(setClients).catch(console.error);
    api.getBills()
      .then(bs => setAmountsByClient(Object.fromEntries(bs.map(b => [b.clientName, b.amounts]))))
      .catch(console.error);
  }, []);

  function startEdit(c) {
    setEditId(c._id);
    setOpenFor(c._id);
    setForm({ ...BLANK_FORM, ...c, password: '', zelleNames: (c.zelleNames || []).join(', '), expdate: c.expdate ? new Date(c.expdate).toISOString().split('T')[0] : '', commissionRate: c.commissionRate ?? '', commissionType: c.commissionType || '', email: c.email || '', emailName: c.emailName || '', autoEmailEnabled: !!c.autoEmailEnabled, runAlertEmailEnabled: !!c.runAlertEmailEnabled, requestsEnabled: !!c.requestsEnabled, fleetEnabled: !!c.fleetEnabled, reminderSchedule: c.reminderSchedule || 'payPeriod', reminderDay: c.reminderDay ?? '', renewalLeadDays: c.renewalLeadDays ?? '' });
    setMsg('');
    setSwitchForm({ newBillingType: c.billingType === 'perMonth' ? 'perShift' : 'perMonth', oldPlanEndDate: '', newPlanStartDate: '', monthlyRate: c.monthlyRate || '' });
    setSwitchMsg('');
    setReminderMsg('');
    setReminderInfo(null);
    const propertyUsernames = (c.properties || []).map(p => p.user).filter(Boolean);
    api.findClientUsers(c.clientName, propertyUsernames).then(users => {
      const list = Array.isArray(users) ? users : users ? [users] : [];
      const userMap = {};
      list.forEach(u => { if (u?.username) userMap[u.username] = u; });
      const enriched = (c.properties || []).map(p => ({
        clientid: p.clientid || '',
        user: p.user || '',
        pin: userMap[p.user]?.pin || '',
        version: userMap[p.user]?.version || '6.1.1',
        Commission: userMap[p.user]?.Commission || 'No'
      }));
      setProperties(enriched);
      setOriginalUsernames([...new Set(enriched.map(p => p.user).filter(Boolean))]);
      if (list.length > 0 && list[0].expdate) {
        f('expdate', new Date(list[0].expdate).toISOString().split('T')[0]);
      }
    }).catch(() => {
      const fallback = (c.properties || []).map(p => ({ ...BLANK_PROP, clientid: p.clientid, user: p.user }));
      setProperties(fallback);
      setOriginalUsernames([...new Set(fallback.map(p => p.user).filter(Boolean))]);
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // Opens the detail pane blank, for adding someone new.
  function startCreate() {
    setEditId(null);
    setOpenFor('new');
    setForm(BLANK_FORM);
    setProperties([]);
    setOriginalUsernames([]);
    setMsg('');
    setSwitchMsg('');
    setReminderMsg('');
    setReminderInfo(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // Closes the detail pane and returns to the list.
  function cancelEdit() {
    setEditId(null);
    setOpenFor(null);
    setForm(BLANK_FORM);
    setProperties([]);
    setOriginalUsernames([]);
    setReminderMsg('');
    setReminderInfo(null);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      const validProps = properties.filter(p => p.clientid && p.user);
      const payload = {
        ...form,
        zelleNames: form.zelleNames.split(',').map(s => s.trim()).filter(Boolean),
        properties: validProps.map(p => ({ clientid: p.clientid, user: p.user })),
        monthlyRate: form.monthlyRate ? parseFloat(form.monthlyRate) : null,
        monthlyDueDate: form.monthlyDueDate ? parseInt(form.monthlyDueDate) : null,
        commissionRate: form.commissionRate !== '' ? parseFloat(form.commissionRate) : null,
        commissionType: form.commissionType || null,
        generatesCommissionFor: form.generatesCommissionFor?.trim() || null,
        managedBy: form.managedBy?.trim() || null,
        email: form.email?.trim() || null,
        emailName: form.emailName?.trim() || null,
        autoEmailEnabled: !!form.autoEmailEnabled,
        runAlertEmailEnabled: !!form.runAlertEmailEnabled,
        requestsEnabled: !!form.requestsEnabled,
        fleetEnabled: !!form.fleetEnabled,
        reminderSchedule: form.reminderSchedule || 'payPeriod',
        reminderDay: form.reminderDay !== '' ? parseInt(form.reminderDay) : null,
        renewalLeadDays: form.renewalLeadDays !== '' ? parseInt(form.renewalLeadDays) : null
      };

      // Save user account data per unique username
      const uniqueUsers = {};
      validProps.forEach(p => {
        if (p.user && !uniqueUsers[p.user]) {
          uniqueUsers[p.user] = { pin: p.pin, version: p.version, type: p.type, Commission: p.Commission };
        }
      });
      const userType = payload.billingType === 'perMonth' ? 'Subscription' : 'Per Shift';
      for (const [username, userData] of Object.entries(uniqueUsers)) {
        const withType = { ...userData, type: userType, name: payload.clientName };
        try {
          await api.updateUser(username, withType);
        } catch {
          await api.createUser({ username, ...withType, expdate: payload.expdate || undefined }).catch(() => {});
        }
      }

      // Delete users that were removed
      const currentUsernames = new Set(Object.keys(uniqueUsers));
      for (const username of originalUsernames) {
        if (!currentUsernames.has(username)) {
          await api.deleteUser(username).catch(() => {});
        }
      }
      if (!payload.password) delete payload.password;

      if (editId) {
        const updated = await api.updateClient(editId, payload);
        setClients(cs => cs.map(c => c._id === editId ? updated : c));
        setMsg('Client updated.');
        cancelEdit();
      } else {
        const created = await api.createClient(payload);
        setClients(cs => [...cs, created]);
        setMsg('Client created.');
        cancelEdit();
      }
    } catch (err) { setMsg(`Error: ${err.message}`); }
  }

  const f = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const sf = (k, v) => setSwitchForm(prev => ({ ...prev, [k]: v }));

  async function handleSwitchBillingType() {
    if (!editId) return;
    if (!switchForm.oldPlanEndDate || !switchForm.newPlanStartDate) {
      setSwitchMsg('Error: both dates are required.');
      return;
    }
    if (!window.confirm(
      `Switch ${form.clientName} from ${form.billingType === 'perMonth' ? 'Per Month' : 'Per Shift'} to ` +
      `${switchForm.newBillingType === 'perMonth' ? 'Per Month' : 'Per Shift'}?\n\n` +
      `Old plan ends: ${switchForm.oldPlanEndDate}\nNew plan starts: ${switchForm.newPlanStartDate}\n\n` +
      `Any balance currently owed will carry forward as debt on the new plan.`
    )) return;

    setSwitchBusy(true);
    setSwitchMsg('');
    try {
      const payload = {
        newBillingType: switchForm.newBillingType,
        oldPlanEndDate: switchForm.oldPlanEndDate,
        newPlanStartDate: switchForm.newPlanStartDate
      };
      if (switchForm.newBillingType === 'perMonth' && switchForm.monthlyRate) {
        payload.monthlyRate = parseFloat(switchForm.monthlyRate);
      }
      const { client: updated, bill } = await api.switchBillingType(editId, payload);
      setClients(cs => cs.map(c => c._id === editId ? updated : c));
      startEdit(updated);
      setSwitchMsg(
        `Switched to ${switchForm.newBillingType === 'perMonth' ? 'Per Month' : 'Per Shift'}.` +
        (bill?.carryoverBalance ? ` Carried forward balance: ${fmt(bill.carryoverBalance)}.` : '')
      );
    } catch (err) {
      setSwitchMsg(`Error: ${err.message}`);
    } finally {
      setSwitchBusy(false);
    }
  }

  async function loadReminderPreview() {
    if (!editId) return;
    setReminderBusy(true);
    setReminderMsg('');
    try {
      setReminderInfo(await api.getReminderPreview(editId));
    } catch (err) {
      setReminderMsg(`Error: ${err.message}`);
    } finally { setReminderBusy(false); }
  }

  async function handleSendReminderNow() {
    if (!editId) return;
    if (!window.confirm(`Send a payment reminder email to ${form.email} right now?`)) return;
    setReminderBusy(true);
    setReminderMsg('');
    try {
      const r = await api.sendReminderNow(editId);
      setReminderMsg(`Reminder sent to ${r.to} — ${fmt(r.amountDue)} due, ${fmt(r.totalBalance)} total balance.`);
    } catch (err) {
      setReminderMsg(`Error: ${err.message}`);
    } finally { setReminderBusy(false); }
  }

  // ── List view ──────────────────────────────────────────────────────────────
  const q = search.trim().toLowerCase();
  const visible = clients
    .filter(c => showInactive || c.active !== false)
    .filter(c => !q || [
      c.clientName, c.username, c.email, rollsUpTo(c),
      (c.zelleNames || []).join(' '), (c.properties || []).map(p => p.clientid).join(' ')
    ].some(v => (v || '').toLowerCase().includes(q)))
    .sort((a, b) => a.clientName.localeCompare(b.clientName));
  const inactiveCount = clients.filter(c => c.active === false).length;

  if (!openFor) {
    return (
      <div>
        {msg && <div className={`alert ${msg.startsWith('Error') ? 'alert-error' : 'alert-success'}`}>{msg}</div>}

        <div className="card" style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search name, login, email, property, Zelle…"
              style={{ flex: 1, minWidth: 220 }}
            />
            <button
              type="button"
              className={`toggle-btn${showInactive ? ' active' : ''}`}
              onClick={() => setShowInactive(p => !p)}
            >
              {showInactive ? 'Hide inactive' : `Show inactive (${inactiveCount})`}
            </button>
            <button type="button" className="btn btn-primary btn-sm" onClick={startCreate}>+ New Client</button>
          </div>
          <div style={{ fontSize: 12, color: '#a0aec0', marginTop: 8 }}>
            Showing {visible.length} of {clients.length} — click a name to open their details.
          </div>
        </div>

        {visible.map(c => {
          const manager = rollsUpTo(c);
          const props = (c.properties || []).map(p => p.clientid).filter(Boolean);
          const exp = expiryStatus(c.expdate);
          const amt = amountsByClient[c.clientName];
          return (
            <div
              key={c._id}
              className="card"
              onClick={() => startEdit(c)}
              style={{ marginBottom: 6, padding: '10px 14px', cursor: 'pointer', opacity: c.active === false ? 0.55 : 1 }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <strong style={{ fontSize: 15 }}>{c.clientName}</strong>
                {c.billingType === 'perMonth'
                  ? chip(`Monthly${c.monthlyRate ? ` · $${c.monthlyRate}` : ''}`, '#2c5282', '#ebf8ff')
                  : chip('Per Shift', '#4a5568', '#edf2f7')}
                {c.active === false && chip('Inactive', '#c53030', '#fff5f5')}
                {manager && chip(`via ${manager}`, '#2b6cb0', '#ebf8ff')}
                {c.receivesCommission && chip('Receives commission', '#276749', '#f0fff4')}
                {exp && chip(exp.label, exp.color, exp.bg)}

                <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
                  {c.autoEmailEnabled
                    ? chip('Email ON', '#276749', '#f0fff4')
                    : chip('Email OFF', '#a0aec0', '#f7f8fa')}
                  {!c.portalPassword && chip('No password', '#c53030', '#fff5f5')}
                  {props.length === 0 && chip('No properties', '#c53030', '#fff5f5')}
                  <span style={{ fontSize: 13, color: '#cbd5e0' }}>›</span>
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 3, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, color: '#a0aec0' }}>
                  {c.username || c.clientName}
                  {c.email ? ` · ${c.email}` : ''}
                  {props.length ? ` · ${props.join(', ')}` : ''}
                </span>
                {amt && (
                  <span style={{ marginLeft: 'auto', display: 'flex', gap: 12, fontSize: 12, whiteSpace: 'nowrap' }}>
                    <span style={{ color: amt.amountDue > 0 ? '#c53030' : '#a0aec0', fontWeight: amt.amountDue > 0 ? 700 : 400 }}>
                      Due {fmt(amt.amountDue)}
                    </span>
                    <span style={{ color: amt.totalBalance > 0 ? '#4a5568' : '#a0aec0' }}>
                      Balance {fmt(amt.totalBalance)}
                    </span>
                    {amt.nextCharge > 0 && (
                      <span style={{ color: '#a0aec0' }}>Next {fmt(amt.nextCharge)}</span>
                    )}
                  </span>
                )}
              </div>
            </div>
          );
        })}

        {visible.length === 0 && (
          <div className="card" style={{ color: '#a0aec0', fontSize: 13 }}>
            No clients match “{search}”.
          </div>
        )}
      </div>
    );
  }

  // ── Detail view ────────────────────────────────────────────────────────────
  const manager = rollsUpTo(form);
  const generators = editId ? clients.filter(c => c.generatesCommissionFor === form.clientName) : [];
  const emailHint = form.autoEmailEnabled
    ? (form.reminderSchedule === 'specificDate'
        ? `on day ${form.reminderDay || form.monthlyDueDate || '—'} each month`
        : 'every pay period')
    : 'off';

  return (
    <div>
      {msg && <div className={`alert ${msg.startsWith('Error') ? 'alert-error' : 'alert-success'}`}>{msg}</div>}

      {/* autoComplete off + "new-password" below: this is not a login form, and the
          browser was pasting the admin's saved login into a client's username and
          password, which then got saved onto that client. */}
      <form onSubmit={handleSubmit} autoComplete="off">
        {/* Header — identity and the actions, always in reach */}
        <div className="card" style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-secondary btn-sm" onClick={cancelEdit}>← All Clients</button>
            <strong style={{ fontSize: 17 }}>{editId ? form.clientName : 'New Client'}</strong>
            {editId && (form.billingType === 'perMonth'
              ? chip(`Monthly${form.monthlyRate ? ` · $${form.monthlyRate}` : ''}`, '#2c5282', '#ebf8ff')
              : chip('Per Shift', '#4a5568', '#edf2f7'))}
            {manager && chip(`Managed by ${manager}`, '#2b6cb0', '#ebf8ff')}
            {form.active === false && chip('Inactive', '#c53030', '#fff5f5')}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer', fontSize: 13, margin: 0 }}>
                <input type="checkbox" style={{ width: 'auto' }} checked={form.active !== false} onChange={e => f('active', e.target.checked)} />
                Active
              </label>
              <button className="btn btn-primary" type="submit">{editId ? 'Save Changes' : 'Create Client'}</button>
            </div>
          </div>
        </div>

        {/* Profile & login */}
        <Section title="Profile & Login" defaultOpen hint={form.email || 'no email set'}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
            <div className="form-group">
              <label>Display Name</label>
              <input value={form.clientName} onChange={e => f('clientName', e.target.value)} required={!editId} disabled={!!editId} />
            </div>
            <div className="form-group">
              <label>Login Username</label>
              <input name="client-portal-username" autoComplete="off" data-lpignore="true" data-1p-ignore value={form.username || ''} onChange={e => f('username', e.target.value)} placeholder="blank = use display name" />
            </div>
            <div className="form-group">
              <label>Password</label>
              <div style={{ display: 'flex', gap: 6, marginBottom: editId ? 4 : 0 }}>
                <input name="client-portal-password" autoComplete="new-password" data-lpignore="true" data-1p-ignore type={showPassword ? 'text' : 'password'} value={form.password} onChange={e => f('password', e.target.value)} required={!editId} placeholder={editId ? 'blank = keep current' : ''} style={{ flex: 1 }} />
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowPassword(p => !p)}>{showPassword ? 'Hide' : 'Show'}</button>
              </div>
              {editId && (
                <button type="button" className="btn btn-danger btn-sm" onClick={async () => {
                  if (!window.confirm('Delete this client\'s password? They won\'t be able to log in until a new one is set.')) return;
                  try {
                    await api.deleteClientPassword(editId);
                    setClients(cs => cs.map(c => c._id === editId ? { ...c, portalPassword: null } : c));
                    setMsg('Password deleted.');
                  } catch (err) { setMsg(`Error: ${err.message}`); }
                }}>Delete Password</button>
              )}
            </div>
            <div className="form-group">
              <label>Email Address</label>
              <input type="email" value={form.email || ''} onChange={e => f('email', e.target.value)} placeholder="client@example.com" />
            </div>
            <div className="form-group">
              <label>Name for Email <span style={{ fontWeight: 400, color: '#718096' }}>(how they are greeted)</span></label>
              <input value={form.emailName || ''} onChange={e => f('emailName', e.target.value)} placeholder={form.clientName ? `blank = ${form.clientName}` : 'blank = display name'} />
            </div>
            <div className="form-group">
              <label>Zelle Names <span style={{ fontWeight: 400, color: '#718096' }}>(comma separated)</span></label>
              <input value={form.zelleNames} onChange={e => f('zelleNames', e.target.value)} placeholder="John Smith, J Smith" />
            </div>
            <div className="form-group">
              <label>Managed By <span style={{ fontWeight: 400, color: '#718096' }}>(manager's client name)</span></label>
              <input value={form.managedBy || ''} onChange={e => f('managedBy', e.target.value)} placeholder="e.g. Gil" />
            </div>
          </div>
        </Section>

        {/* Billing */}
        <Section
          title="Billing"
          defaultOpen
          hint={form.billingType === 'perMonth'
            ? `$${form.monthlyRate || '—'}/mo${form.monthlyDueDate ? `, due day ${form.monthlyDueDate}` : ''}${form.expdate ? ` · expires ${form.expdate}` : ''}`
            : `per shift${form.expdate ? ` · expires ${form.expdate}` : ''}`}
        >
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Billing Type</label>
              <select value={form.billingType} onChange={e => f('billingType', e.target.value)} style={{ width: 140 }} disabled={!!editId}>
                <option value="perShift">Per Shift</option>
                <option value="perMonth">Per Month</option>
              </select>
              {editId && <div style={{ fontSize: 11, color: '#718096', marginTop: 2 }}>Use "Switch Billing Plan" below to change this.</div>}
            </div>
            {form.billingType === 'perMonth' && (
              <>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Monthly Rate ($)</label>
                  <input type="number" step="0.01" value={form.monthlyRate} onChange={e => f('monthlyRate', e.target.value)} style={{ width: 120 }} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Bill Due (day of month)</label>
                  <input type="number" min="1" max="28" value={form.monthlyDueDate} onChange={e => f('monthlyDueDate', e.target.value)} style={{ width: 100 }} />
                </div>
              </>
            )}
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Expiry Date</label>
              <input type="date" value={form.expdate || ''} onChange={e => f('expdate', e.target.value)} style={{ width: 150 }} />
            </div>
          </div>

          {/* Switch Billing Plan — only for an existing client, keeps balance history intact */}
          {editId && (
            <div style={{ border: '1px dashed #cbd5e0', borderRadius: 8, padding: 12, marginTop: 16 }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Switch Billing Plan</div>
              {switchMsg && <div className={`alert ${switchMsg.startsWith('Error') ? 'alert-error' : 'alert-success'}`} style={{ marginBottom: 8 }}>{switchMsg}</div>}
              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>New Billing Type</label>
                  <select value={switchForm.newBillingType} onChange={e => sf('newBillingType', e.target.value)} style={{ width: 140 }}>
                    <option value="perShift">Per Shift</option>
                    <option value="perMonth">Per Month</option>
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Current Plan End Date</label>
                  <input type="date" value={switchForm.oldPlanEndDate} onChange={e => sf('oldPlanEndDate', e.target.value)} style={{ width: 150 }} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>New Plan Start Date</label>
                  <input type="date" value={switchForm.newPlanStartDate} onChange={e => sf('newPlanStartDate', e.target.value)} style={{ width: 150 }} />
                </div>
                {switchForm.newBillingType === 'perMonth' && (
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Monthly Rate ($)</label>
                    <input type="number" step="0.01" value={switchForm.monthlyRate} onChange={e => sf('monthlyRate', e.target.value)} style={{ width: 120 }} />
                  </div>
                )}
                <button type="button" className="btn btn-secondary btn-sm" disabled={switchBusy} onClick={handleSwitchBillingType}>
                  {switchBusy ? 'Switching...' : 'Switch Plan'}
                </button>
              </div>
              <div style={{ fontSize: 11, color: '#718096', marginTop: 6 }}>
                Any balance still owed under the current plan carries forward as debt on the new plan — nothing is written off.
              </div>
            </div>
          )}
        </Section>

        {/* Automatic payment reminder emails. A managed client has their own
            independent switch — turning it on emails them directly, and their
            manager still receives the group roll-up covering the same balance. */}
        <Section
          title="Payment Reminder Emails"
          hint={emailHint}
          badge={form.autoEmailEnabled ? chip('ON', '#276749', '#f0fff4') : chip('OFF', '#a0aec0', '#f7f8fa')}
        >
          {manager && (
            <div style={{ fontSize: 12, color: '#2c5282', background: '#ebf8ff', borderRadius: 4, padding: '6px 8px', marginBottom: 10 }}>
              This client's balance is already included in <strong>{manager}</strong>'s roll-up email.
              Switch this on as well to email them directly at the same time — both will be sent.
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: form.autoEmailEnabled ? 12 : 0, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0, cursor: 'pointer', fontWeight: 600 }}>
              <input type="checkbox" checked={!!form.autoEmailEnabled} onChange={e => f('autoEmailEnabled', e.target.checked)} />
              Automatic Payment Reminder Emails
            </label>
            {form.autoEmailEnabled && !form.email && (
              <span style={{ fontSize: 12, color: '#c53030' }}>⚠ Add an email address above or nothing will send.</span>
            )}
            {form.autoEmailEnabled && form.reminderSchedule === 'specificDate'
              && !form.reminderDay && !(form.billingType === 'perMonth' && form.monthlyDueDate) && (
              <span style={{ fontSize: 12, color: '#c53030' }}>⚠ Set a day of month or nothing will send.</span>
            )}
          </div>

          {form.autoEmailEnabled && (
            <>
              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Send On</label>
                  <select value={form.reminderSchedule} onChange={e => f('reminderSchedule', e.target.value)} style={{ width: 190 }}>
                    <option value="payPeriod">Every pay period date</option>
                    <option value="specificDate">A specific date each month</option>
                  </select>
                </div>
                {form.reminderSchedule === 'specificDate' && (
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Day of Month</label>
                    <input
                      type="number" min="1" max="28"
                      value={form.reminderDay}
                      onChange={e => f('reminderDay', e.target.value)}
                      placeholder={form.billingType === 'perMonth' && form.monthlyDueDate ? String(form.monthlyDueDate) : '1-28'}
                      style={{ width: 110 }}
                    />
                  </div>
                )}
                {form.billingType === 'perMonth' && (
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Remind before renewal</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input
                        type="number" min="0" max="60"
                        value={form.renewalLeadDays}
                        onChange={e => f('renewalLeadDays', e.target.value)}
                        placeholder="3"
                        style={{ width: 70 }}
                      />
                      <span style={{ fontSize: 12, color: '#718096' }}>days before (blank = 3)</span>
                    </div>
                  </div>
                )}
                {editId && (
                  <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
                    <button type="button" className="btn btn-secondary btn-sm" disabled={reminderBusy} onClick={loadReminderPreview}>
                      {reminderBusy ? 'Working...' : 'Preview'}
                    </button>
                    <button type="button" className="btn btn-primary btn-sm" disabled={reminderBusy || !form.email} onClick={handleSendReminderNow}>
                      Send Now
                    </button>
                  </div>
                )}
              </div>
              <div style={{ fontSize: 12, color: '#718096', marginTop: 8 }}>
                {form.reminderSchedule === 'payPeriod'
                  ? 'Sends at 10:00 AM on each biweekly pay day, covering every shift whose pay date has passed.'
                  : `Sends at 10:00 AM on day ${form.reminderDay || (form.billingType === 'perMonth' && form.monthlyDueDate) || '—'} of each month.`}
                {form.billingType === 'perMonth' && (
                  <> {' '}Also sends {String(form.renewalLeadDays) === '0'
                    ? 'on the subscription expiry date'
                    : `${form.renewalLeadDays === '' ? 3 : form.renewalLeadDays} day${String(form.renewalLeadDays) === '1' ? '' : 's'} before it expires`}
                    {form.expdate ? ` (expires ${form.expdate})` : ''}, telling them to pay to extend.</>
                )}
                {' '}A copy of every reminder is blind-copied to bqtexpress@gmail.com. Saved changes apply from the next run.
              </div>
              {reminderMsg && (
                <div className={`alert ${reminderMsg.startsWith('Error') ? 'alert-error' : 'alert-success'}`} style={{ marginTop: 8, marginBottom: 0 }}>{reminderMsg}</div>
              )}
              {reminderInfo && (
                <div style={{ marginTop: 8, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, padding: 10 }}>
                  {!reminderInfo.hasBill ? (
                    <span style={{ fontSize: 12, color: '#718096' }}>
                      Nothing outstanding, so no reminder would be sent.
                      {reminderInfo.managedBy ? ` This client's balance is also covered by ${reminderInfo.managedBy}'s roll-up.` : ''}
                    </span>
                  ) : (
                    <>
                      <div style={{ fontSize: 12, color: '#4a5568', marginBottom: 6 }}>
                        Due now: <strong>{fmt(reminderInfo.amountDue)}</strong>
                        {reminderInfo.notYetDue > 0 && <> {' + '}Not yet due: <strong>{fmt(reminderInfo.notYetDue)}</strong></>}
                        {' = '}Total balance: <strong>{fmt(reminderInfo.totalBalance)}</strong>
                        {reminderInfo.nextCharge > 0 && <> {' · '}Next payment: <strong>{fmt(reminderInfo.nextCharge)}</strong></>}
                        {' · '}Scheduled today: <strong>{reminderInfo.isReminderDayToday ? 'Yes' : 'No'}</strong>
                        {reminderInfo.lastReminderSent && <> {' · '}Last sent: <strong>{reminderInfo.lastReminderSent}</strong></>}
                      </div>
                      {reminderInfo.managesOthers && (
                        <div style={{ fontSize: 12, color: '#2c5282', background: '#ebf8ff', borderRadius: 4, padding: '6px 8px', marginBottom: 6 }}>
                          Covers {reminderInfo.accounts.length} account{reminderInfo.accounts.length === 1 ? '' : 's'}:{' '}
                          {reminderInfo.accounts.map(a => `${a.label}${a.isSelf ? ' (self)' : ''} ${fmt(a.amountDue)}`).join(' · ')}
                        </div>
                      )}
                      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{reminderInfo.subject}</div>
                      {reminderInfo.previewHtml
                        ? <div style={{ border: '1px solid #edf2f7', borderRadius: 4, padding: 10, background: '#fff' }} dangerouslySetInnerHTML={{ __html: reminderInfo.previewHtml }} />
                        : <pre style={{ margin: 0, fontSize: 11, whiteSpace: 'pre-wrap', color: '#4a5568', fontFamily: 'inherit' }}>{reminderInfo.preview}</pre>}
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </Section>

        {/* Portal Request tab — off unless switched on per client. */}
        <Section
          title="Request Tab"
          hint={form.requestsEnabled ? 'shown in portal' : 'hidden'}
          badge={form.requestsEnabled ? chip('ON', '#276749', '#f0fff4') : chip('OFF', '#a0aec0', '#f7f8fa')}
        >
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0, cursor: 'pointer', fontWeight: 600 }}>
            <input type="checkbox" checked={!!form.requestsEnabled} onChange={e => f('requestsEnabled', e.target.checked)} />
            Can request shifts (shows the Request tab in their portal)
          </label>
        </Section>

        {/* Portal Fleet tab — lets the client remote-control the PCs assigned to them. */}
        <Section
          title="Fleet Tab"
          hint={form.fleetEnabled ? 'shown in portal' : 'hidden'}
          badge={form.fleetEnabled ? chip('ON', '#276749', '#f0fff4') : chip('OFF', '#a0aec0', '#f7f8fa')}
        >
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0, cursor: 'pointer', fontWeight: 600 }}>
            <input type="checkbox" checked={!!form.fleetEnabled} onChange={e => f('fleetEnabled', e.target.checked)} />
            Can control their own machines (shows the Fleet tab in their portal)
          </label>
        </Section>

        {/* Run-started alert emails (backend jobs/runAlerts): sent to this client's
            email when a bot run starts on one of their property logins. */}
        <Section
          title="Run Alert Emails"
          hint={form.runAlertEmailEnabled ? 'emails when a run starts' : 'off'}
          badge={form.runAlertEmailEnabled ? chip('ON', '#276749', '#f0fff4') : chip('OFF', '#a0aec0', '#f7f8fa')}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0, cursor: 'pointer', fontWeight: 600 }}>
              <input type="checkbox" checked={!!form.runAlertEmailEnabled} onChange={e => f('runAlertEmailEnabled', e.target.checked)} />
              Email this client when a run starts
            </label>
            {form.runAlertEmailEnabled && !form.email && (
              <span style={{ fontSize: 12, color: '#c53030' }}>⚠ Add an email address above or nothing will send.</span>
            )}
          </div>
          <div style={{ fontSize: 11, color: '#718096', marginTop: 6 }}>
            Sent once per run, within about a minute of it starting, for runs on this client's own property logins.
            Shows the property, username, search time and criteria.
          </div>
        </Section>

        {/* Commission */}
        <Section
          title="Commission"
          hint={form.isCommissionClient || form.receivesCommission
            ? [form.isCommissionClient && 'commission client', form.receivesCommission && 'receives commission'].filter(Boolean).join(' · ')
            : 'none'}
        >
          <div style={{ display: 'flex', gap: 20, marginBottom: 16, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
              <input type="checkbox" style={{ width: 'auto' }} checked={!!form.isCommissionClient} onChange={e => f('isCommissionClient', e.target.checked)} />
              Commission Client
            </label>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
              <input type="checkbox" style={{ width: 'auto' }} checked={!!form.receivesCommission} onChange={e => f('receivesCommission', e.target.checked)} />
              Receives Commission
            </label>
          </div>

          {(form.isCommissionClient || form.receivesCommission) && (
            <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Generates Commission For</label>
                <input
                  value={form.generatesCommissionFor || ''}
                  onChange={e => f('generatesCommissionFor', e.target.value)}
                  placeholder="e.g. Kimji"
                  style={{ width: 160 }}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Commission Type</label>
                <select value={form.commissionType || ''} onChange={e => f('commissionType', e.target.value)} style={{ width: 120 }}>
                  <option value="">Default</option>
                  <option value="percent">Percent (%)</option>
                  <option value="flat">Flat ($)</option>
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>
                  Commission Rate
                  <span style={{ fontWeight: 400, color: '#718096', marginLeft: 4 }}>
                    {(form.commissionType || (form.billingType === 'perShift' ? 'percent' : 'flat')) === 'percent'
                      ? '(e.g. 0.25 = 25%)'
                      : '(e.g. 300 = $300 flat)'}
                  </span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={form.commissionRate || ''}
                  onChange={e => f('commissionRate', e.target.value)}
                  placeholder={(form.commissionType || (form.billingType === 'perShift' ? 'percent' : 'flat')) === 'percent' ? '0.25' : '300'}
                  style={{ width: 120 }}
                />
              </div>
            </div>
          )}

          {/* Commission clients — shown when editing a receiver like Kimji */}
          {form.receivesCommission && generators.length > 0 && (
            <div>
              <label style={{ display: 'block', marginBottom: 8 }}>Commission Clients</label>
              <table>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Client</th>
                    <th style={{ textAlign: 'left' }}>Billing</th>
                    <th style={{ textAlign: 'left' }}>Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {generators.map(c => (
                    <tr key={c._id}>
                      <td>{c.clientName}</td>
                      <td style={{ color: '#718096' }}>{c.billingType === 'perShift' ? 'Per Shift' : 'Monthly'}</td>
                      <td style={{ color: '#38a169', fontWeight: 600 }}>
                        {(c.commissionType || (c.billingType === 'perShift' ? 'percent' : 'flat')) === 'percent'
                          ? `${((c.commissionRate ?? 0.25) * 100).toFixed(0)}%`
                          : `$${c.commissionRate ?? 300} flat`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        {/* Properties & User Accounts — unified */}
        <Section
          title="Properties & User Accounts"
          defaultOpen
          badge={properties.length === 0 ? chip('None', '#c53030', '#fff5f5') : chip(`${properties.length}`, '#4a5568', '#edf2f7')}
        >
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setProperties(ps => [...ps, { ...BLANK_PROP }])}>+ Add Row</button>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Property</th>
                  <th>User ID</th>
                  <th>PIN</th>
                  <th>Version</th>
                  <th>Commission</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {properties.map((p, i) => (
                  <tr key={i}>
                    <td><input placeholder="e.g. Mgmgrand" value={p.clientid} onChange={e => setProperties(ps => ps.map((x, j) => j === i ? { ...x, clientid: e.target.value } : x))} style={{ width: 120 }} /></td>
                    <td><input autoComplete="off" data-lpignore="true" data-1p-ignore placeholder="e.g. JhunL" value={p.user} onChange={e => setProperties(ps => ps.map((x, j) => j === i ? { ...x, user: e.target.value } : x))} style={{ width: 100 }} /></td>
                    <td><input autoComplete="off" data-lpignore="true" data-1p-ignore placeholder="PIN" value={p.pin} onChange={e => setProperties(ps => ps.map((x, j) => j === i ? { ...x, pin: e.target.value } : x))} style={{ width: 70 }} /></td>
                    <td><input placeholder="6.1.1" value={p.version} onChange={e => setProperties(ps => ps.map((x, j) => j === i ? { ...x, version: e.target.value } : x))} style={{ width: 70 }} /></td>
                    <td>
                      <select value={p.Commission} onChange={e => setProperties(ps => ps.map((x, j) => j === i ? { ...x, Commission: e.target.value } : x))} style={{ width: 70 }}>
                        <option>No</option>
                        <option>Yes</option>
                      </select>
                    </td>
                    <td>
                      <button type="button" className="btn btn-danger btn-sm" onClick={() => setProperties(ps => ps.filter((_, j) => j !== i))}>✕</button>
                    </td>
                  </tr>
                ))}
                {properties.length === 0 && (
                  <tr><td colSpan={6} style={{ color: '#aaa', fontSize: 12, textAlign: 'center' }}>No rows — click + Add Row</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Section>

        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button className="btn btn-primary" type="submit">{editId ? 'Save Changes' : 'Create Client'}</button>
          <button className="btn btn-secondary" type="button" onClick={cancelEdit}>Cancel</button>
        </div>
      </form>
    </div>
  );
}

// ── Bills Tab ─────────────────────────────────────────────────────────────────
const TWELVE_WEEKS = 84 * 24 * 60 * 60 * 1000;
const EIGHT_WEEKS  = 56 * 24 * 60 * 60 * 1000;

function filterShifts(shifts, showAll) {
  if (showAll) return shifts;
  const cutoff = Date.now() - TWELVE_WEEKS;
  return shifts.filter(s => {
    if (!s.paid) return true;
    if (s.needsIdentification) return true;
    if (s.datePaid) return new Date(s.datePaid).getTime() >= cutoff;
    return true;
  });
}

function lastShiftDate(bill) {
  let latest = null;
  (bill.shifts || []).forEach(s => {
    if (!s.shdate) return;
    const [y, m, d] = s.shdate.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    if (!latest || date > latest) latest = date;
  });
  return latest;
}

function BillsTab() {
  const [bills, setBills] = useState([]);
  const [showAllShifts, setShowAllShifts] = useState(false);
  const [clientMap, setClientMap] = useState({});
  const [commissionMap, setCommissionMap] = useState({});
  const [filterClient, setFilterClient] = useState('');
  const [filterType, setFilterType] = useState('');
  const [hideInactive, setHideInactive] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [payInputs, setPayInputs] = useState({});
  const [payLoading, setPayLoading] = useState({});
  const [groupPayInputs, setGroupPayInputs] = useState({});
  const [groupPayLoading, setGroupPayLoading] = useState({});
  const [genLoading, setGenLoading] = useState(false);
  const [recalcLoading, setRecalcLoading] = useState({});
  const [msg, setMsg] = useState('');
  const [toast, setToast] = useState('');
  const [showMonthly, setShowMonthly] = useState(false);
  const [expandedMonths, setExpandedMonths] = useState({});

  useEffect(() => {
    // Manager/managed relationships no longer need mapping here — /admin/bills
    // returns each manager's group already computed. See bill.group.
    api.getClients()
      .then(cs => setClientMap(Object.fromEntries(cs.map(c => [c.clientName, c]))))
      .catch(console.error);
    api.getCommissions().then(cs => {
      // Map: receiverClient → { period → commissionRecord }
      const map = {};
      cs.forEach(c => {
        if (!map[c.receiverClient]) map[c.receiverClient] = {};
        map[c.receiverClient][c.period] = c;
      });
      setCommissionMap(map);
    }).catch(console.error);
  }, []);

  useEffect(() => {
    const params = {};
    if (filterClient) params.clientName = filterClient;
    api.getBills(params).then(setBills).catch(console.error);
  }, [filterClient]);

  async function submitGenerate() {
    setGenLoading(true);
    setMsg('');
    try {
      const res = await api.generateBill();
      const newBills = res.results.filter(r => r.status === 'ok');
      const skipped = res.results.filter(r => r.status === 'already exists').length;
      const errors = res.results.filter(r => r.status === 'error');
      const parts = [];
      if (newBills.length) parts.push(`${newBills.length} bill(s) created`);
      if (skipped) parts.push(`${skipped} already existed`);
      if (errors.length) parts.push(`${errors.length} error(s): ${errors.map(e => e.clientName).join(', ')}`);
      setMsg(parts.join(' · ') || 'No new bills to generate.');
      api.getBills(filterClient ? { clientName: filterClient } : {}).then(setBills);
    } catch (err) {
      setMsg(`Error: ${err.message}`);
    } finally {
      setGenLoading(false);
    }
  }

  async function applyPayment(billId) {
    if (payLoading[billId]) return;
    const input = payInputs[billId] || {};
    let { amount, note } = input;

    // If user hasn't typed anything, fall back to the monthly rate default
    if (!amount) {
      const bill = bills.find(b => b._id === billId);
      if (bill?.billingType === 'perMonth') {
        amount = String(clientMap[bill.clientName]?.monthlyRate || 0);
      }
    }

    if (!amount) return;
    setPayLoading(p => ({ ...p, [billId]: true }));
    try {
      await api.manualPayment(billId, parseFloat(amount), note || '');
      setPayInputs(p => ({ ...p, [billId]: { amount: '', note: '' } }));
      const [updated, updatedClients] = await Promise.all([
        api.getBills(filterClient ? { clientName: filterClient } : {}),
        api.getClients()
      ]);
      setBills(updated);
      setClientMap(Object.fromEntries(updatedClients.map(c => [c.clientName, c])));
      setMsg('Payment applied.');
    } catch (err) {
      setMsg(`Error: ${err.message}`);
    } finally {
      setPayLoading(p => ({ ...p, [billId]: false }));
    }
  }

  async function applyGroupPayment(managerClientName) {
    if (groupPayLoading[managerClientName]) return;
    const input = groupPayInputs[managerClientName] || {};
    const { amount, note } = input;
    if (!amount) return;
    setGroupPayLoading(p => ({ ...p, [managerClientName]: true }));
    try {
      await api.groupPayment(managerClientName, parseFloat(amount), note || '');
      setGroupPayInputs(p => ({ ...p, [managerClientName]: { amount: '', note: '' } }));
      const updated = await api.getBills(filterClient ? { clientName: filterClient } : {});
      setBills(updated);
      setMsg('Group payment applied.');
    } catch (err) {
      setMsg(`Error: ${err.message}`);
    } finally {
      setGroupPayLoading(p => ({ ...p, [managerClientName]: false }));
    }
  }

  async function handleAdminNote(shiftId, adminNotes) {
    await api.updateAdminNotes(shiftId, adminNotes);
    setBills(bs => bs.map(b => ({
      ...b,
      shifts: b.shifts.map(s => String(s.shiftId) === shiftId ? { ...s, adminNotes } : s)
    })));
  }

  async function recalculate(billId) {
    setRecalcLoading(p => ({ ...p, [billId]: true }));
    try {
      const updated = await api.recalculateBill(billId);
      setBills(bs => bs.map(b => b._id === billId ? updated : b));
      setToast('Rates recalculated.');
      setTimeout(() => setToast(''), 5000);
    } catch (err) {
      setToast(`Error: ${err.message}`);
      setTimeout(() => setToast(''), 5000);
    } finally {
      setRecalcLoading(p => ({ ...p, [billId]: false }));
    }
  }

  // Compute totals. Billed and Collected cover both billing types (with a per-type
  // breakdown); Due Now, Outstanding and Clients with Balance are per-shift only.
  // Due Now and Total Outstanding come from each bill's amounts block (backend
  // utils/balanceCalc), so the tiles agree with the per-client rows, the group boxes
  // and the reminder emails. The hand-rolled sum this replaces skipped the noCharge
  // check and counted work marked non-billable as money owed.
  const perShiftBills   = bills.filter(b => b.billingType === 'perShift');
  const totalBalance    = perShiftBills.reduce((s, b) => s + (b.amounts?.totalBalance ?? b.balance ?? 0), 0);
  const perMonthBills   = bills.filter(b => b.billingType === 'perMonth');
  const billedPerShift  = perShiftBills.reduce((s, b) => s + (b.netAmount || 0), 0);
  // A monthly bill's netAmount is only one month's rate while amountPaid accumulates
  // every month paid, so "billed" for monthly is everything paid plus what's owed now.
  const billedPerMonth  = perMonthBills.reduce((s, b) => s + (b.amountPaid || 0) + (b.amounts?.totalBalance ?? 0), 0);
  const totalBilled     = billedPerShift + billedPerMonth;
  const paidPerShift    = perShiftBills.reduce((s, b) => s + (b.amountPaid || 0), 0);
  const paidPerMonth    = perMonthBills.reduce((s, b) => s + (b.amountPaid || 0), 0);
  const totalPaid       = paidPerShift + paidPerMonth;
  const unpaidClients   = perShiftBills.filter(b => (b.amounts?.totalBalance ?? b.balance ?? 0) > 0).length;
  const balanceDue      = perShiftBills.reduce((s, b) => s + (b.amounts?.amountDue ?? 0), 0);

  // Monthly collections — split by billing type, with per-client detail
  const monthlyCollections = (() => {
    const map = {};
    const ensure = (month, clientName) => {
      if (!map[month]) map[month] = { perShift: 0, perShiftTips: 0, perMonth: 0, perMonthTips: 0, clients: {} };
      if (!map[month].clients[clientName]) map[month].clients[clientName] = { billingType: '', amount: 0, tips: 0 };
    };
    bills.forEach(b => {
      if (b.billingType === 'perShift') {
        (b.shifts || []).forEach(s => {
          if (!s.paid || s.canceled || !s.datePaid) return;
          const month = s.datePaid.slice(0, 7);
          ensure(month, b.clientName);
          map[month].perShift += s.rate || 0;
          map[month].clients[b.clientName].billingType = 'perShift';
          map[month].clients[b.clientName].amount += s.rate || 0;
        });
        (b.payments || []).forEach(p => {
          if (!p.date || !p.tip) return;
          const month = p.date.slice(0, 7);
          ensure(month, b.clientName);
          map[month].perShiftTips += p.tip;
          map[month].clients[b.clientName].tips += p.tip;
        });
      } else {
        (b.payments || []).forEach(p => {
          if (!p.date) return;
          const month = p.date.slice(0, 7);
          ensure(month, b.clientName);
          map[month].perMonth += p.amount || 0;
          map[month].perMonthTips += p.tip || 0;
          map[month].clients[b.clientName].billingType = 'perMonth';
          map[month].clients[b.clientName].amount += p.amount || 0;
          map[month].clients[b.clientName].tips += p.tip || 0;
        });
      }
    });
    return Object.entries(map)
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([month, v]) => ({
        month, ...v,
        clients: Object.entries(v.clients)
          .map(([name, d]) => ({ name, ...d }))
          .sort((a, b) => b.amount - a.amount)
      }));
  })();

  // Latest shift date per client, across all loaded bills/periods
  const lastShiftByClient = {};
  bills.forEach(b => {
    const d = lastShiftDate(b);
    if (d && (!lastShiftByClient[b.clientName] || d > lastShiftByClient[b.clientName])) {
      lastShiftByClient[b.clientName] = d;
    }
  });
  const isInactive = (clientName) => {
    const last = lastShiftByClient[clientName];
    return !last || (Date.now() - last.getTime()) > EIGHT_WEEKS;
  };
  const inactiveClientCount = new Set(bills.filter(b => isInactive(b.clientName)).map(b => b.clientName)).size;
  const clientNames = Object.keys(clientMap).sort((a, b) => a.localeCompare(b));
  const visibleBills = bills
    .filter(b => !filterType || b.billingType === filterType)
    .filter(b => filterClient || !hideInactive || !isInactive(b.clientName));

  return (
    <div>
      {msg && <div className="alert alert-success">{msg}</div>}
      {toast && (
        <div style={{
          position: 'fixed', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          background: '#1a1a2e', color: '#fff',
          padding: '16px 28px', borderRadius: 10,
          fontSize: 15, fontWeight: 600,
          zIndex: 9999, boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
          pointerEvents: 'none'
        }}>
          {toast}
        </div>
      )}

      {/* Summary totals */}
      <div className="summary-grid" style={{ marginBottom: 8 }}>
        <div className="summary-card">
          <div className="label">Total Billed</div>
          <div className="value" style={{ fontSize: 18 }}>{fmt(totalBilled)}</div>
          <div style={{ fontSize: 11, color: '#718096', marginTop: 4 }}>
            Per shift {fmt(billedPerShift)} · Monthly {fmt(billedPerMonth)}
          </div>
        </div>
        <div className="summary-card paid">
          <div className="label">Total Collected</div>
          <div className="value" style={{ fontSize: 18 }}>{fmt(totalPaid)}</div>
          <div style={{ fontSize: 11, color: '#718096', marginTop: 4 }}>
            Per shift {fmt(paidPerShift)} · Monthly {fmt(paidPerMonth)}
          </div>
        </div>
        <div className="summary-card outstanding">
          <div className="label">Due Now</div>
          <div className="value" style={{ fontSize: 18 }}>{fmt(balanceDue)}</div>
        </div>
        <div className="summary-card" style={{ border: '1px solid #e2e8f0' }}>
          <div className="label">Total Outstanding</div>
          <div className="value" style={{ fontSize: 18, color: '#4a5568' }}>{fmt(totalBalance)}</div>
        </div>
        <div className="summary-card">
          <div className="label">Clients with Balance</div>
          <div className="value" style={{ fontSize: 18 }}>{unpaidClients}</div>
        </div>
      </div>

      {/* Monthly collections summary */}
      {monthlyCollections.length > 0 && (
        <div className="card" style={{ marginBottom: 8, padding: '10px 16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showMonthly ? 10 : 0 }}>
            <strong style={{ fontSize: 13 }}>Collections by Month</strong>
            <button className="toggle-btn" onClick={() => setShowMonthly(p => !p)} style={{ fontSize: 12 }}>
              {showMonthly ? 'Hide' : 'Show'}
            </button>
          </div>
          {showMonthly && (
            <table style={{ width: '100%', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', paddingBottom: 6, color: '#718096', fontWeight: 600 }}>Month</th>
                  <th style={{ textAlign: 'right', paddingBottom: 6, color: '#718096', fontWeight: 600 }}>Per Shift</th>
                  <th style={{ textAlign: 'right', paddingBottom: 6, color: '#718096', fontWeight: 600 }}>Tips</th>
                  <th style={{ textAlign: 'right', paddingBottom: 6, color: '#718096', fontWeight: 600 }}>Monthly</th>
                  <th style={{ textAlign: 'right', paddingBottom: 6, color: '#718096', fontWeight: 600 }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {monthlyCollections.map(({ month, perShift, perShiftTips, perMonth, perMonthTips, clients }) => {
                  const total = perShift + perShiftTips + perMonth + perMonthTips;
                  const isExp = expandedMonths[month];
                  return (
                    <React.Fragment key={month}>
                      <tr
                        style={{ borderTop: '1px solid #f0f2f5', cursor: 'pointer' }}
                        onClick={() => setExpandedMonths(p => ({ ...p, [month]: !p[month] }))}
                      >
                        <td style={{ padding: '5px 0', fontWeight: 500 }}>
                          <span style={{ marginRight: 6, fontSize: 10, color: '#aaa' }}>{isExp ? '▼' : '▶'}</span>
                          {month}
                        </td>
                        <td style={{ textAlign: 'right', color: '#38a169', fontWeight: 600 }}>{perShift > 0 ? fmt(perShift) : '—'}</td>
                        <td style={{ textAlign: 'right', color: perShiftTips > 0 ? '#d69e2e' : '#ccc' }}>{perShiftTips > 0 ? fmt(perShiftTips) : '—'}</td>
                        <td style={{ textAlign: 'right', color: '#2b6cb0', fontWeight: 600 }}>{perMonth > 0 ? fmt(perMonth) : '—'}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmt(total)}</td>
                      </tr>
                      {isExp && clients.map(c => (
                        <tr key={c.name} style={{ background: '#f7f8fa' }}>
                          <td style={{ padding: '3px 0 3px 22px', color: '#4a5568', fontSize: 12 }}>{c.name}</td>
                          <td style={{ textAlign: 'right', fontSize: 12, color: c.billingType === 'perShift' ? '#38a169' : '#ccc' }}>
                            {c.billingType === 'perShift' ? fmt(c.amount) : '—'}
                          </td>
                          <td style={{ textAlign: 'right', fontSize: 12, color: c.tips > 0 ? '#d69e2e' : '#ccc' }}>
                            {c.tips > 0 ? fmt(c.tips) : '—'}
                          </td>
                          <td style={{ textAlign: 'right', fontSize: 12, color: c.billingType === 'perMonth' ? '#2b6cb0' : '#ccc' }}>
                            {c.billingType === 'perMonth' ? fmt(c.amount) : '—'}
                          </td>
                          <td style={{ textAlign: 'right', fontSize: 12, fontWeight: 600 }}>{fmt(c.amount + c.tips)}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div className="period-bar">
        <select value={filterClient} onChange={e => setFilterClient(e.target.value)} style={{ width: 200 }}>
          <option value="">All Clients</option>
          {clientNames.map(name => <option key={name} value={name}>{name}</option>)}
        </select>
        <div style={{ display: 'flex', gap: 4 }}>
          {[['', 'All'], ['perShift', 'Per Shift'], ['perMonth', 'Monthly']].map(([val, label]) => (
            <button
              key={val}
              className={`toggle-btn${filterType === val ? ' active' : ''}`}
              onClick={() => setFilterType(val)}
            >{label}</button>
          ))}
        </div>
        <button className="toggle-btn" onClick={() => setShowAllShifts(p => !p)} style={{ marginLeft: 12 }}>
          {showAllShifts ? 'Show Recent Shifts Only' : 'Show All Shifts'}
        </button>
        <button
          className={`toggle-btn${hideInactive ? ' active' : ''}`}
          onClick={() => setHideInactive(p => !p)}
          disabled={!!filterClient}
          title={filterClient ? 'Clear the client filter to use this' : ''}
          style={{ marginLeft: 12, opacity: filterClient ? 0.5 : 1 }}
        >
          {hideInactive ? `Hiding ${inactiveClientCount} Inactive (8+ wks)` : 'Unhide All Clients'}
        </button>
      </div>

      <div className="card">
        <div className="card-title">Generate Bill</div>
        <p style={{ fontSize: 13, color: '#718096', marginBottom: 12 }}>
          Scans BookedShifts for all active clients and creates any missing bills. Already-generated bills are not overwritten.
        </p>
        <button className="btn btn-primary" onClick={submitGenerate} disabled={genLoading}>
          {genLoading ? 'Generating...' : 'Generate Bills for All Clients'}
        </button>
      </div>

      {visibleBills.map(bill => {
        const pay = payInputs[bill._id] || { amount: '', note: '' };
        const hasUnpaid = bill.billingType === 'perShift'
          ? bill.balance > 0
          : bill.shifts.length > 0;
        const isExpanded = expanded[bill._id] ?? hasUnpaid;
        const toggle = () => setExpanded(e => ({ ...e, [bill._id]: !isExpanded }));

        return (
          <div key={bill._id} className="card" style={{ opacity: hasUnpaid ? 1 : 0.65 }}>
            {/* Header row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }} onClick={toggle}>
                <span style={{ fontSize: 13, color: '#718096' }}>{isExpanded ? '▾' : '▸'}</span>
                <strong style={{ fontSize: 16 }}>{bill.clientName}</strong>
                {bill.billingType === 'perShift'
                  ? <>
                      <span className={`badge badge-${bill.status}`}>{bill.status}</span>
                      {clientMap[bill.clientName]?.expdate && (() => {
                        const exp = new Date(clientMap[bill.clientName].expdate);
                        const isExpired = exp < new Date();
                        return (
                          <span style={{ fontSize: 11, color: isExpired ? '#e53e3e' : '#38a169', fontWeight: 600 }}>
                            {isExpired ? 'Expired' : 'Exp'} {exp.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                        );
                      })()}
                    </>
                  : <>
                      <span className="badge badge-partial">Monthly</span>
                      {clientMap[bill.clientName]?.expdate && (() => {
                        const exp = new Date(clientMap[bill.clientName].expdate);
                        const isExpired = exp < new Date();
                        return (
                          <span style={{ fontSize: 11, color: isExpired ? '#e53e3e' : '#38a169', fontWeight: 600 }}>
                            {isExpired ? 'Expired' : 'Paid thru'} {exp.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                        );
                      })()}
                    </>}
                <span style={{ fontSize: 12, color: '#718096' }}>{bill.shifts.length} shifts</span>
              </div>
              <div style={{ fontSize: 13, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                <span>Gross: {fmt(bill.grossAmount)}</span>
                {bill.commissionDeduction > 0 && <span style={{ color: '#e53e3e' }}>Commission: -{fmt(bill.commissionDeduction)}</span>}
                <span>Net: {fmt(bill.netAmount)}</span>
                <span>Paid: {fmt(bill.amountPaid)}</span>
                {bill.tips > 0 && <span>Tips: {fmt(bill.tips)}</span>}
                {/* Per-shift: the part of the balance whose pay day has passed — same
                    balanceCalc figure the portal and reminder emails quote. */}
                {bill.billingType === 'perShift' && bill.amounts && (
                  <span style={{ fontWeight: 700, color: bill.amounts.amountDue > 0 ? '#e53e3e' : '#38a169' }}>
                    Due Now: {fmt(bill.amounts.amountDue)}
                  </span>
                )}
                <span style={{ fontWeight: 700, color: bill.balance > 0 ? '#e53e3e' : '#38a169' }}>
                  Balance: {fmt(bill.balance)}
                </span>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => recalculate(bill._id)}
                  disabled={!!recalcLoading[bill._id]}
                >
                  {recalcLoading[bill._id] ? 'Recalculating...' : 'Recalculate'}
                </button>
              </div>
            </div>

            {isExpanded && <>
            {/* Group payment — for clients who pay on behalf of managed accounts */}
            {bill.group && (() => {
              // bill.group is built by the backend from utils/balanceCalc — the same
              // figures the portal and the reminder emails quote. It deliberately does
              // not depend on which bills this page has loaded: filtering the list to
              // the manager alone used to leave the group total showing only them.
              const rows = bill.group.members.map(m => ({
                clientName: m.clientName,
                isSelf: m.isSelf,
                total: m.totalBalance ?? 0,
                due: m.amountDue ?? 0,
                nextCharge: m.nextCharge ?? 0,
                nextChargeDate: m.nextChargeDate || null,
                renewalDue: !!m.renewalDue,
                renewalLapsed: !!m.renewalLapsed
              }));
              const groupTotal = bill.group.totals.totalBalance;
              const groupDue = bill.group.totals.amountDue;
              const gpay = groupPayInputs[bill.clientName] || { amount: '', note: '' };
              return (
                <div style={{ background: '#f0f4ff', borderRadius: 6, padding: '8px 12px', marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, flexWrap: 'wrap', gap: 6 }}>
                    <strong style={{ fontSize: 13 }}>
                      Group Total: <span style={{ color: groupTotal > 0 ? '#e53e3e' : '#38a169' }}>{fmt(groupTotal)}</span>
                      <span style={{ marginLeft: 12 }}>
                        Due Now: <span style={{ color: groupDue > 0 ? '#e53e3e' : '#38a169' }}>{fmt(groupDue)}</span>
                      </span>
                    </strong>
                  </div>
                  {/* Per-person breakdown: total stays neutral, the due portion is red. */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                    {rows.map(r => (
                      <div key={r.clientName} style={{ background: '#fff', borderRadius: 5, padding: '5px 9px', fontSize: 11, minWidth: 130 }}>
                        <div style={{ fontWeight: 600 }}>{r.clientName}{r.isSelf ? ' (manager)' : ''}</div>
                        <div style={{ color: '#718096' }}>Total: {fmt(r.total)}</div>
                        <div style={{ fontWeight: 700, color: r.due > 0 ? '#e53e3e' : '#38a169' }}>
                          {r.due > 0 ? `Due: ${fmt(r.due)}` : 'Nothing due'}
                        </div>
                        {/* Outside the due window the renewal is only a heads-up; inside
                            it the amount is already in "Due" above, so just date it. */}
                        {r.nextCharge > 0 && (
                          <div style={{ color: '#718096' }}>
                            Next: {fmt(r.nextCharge)}{r.nextChargeDate ? ` ${fmtISO(r.nextChargeDate)}` : ''}
                          </div>
                        )}
                        {r.renewalDue && !r.renewalLapsed && r.nextChargeDate && (
                          <div style={{ color: '#e53e3e' }}>Renews {fmtISO(r.nextChargeDate)}</div>
                        )}
                        {r.renewalLapsed && r.nextChargeDate && (
                          <div style={{ color: '#e53e3e' }}>Expired {fmtISO(r.nextChargeDate)}</div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <strong style={{ fontSize: 13 }}>Group Payment:</strong>
                    <input
                      type="text" inputMode="numeric" placeholder="Amount"
                      value={gpay.amount}
                      onChange={e => setGroupPayInputs(p => ({ ...p, [bill.clientName]: { ...gpay, amount: onlyDigits(e.target.value) } }))}
                      style={{ width: 80 }}
                    />
                    <input
                      placeholder="Note (optional)"
                      value={gpay.note}
                      onChange={e => setGroupPayInputs(p => ({ ...p, [bill.clientName]: { ...gpay, note: e.target.value } }))}
                      style={{ width: 130 }}
                    />
                    <button
                      className="btn btn-primary btn-sm"
                      disabled={!gpay.amount || groupPayLoading[bill.clientName]}
                      onClick={() => applyGroupPayment(bill.clientName)}
                    >
                      {groupPayLoading[bill.clientName] ? 'Saving...' : 'Apply to Group'}
                    </button>
                  </div>
                </div>
              );
            })()}
            {/* Monthly payment — show due date + one-click payment button */}
            {bill.billingType === 'perMonth' && (() => {
              const clientInfo = clientMap[bill.clientName];
              const monthlyRate = clientInfo?.monthlyRate || 0;
              const dueDate = clientInfo?.monthlyDueDate;
              const currentMonth = new Date().toLocaleString('default', { month: 'long', year: 'numeric' });
              // Prefilled with the monthly rate, rounded — the field only holds whole dollars.
              const pay = payInputs[bill._id] || { amount: String(Math.round(monthlyRate)), note: '' };
              return (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap', background: '#f7f8fa', padding: '6px 10px', borderRadius: 6 }}>
                  {dueDate && <span style={{ fontSize: 12, color: '#718096' }}>Due: <strong>day {dueDate} of each month</strong></span>}
                  <span style={{ fontSize: 12, color: '#718096', marginLeft: 8 }}>Rate: <strong>{fmt(monthlyRate)}/mo</strong></span>
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="Amount"
                      value={pay.amount}
                      onChange={e => setPayInputs(p => ({ ...p, [bill._id]: { ...pay, amount: onlyDigits(e.target.value) } }))}
                      style={{ width: 80 }}
                    />
                    <button className="btn btn-primary btn-sm" onClick={() => applyPayment(bill._id)} disabled={!pay.amount || payLoading[bill._id]}>
                      {payLoading[bill._id] ? 'Saving...' : 'Mark Paid'}
                    </button>
                  </div>
                </div>
              );
            })()}
            {/* Apply payment — perShift only */}
            {bill.billingType === 'perShift' && <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap', background: '#f7f8fa', padding: '6px 10px', borderRadius: 6 }}>
              <strong style={{ fontSize: 13 }}>Apply Payment:</strong>
              <input
                type="text"
                inputMode="numeric"
                placeholder="Amount"
                value={pay.amount}
                onChange={e => setPayInputs(p => ({ ...p, [bill._id]: { ...pay, amount: onlyDigits(e.target.value) } }))}
                style={{ width: 80 }}
              />
              <input
                placeholder="Note (optional)"
                value={pay.note}
                onChange={e => setPayInputs(p => ({ ...p, [bill._id]: { ...pay, note: e.target.value } }))}
                style={{ width: 130 }}
              />
              <button className="btn btn-primary btn-sm" onClick={() => applyPayment(bill._id)} disabled={!pay.amount}>
                Mark Paid
              </button>
            </div>}

            {/* Monthly payment breakdown — perMonth clients only, not commission receivers (they have their own statement) */}
            {bill.billingType === 'perMonth' && !clientMap[bill.clientName]?.receivesCommission && (() => {
              const payments = [...bill.payments].sort((a, b) => b.date.localeCompare(a.date));
              if (!payments.length) return null;

              const fmtPeriodDate = iso => {
                if (!iso) return '';
                const [y, m, d] = iso.split('-').map(Number);
                return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              };

              return (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 6 }}>Monthly Payment History</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {payments.map((p, i) => {
                      const periodLabel = p.periodStart && p.periodEnd
                        ? `${fmtPeriodDate(p.periodStart)} – ${fmtPeriodDate(p.periodEnd)}`
                        : p.date?.slice(0, 7);
                      return (
                        <div key={i} style={{
                          padding: '4px 10px', borderRadius: 6, fontSize: 11, border: '1px solid',
                          borderColor: '#c6f6d5', background: '#f0fff4', color: '#276749'
                        }}>
                          <div style={{ fontWeight: 600 }}>{periodLabel}</div>
                          <div>{fmt(p.amount)} · {p.date}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Payment history — perShift only */}
            {bill.billingType === 'perShift' && bill.payments.length > 0 && (
              <div style={{ marginBottom: 4, fontSize: 11, color: '#4a5568' }}>
                <strong>Payment history: </strong>
                {bill.payments.map((p, i) => (
                  <span key={i} style={{ marginLeft: 8 }}>
                    {p.date}: {fmt(p.amount)} via {p.source}
                    {p.tip > 0 ? ` (tip: ${fmt(p.tip)})` : ''}
                    {p.note ? ` — ${p.note}` : ''}
                    {i < bill.payments.length - 1 ? ' ·' : ''}
                  </span>
                ))}
              </div>
            )}

            {/* Monthly statement — commission-receiving clients only */}
            {clientMap[bill.clientName]?.receivesCommission && (() => {
              const clientCommissions = commissionMap[bill.clientName] || {};
              const monthlyFee = clientMap[bill.clientName]?.monthlyRate || 0;

              // Group payments by YYYY-MM
              const paymentsByMonth = {};
              bill.payments.forEach(p => {
                const period = p.date?.slice(0, 7);
                if (!period) return;
                if (!paymentsByMonth[period]) paymentsByMonth[period] = 0;
                paymentsByMonth[period] += p.amount;
              });

              // All periods from both commissions and payments
              const allPeriods = [...new Set([
                ...Object.keys(clientCommissions),
                ...Object.keys(paymentsByMonth)
              ])].sort().reverse();

              if (!allPeriods.length) return null;

              return (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 4 }}>Monthly Statement</div>
                  <table>
                    <thead>
                      <tr>
                        <th>Month</th>
                        <th style={{ textAlign: 'right' }}>Commission Earned</th>
                        <th style={{ textAlign: 'right' }}>His Fee</th>
                        <th style={{ textAlign: 'right' }}>Result</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allPeriods.map(period => {
                        const commission = clientCommissions[period]?.totalEarned || 0;
                        const net = commission - monthlyFee; // positive = you owe Kimji, negative = Kimji owes you
                        return (
                          <tr key={period}>
                            <td>{period}</td>
                            <td style={{ textAlign: 'right', color: '#38a169', fontWeight: 500 }}>{fmt(commission)}</td>
                            <td style={{ textAlign: 'right', color: '#e53e3e' }}>−{fmt(monthlyFee)}</td>
                            <td style={{ textAlign: 'right', fontWeight: 700, color: net > 0 ? '#38a169' : net < 0 ? '#e53e3e' : '#718096' }}>
                              {net > 0
                                ? `You owe Kimji ${fmt(net)}`
                                : net < 0
                                ? `Kimji owes ${fmt(Math.abs(net))}`
                                : 'Even'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })()}

            <ShiftTable
              shifts={filterShifts(bill.shifts.map(s => ({ ...s, billId: bill._id })), showAllShifts)}
              billingType={bill.billingType}
              adminView
              onNoteSave={handleAdminNote}
              onTagAssign={async (billId, shiftId, rawTag) => {
                await api.assignTag(billId, shiftId, rawTag);
                api.getBills(filterClient ? { clientName: filterClient } : {}).then(setBills);
              }}
              onMarkPaid={async (billId, shiftId, paid) => {
                const updated = await api.markShiftPaid(billId, shiftId, paid);
                setBills(bs => bs.map(b => b._id === billId ? updated : b));
                if (paid) {
                  const updatedClients = await api.getClients();
                  setClientMap(Object.fromEntries(updatedClients.map(c => [c.clientName, c])));
                }
              }}
              onCancel={async (billId, shiftId, canceled) => {
                const updated = await api.markShiftCanceled(billId, shiftId, canceled);
                setBills(bs => bs.map(b => b._id === billId ? updated : b));
              }}
              onNoCharge={async (billId, shiftId, noCharge) => {
                const updated = await api.markShiftNoCharge(billId, shiftId, noCharge);
                setBills(bs => bs.map(b => b._id === billId ? updated : b));
              }}
            />
            </>}
          </div>
        );
      })}
    </div>
  );
}

// ── Unidentified Tab ──────────────────────────────────────────────────────────
function UnidentifiedTab() {
  const [data, setData] = useState([]);

  const load = useCallback(() => api.getUnidentified().then(setData).catch(console.error), []);
  useEffect(() => { load(); }, [load]);

  async function assignTag(billId, shiftId, rawTag) {
    await api.assignTag(billId, shiftId, rawTag);
    load();
  }

  const count = data.reduce((s, d) => s + d.shifts.length, 0);

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <strong>Unidentified shifts needing review:</strong>
        {count > 0 ? <span className="unid-badge">{count}</span> : <span style={{ marginLeft: 8, color: '#38a169' }}>All clear ✓</span>}
      </div>
      {data.map(d => (
        <div key={d.billId} className="card">
          <div className="card-title">{d.clientName} — {d.period}</div>
          <ShiftTable
            shifts={d.shifts.map(s => ({ ...s, billId: d.billId }))}
            billingType="perShift"
            adminView
            onTagAssign={assignTag}
          />
        </div>
      ))}
    </div>
  );
}

// ── Pricing Tab ───────────────────────────────────────────────────────────────
function PricingTab() {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientName, setClientName] = useState('');
  const [billingType, setBillingType] = useState('perShift');
  const [monthlyRate, setMonthlyRate] = useState('');
  const [criteriaRates, setCriteriaRates] = useState([]);
  const [flatRates, setFlatRates] = useState([]);
  const [msg, setMsg] = useState('');

  const TAG_DEFAULTS = {
    BREAKFAST: 30,
    LUNCH: 30,
    DINNER: 50,
    RECEPTION: 50,
    COFFEE_BREAK: 50,
    CASH_CONCESSION: 30
  };
  const TAGS = Object.keys(TAG_DEFAULTS);

  useEffect(() => {
    api.getClients().then(cs => setClients(cs.filter(c => c.active))).catch(console.error);
  }, []);

  async function selectClient(name) {
    setClientName(name);
    setMsg('');
    const client = clients.find(c => c.clientName === name);
    setSelectedClient(client);
    setBillingType(client?.billingType || 'perShift');
    setMonthlyRate(client?.monthlyRate || '');
    try {
      const p = await api.getPricing(name);
      const existing = p.criteriaRates || [];
      // Fill in any missing tags so all 6 always show
      const merged = TAGS.map(tag => {
        const found = existing.find(r => r.criteriaTag === tag);
        return found || { criteriaTag: tag, rate: TAG_DEFAULTS[tag] };
      });
      setCriteriaRates(merged);
      setFlatRates(p.flatRateProperties || []);
    } catch {
      setCriteriaRates(TAGS.map(t => ({ criteriaTag: t, rate: TAG_DEFAULTS[t] })));
      setFlatRates([]);
    }
  }

  async function save() {
    try {
      if (billingType === 'perMonth') {
        await api.updateClient(selectedClient._id, {
          billingType: 'perMonth',
          monthlyRate: monthlyRate ? parseFloat(monthlyRate) : null
        });
        setClients(cs => cs.map(c => c._id === selectedClient._id ? { ...c, billingType: 'perMonth', monthlyRate: parseFloat(monthlyRate) } : c));
      } else {
        await api.updatePricing(clientName, {
          criteriaRates: criteriaRates.filter(r => r.rate !== '').map(r => ({ ...r, rate: parseFloat(r.rate) })),
          flatRateProperties: flatRates.filter(r => r.clientid && r.rate !== '').map(r => ({ ...r, rate: parseFloat(r.rate) }))
        });
        await api.updateClient(selectedClient._id, { billingType: 'perShift' });
      }
      setMsg('Saved.');
    } catch (err) { setMsg(`Error: ${err.message}`); }
  }

  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      {/* Client list */}
      <div className="card" style={{ minWidth: 180, flex: '0 0 180px' }}>
        <div className="card-title">Clients</div>
        {clients.map(c => (
          <div
            key={c._id}
            onClick={() => selectClient(c.clientName)}
            style={{
              padding: '8px 10px', borderRadius: 6, cursor: 'pointer', marginBottom: 4,
              background: clientName === c.clientName ? '#1a1a2e' : 'transparent',
              color: clientName === c.clientName ? '#fff' : '#1a1a2e',
              fontWeight: clientName === c.clientName ? 600 : 400
            }}
          >
            {c.clientName}
          </div>
        ))}
      </div>

      {/* Pricing form */}
      <div style={{ flex: 1 }}>
        {!clientName ? (
          <div className="card" style={{ color: '#718096' }}>Select a client on the left to set their pricing.</div>
        ) : (
          <div className="card">
            {msg && <div className="alert alert-success">{msg}</div>}
            <div className="card-title">{clientName} — Rates</div>

            {/* Monthly toggle */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, cursor: 'pointer', width: 'fit-content' }}>
              <input
                type="checkbox"
                style={{ width: 'auto' }}
                checked={billingType === 'perMonth'}
                onChange={e => setBillingType(e.target.checked ? 'perMonth' : 'perShift')}
              />
              <span style={{ fontWeight: 500 }}>Monthly billing</span>
            </label>

            {billingType === 'perMonth' ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                <span style={{ fontWeight: 500, fontSize: 13, width: 140 }}>Monthly Rate</span>
                <span style={{ color: '#718096' }}>$</span>
                <input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={monthlyRate}
                  onChange={e => setMonthlyRate(e.target.value)}
                  style={{ width: 120 }}
                />
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10, marginBottom: 20 }}>
                {criteriaRates.map((r, i) => (
                  <div key={r.criteriaTag} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ width: 140, fontWeight: 500, fontSize: 13 }}>{r.criteriaTag}</span>
                    <span style={{ color: '#718096' }}>$</span>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={r.rate}
                      onChange={e => setCriteriaRates(rs => rs.map((x, j) => j === i ? { ...x, rate: e.target.value } : x))}
                      style={{ width: 90 }}
                    />
                  </div>
                ))}
              </div>
            )}

            <div className="card-title">Flat Rate Properties</div>
            <p style={{ fontSize: 12, color: '#718096', marginBottom: 10 }}>Override rate for a specific property — ignores shift tag.</p>
            {flatRates.map((r, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                <input placeholder="Property (e.g. Wynnlv)" value={r.clientid} onChange={e => setFlatRates(rs => rs.map((x, j) => j === i ? { ...x, clientid: e.target.value } : x))} style={{ width: 160 }} />
                <span style={{ color: '#718096' }}>$</span>
                <input type="number" step="0.01" placeholder="0.00" value={r.rate} onChange={e => setFlatRates(rs => rs.map((x, j) => j === i ? { ...x, rate: e.target.value } : x))} style={{ width: 90 }} />
                <button className="btn btn-danger btn-sm" onClick={() => setFlatRates(rs => rs.filter((_, j) => j !== i))}>✕</button>
              </div>
            ))}
            <button className="btn btn-secondary btn-sm" onClick={() => setFlatRates(rs => [...rs, { clientid: '', rate: '' }])}>+ Add Flat Rate Property</button>

            <div style={{ marginTop: 20 }}>
              <button className="btn btn-primary" onClick={save}>Save</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Commissions Tab ───────────────────────────────────────────────────────────
function CommissionsTab() {
  const [commissions, setCommissions] = useState([]);
  const [editing, setEditing] = useState({});
  const [settlingId, setSettlingId] = useState(null);
  const [msg, setMsg] = useState('');

  const [clientMap2, setClientMap2] = useState({});
  useEffect(() => {
    api.getCommissions().then(cs => setCommissions(cs.sort((a,b) => b.period.localeCompare(a.period)))).catch(console.error);
    api.getClients().then(cs => setClientMap2(Object.fromEntries(cs.map(c => [c.clientName, c])))).catch(console.error);
  }, []);

  async function saveEdit(c) {
    try {
      const updated = await api.updateCommission(c._id, { totalEarned: parseFloat(editing[c._id]) });
      setCommissions(cs => cs.map(x => x._id === c._id ? updated : x));
      setEditing(e => { const n = { ...e }; delete n[c._id]; return n; });
      setMsg('Commission updated.');
    } catch (err) { setMsg(`Error: ${err.message}`); }
  }

  return (
    <div>
      {msg && <div className="alert alert-success">{msg}</div>}
      {commissions.map(c => (
        <div key={c._id} className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div>
              <strong>{c.period}</strong>
              <span style={{ marginLeft: 10, color: '#718096', fontSize: 12 }}>→ {c.receiverClient}</span>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {(() => {
                const fee = clientMap2[c.receiverClient]?.monthlyRate || 0;
                const net = c.totalEarned - fee;
                return (
                  <>
                    <strong style={{ color: '#38a169' }}>{fmt(c.totalEarned)}</strong>
                    <span style={{ color: '#718096' }}>− {fmt(fee)} fee =</span>
                    <strong style={{ color: net >= 0 ? '#38a169' : '#e53e3e' }}>
                      {net >= 0 ? `Owe ${fmt(net)}` : `${c.receiverClient} owes ${fmt(Math.abs(net))}`}
                    </strong>
                    {net !== 0 && (
                      c.settled
                        ? <span className="badge badge-paid" style={{ marginLeft: 4 }}>
                            {net > 0 ? `Paid ${c.settledDate}` : `Received ${c.settledDate}`}
                          </span>
                        : <button
                            className="btn btn-primary btn-sm"
                            disabled={settlingId === c._id}
                            onClick={async () => {
                              setSettlingId(c._id);
                              try {
                                const updated = await api.settleCommission(c._id, true);
                                setCommissions(cs => cs.map(x => x._id === c._id ? updated : x));
                              } finally {
                                setSettlingId(null);
                              }
                            }}
                          >
                            {settlingId === c._id ? 'Saving...' : net > 0 ? 'Mark Paid' : 'Mark Received'}
                          </button>
                    )}
                    {c._id in editing ? (
                      <>
                        <input type="number" step="0.01" value={editing[c._id]}
                          onChange={e => setEditing(ed => ({ ...ed, [c._id]: e.target.value }))}
                          style={{ width: 80 }} />
                        <button className="btn btn-primary btn-sm" onClick={() => saveEdit(c)}>Save</button>
                        <button className="btn btn-secondary btn-sm" onClick={() => setEditing(e => { const n={...e}; delete n[c._id]; return n; })}>✕</button>
                      </>
                    ) : (
                      <button className="btn btn-secondary btn-sm" onClick={() => setEditing(ed => ({ ...ed, [c._id]: c.totalEarned }))}>Edit</button>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
          <table>
            <thead>
              <tr><th>From</th><th>Type</th><th>Gross Paid</th><th>Commission</th><th>Date</th></tr>
            </thead>
            <tbody>
              {(c.entries || []).map((e, i) => (
                <tr key={i}>
                  <td>{e.fromClient}</td>
                  <td>{e.billingType === 'perShift' ? 'Per Shift (25%)' : 'Monthly ($300)'}</td>
                  <td>{fmt(e.grossPaid)}</td>
                  <td style={{ color: '#38a169', fontWeight: 600 }}>{fmt(e.commissionAmount)}</td>
                  <td>{e.paidDate}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
      {commissions.length === 0 && <div className="card" style={{ color: '#718096' }}>No commission records yet.</div>}
    </div>
  );
}

// ── Preshift Tab ──────────────────────────────────────────────────────────────
function PreshiftTab() {
  const [records, setRecords] = useState([]);
  const [clientids, setClientids] = useState([]);
  const [selectedClientid, setSelectedClientid] = useState('');
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    api.getPreshift()
      .then(data => {
        setRecords(data);
        const ids = [...new Set(data.map(r => r.clientid).filter(Boolean))].sort();
        setClientids(ids);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  function searchtimeSecs(st) {
    if (!st) return 0;
    const p = st.split(':');
    return parseInt(p[0] || 0) * 3600 + parseInt(p[1] || 0) * 60 + parseInt(p[2] || 0);
  }

  function effectiveDate(ca, searchtime) {
    // If createdAt time > searchtime, the search is for the next day
    const m = ca && ca.match(/(\d{2})\/(\d{2})\/(\d{4}),T(\d{2}):(\d{2}):(\d{2})/);
    if (!m) return ca ? ca.split(',')[0] : '';
    const [, mm, dd, yyyy, hh, min, sec] = m;
    const caTimeSecs = parseInt(hh) * 3600 + parseInt(min) * 60 + parseInt(sec);
    const stSecs = searchtimeSecs(searchtime);
    const date = new Date(parseInt(yyyy), parseInt(mm) - 1, parseInt(dd));
    if (caTimeSecs > stSecs) date.setDate(date.getDate() + 1);
    const p = n => String(n).padStart(2, '0');
    return `${p(date.getMonth() + 1)}/${p(date.getDate())}/${date.getFullYear()}`;
  }

  function dedup(recs) {
    // recs already sorted most-recent-first (_id desc from backend)
    const seen = [];
    return recs.filter(rec => {
      const st = searchtimeSecs(rec.searchtime);
      const day = effectiveDate(rec.createdAt, rec.searchtime);
      const isDup = seen.some(s =>
        s.user === rec.user && s.clientid === rec.clientid &&
        s.day === day && Math.abs(s.st - st) <= 5
      );
      if (!isDup) seen.push({ user: rec.user, clientid: rec.clientid, day, st });
      return !isDup;
    });
  }

  function fmtCreatedAt(ca) {
    if (!ca) return '';
    // "06/04/2026,T09:34:10-7" → "06/04/2026  09:34:10"
    const m = ca.match(/(\d{2}\/\d{2}\/\d{4}),T(\d{2}:\d{2}:\d{2})/);
    return m ? `${m[1]}  ${m[2]}` : ca;
  }

  const filtered = dedup(
    selectedClientid ? records.filter(r => r.clientid === selectedClientid) : records
  );

  function toggleExpand(id) {
    setExpanded(e => ({ ...e, [id]: !e[id] }));
  }

  return (
    <div>
      <div className="card" style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Property</label>
          <select value={selectedClientid} onChange={e => setSelectedClientid(e.target.value)} style={{ minWidth: 160 }}>
            <option value="">All</option>
            {clientids.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        {!loading && (
          <span style={{ color: '#718096', fontSize: 13, marginLeft: 'auto' }}>{filtered.length} records</span>
        )}
      </div>

      {loading && <div className="card" style={{ color: '#718096' }}>Loading...</div>}
      {!loading && filtered.length === 0 && <div className="card" style={{ color: '#718096' }}>No preshift records found.</div>}

      {!loading && filtered.length > 0 && (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table style={{ minWidth: 750 }}>
            <thead>
              <tr>
                <th>Created At</th>
                <th>Client ID</th>
                <th>User</th>
                <th>Search Time</th>
                <th>Start</th>
                <th>End</th>
                <th style={{ textAlign: 'center' }}>Shifts</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => {
                const id = String(r._id);
                const isExp = expanded[id];
                return (
                  <React.Fragment key={id}>
                    <tr
                      style={{ background: i % 2 === 0 ? '#fff' : '#f7fafc', cursor: 'pointer' }}
                      onClick={() => toggleExpand(id)}
                    >
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtCreatedAt(r.createdAt)}</td>
                      <td>{r.clientid}</td>
                      <td>{r.user}</td>
                      <td>{r.searchtime}</td>
                      <td>{r.start}</td>
                      <td>{r.end}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{ background: '#ebf4ff', color: '#2b6cb0', borderRadius: 10, padding: '2px 8px', fontSize: 12 }}>
                          {(r.shiftCriteria || []).length} {isExp ? '▲' : '▼'}
                        </span>
                      </td>
                    </tr>
                    {isExp && (r.shiftCriteria || []).length > 0 && (
                      <tr style={{ background: '#ebf4ff' }}>
                        <td colSpan={7} style={{ padding: '8px 20px 12px' }}>
                          <table style={{ fontSize: 13, width: 'auto' }}>
                            <thead>
                              <tr>
                                <th style={{ textAlign: 'left', paddingRight: 20, paddingBottom: 4 }}>Tag</th>
                                <th style={{ textAlign: 'left', paddingRight: 20, paddingBottom: 4 }}>Date</th>
                                <th style={{ textAlign: 'left', paddingRight: 20, paddingBottom: 4 }}>Site</th>
                                <th style={{ textAlign: 'left', paddingRight: 20, paddingBottom: 4 }}>Start</th>
                                <th style={{ textAlign: 'left', paddingBottom: 4 }}>End</th>
                              </tr>
                            </thead>
                            <tbody>
                              {r.shiftCriteria.map((sc, j) => (
                                <tr key={j}>
                                  <td style={{ paddingRight: 20 }}>{sc.ShTagSetCdName}</td>
                                  <td style={{ paddingRight: 20 }}>{sc.shdate}</td>
                                  <td style={{ paddingRight: 20 }}>{sc.SiteName || '—'}</td>
                                  <td style={{ paddingRight: 20 }}>{sc.tmstart != null ? sc.tmstart : '—'}</td>
                                  <td>{sc.tmend != null ? sc.tmend : '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Timings Tab ───────────────────────────────────────────────────────────────
const MEAL_PERIODS = ['Breakfast', 'Lunch', 'Dinner', 'Reception', 'Coffee Break', 'Cash Concessions'];
const TIME_OPTIONS = Array.from({ length: 24 }, (_, h) =>
  [0, 15, 30, 45].map(m => `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`)
).flat();

function stSecs(st) {
  if (!st) return 0;
  const p = st.split(':');
  return parseInt(p[0] || 0) * 3600 + parseInt(p[1] || 0) * 60 + parseInt(p[2] || 0);
}

function timingGroupKey(t) {
  const date = t.pctimestamp ? t.pctimestamp.substring(0, 10) : '';
  const bucket = Math.floor(stSecs(t.searchtime) / 120); // 2-minute buckets
  return `${t.clientid}|${date}|${bucket}`;
}

function TimingsTab() {
  const [options, setOptions] = useState({ clientids: [], bookingTypes: [] });
  const [clientid, setClientid] = useState('');
  const [bookingType, setBookingType] = useState('');
  const [timings, setTimings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState({});
  const [editingIdx, setEditingIdx] = useState({});
  const [adding, setAdding] = useState({});
  const [dropsMap, setDropsMap] = useState({}); // groupKey -> availableShifts[]
  const [photosMap, setPhotosMap] = useState({}); // groupKey -> photoUrl
  const [uploadingPhoto, setUploadingPhoto] = useState({});
  const [photoPreview, setPhotoPreview] = useState(null);
  const [clients, setClients] = useState([]);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    api.getTimingOptions().then(setOptions).catch(console.error);
    api.getClients().then(setClients).catch(console.error);
  }, []);

  // (clientid + user) -> clientName, so a raw username can be traced back to who it belongs to
  const clientNameByProperty = useMemo(() => {
    const map = {};
    clients.forEach(c => {
      (c.properties || []).forEach(p => {
        map[`${(p.clientid || '').toLowerCase()}|${(p.user || '').toLowerCase()}`] = c.clientName;
      });
    });
    return map;
  }, [clients]);

  useEffect(() => {
    if (!clientid) { setTimings([]); setDropsMap({}); return; }
    setLoading(true);
    api.getTimings(clientid, bookingType)
      .then(setTimings)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [clientid, bookingType]);

  useEffect(() => {
    if (!clientid) return;
    api.getTimingDrops(clientid).then(drops => {
      const dm = {}, pm = {};
      drops.forEach(d => {
        const key = `${d.clientid}|${d.date}|${d.bucket}`;
        dm[key] = d.availableShifts || [];
        if (d.photoUrl) pm[key] = d.photoUrl;
      });
      setDropsMap(dm);
      setPhotosMap(pm);
    }).catch(console.error);
  }, [clientid]);

  // Build group map: key -> { date, bucket, firstIdx, lastIdx, count }
  const groupMap = {};
  const groupFirstIdx = {};
  const groupLastIdx = {};
  const groupCount = {};
  timings.forEach((t, i) => {
    const key = timingGroupKey(t);
    const date = t.pctimestamp ? t.pctimestamp.substring(0, 10) : '';
    const bucket = Math.floor(stSecs(t.searchtime) / 120);
    if (!groupMap[key]) { groupMap[key] = { date, bucket }; groupFirstIdx[key] = i; }
    groupLastIdx[key] = i;
    groupCount[key] = (groupCount[key] || 0) + 1;
  });

  function toggleExpand(key) {
    setExpanded(e => ({ ...e, [key]: !e[key] }));
    setDrafts(d => ({ ...d, [key]: d[key] || { date: '', mealPeriod: 'Breakfast', count: '1', startTime: '', endTime: '' } }));
  }

  async function saveGroupShifts(key, newShifts) {
    setSaving(s => ({ ...s, [key]: true }));
    try {
      const { date, bucket } = groupMap[key];
      await api.upsertTimingDrop(clientid, date, bucket, newShifts);
      setDropsMap(dm => ({ ...dm, [key]: newShifts }));
    } catch (err) { console.error(err); }
    finally { setSaving(s => ({ ...s, [key]: false })); }
  }

  async function handlePhotoUpload(key, file) {
    setUploadingPhoto(u => ({ ...u, [key]: true }));
    try {
      const { url } = await api.uploadTimingPhoto(file);
      const { date, bucket } = groupMap[key];
      await api.upsertTimingDrop(clientid, date, bucket, dropsMap[key] || [], url);
      setPhotosMap(pm => ({ ...pm, [key]: url }));
    } catch (err) { console.error(err); }
    finally { setUploadingPhoto(u => ({ ...u, [key]: false })); }
  }

  function addShift(key) {
    const d = drafts[key] || {};
    if (!d.date || !d.mealPeriod || !d.count) return;
    const entry = { date: d.date, mealPeriod: d.mealPeriod, count: parseInt(d.count) };
    if (d.startTime) entry.startTime = d.startTime;
    if (d.endTime) entry.endTime = d.endTime;
    const current = dropsMap[key] || [];
    const idx = editingIdx[key];
    const newShifts = idx != null
      ? current.map((s, i) => i === idx ? entry : s)
      : [...current, entry];
    setDrafts(ds => ({ ...ds, [key]: { date: d.date, mealPeriod: d.mealPeriod, startTime: d.startTime || '', endTime: d.endTime || '', count: '1' } }));
    setEditingIdx(e => ({ ...e, [key]: null }));
    setAdding(a => ({ ...a, [key]: false }));
    saveGroupShifts(key, newShifts);
  }

  function startEdit(key, idx) {
    const d = (dropsMap[key] || [])[idx];
    if (!d) return;
    setDrafts(ds => ({ ...ds, [key]: { date: d.date, mealPeriod: d.mealPeriod, startTime: d.startTime || '', endTime: d.endTime || '', count: String(d.count) } }));
    setEditingIdx(e => ({ ...e, [key]: idx }));
  }

  function cancelEdit(key) {
    setDrafts(ds => ({ ...ds, [key]: { date: '', mealPeriod: 'Breakfast', startTime: '', endTime: '', count: '1' } }));
    setEditingIdx(e => ({ ...e, [key]: null }));
    setAdding(a => ({ ...a, [key]: false }));
  }

  function removeShift(key, idx) {
    const newShifts = (dropsMap[key] || []).filter((_, i) => i !== idx);
    if (editingIdx[key] === idx) cancelEdit(key);
    saveGroupShifts(key, newShifts);
  }

  // Drop a timing record for good (test runs, bad reads). The backend also
  // removes the source log, so a Refresh won't bring it back.
  async function deleteTiming(t) {
    const when = t.pctimestamp ? formatTs(t.pctimestamp) : '';
    if (!window.confirm(`Delete this timing record permanently?

${t.clientid} / ${t.user}
${when}

This also removes the source log, so it won't come back on the next refresh.`)) return;
    setDeletingId(String(t._id));
    try {
      await api.deleteTiming(t._id);
      setTimings(list => list.filter(r => String(r._id) !== String(t._id)));
    } catch (err) { window.alert(`Delete failed: ${err.message}`); }
    finally { setDeletingId(null); }
  }

  function formatTs(ts) {
    if (!ts) return '';
    const part = ts.split('T')[1];
    return part ? `${ts.substring(0, 10)}  ${part}` : ts;
  }

  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');

  async function handleSync() {
    setSyncing(true);
    setSyncMsg('');
    try {
      const r = await api.syncLogs();
      setSyncMsg(r && r.billsAt
        ? 'Synced! Bills for all clients regenerate in a few seconds.'
        : 'Synced!');
      // Reload timings and drops
      if (clientid) {
        const [t, drops] = await Promise.all([
          api.getTimings(clientid, bookingType),
          api.getTimingDrops(clientid)
        ]);
        setTimings(t);
        const dm = {};
        drops.forEach(d => { dm[`${d.clientid}|${d.date}|${d.bucket}`] = d.availableShifts || []; });
        setDropsMap(dm);
      }
    } catch (err) { setSyncMsg(`Error: ${err.message}`); }
    finally { setSyncing(false); setTimeout(() => setSyncMsg(''), 4000); }
  }

  let lastDate = null;
  let band = false;
  const DATE_COLORS = ['#ffffff', '#ebf4ff'];

  return (
    <div>
      <div className="card" style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Property</label>
          <select value={clientid} onChange={e => {
            const val = e.target.value;
            setClientid(val);
            setBookingType(val ? 'Regular Weekly' : '');
          }} style={{ minWidth: 160 }}>
            <option value="">— select —</option>
            {options.clientids.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Booking Type</label>
          <select value={bookingType} onChange={e => setBookingType(e.target.value)} style={{ minWidth: 160 }} disabled={!clientid}>
            <option value="">All</option>
            {options.bookingTypes.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={handleSync} disabled={syncing} style={{ alignSelf: 'flex-end' }}>
          {syncing ? 'Syncing...' : 'Refresh'}
        </button>
        {syncMsg && <span style={{ fontSize: 13, color: syncMsg.startsWith('Error') ? '#e53e3e' : '#38a169' }}>{syncMsg}</span>}
        {timings.length > 0 && (
          <span style={{ color: '#718096', fontSize: 13, marginLeft: 'auto' }}>{timings.length} records</span>
        )}
      </div>

      {!clientid && !loading && <div className="card" style={{ color: '#718096' }}>Select a property to view timings.</div>}
      {loading && <div className="card" style={{ color: '#718096' }}>Loading...</div>}
      {!loading && clientid && timings.length === 0 && <div className="card" style={{ color: '#718096' }}>No timing records found.</div>}

      {!loading && timings.length > 0 && (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table style={{ minWidth: 750 }}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Client</th>
                <th>Client ID</th>
                <th>User</th>
                <th>Timing</th>
                <th>Return</th>
                <th>Search Time</th>
                <th>PC Timestamp</th>
                <th style={{ textAlign: 'center' }}>Total # of Shifts</th>
                <th style={{ textAlign: 'center' }}>Drops</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {timings.map((t, i) => {
                const dateStr = t.pctimestamp ? t.pctimestamp.substring(0, 10) : '';
                if (dateStr !== lastDate) { band = !band; lastDate = dateStr; }
                const bg = DATE_COLORS[band ? 1 : 0];
                const key = timingGroupKey(t);
                const isFirst = i === groupFirstIdx[key];
                const isLast = i === groupLastIdx[key];
                const count = groupCount[key];
                const isExp = expanded[key];
                const drops = dropsMap[key] || [];
                const totalShifts = drops.reduce((s, d) => s + (d.count || 0), 0);
                const draft = drafts[key] || { date: '', mealPeriod: 'Breakfast', count: '1' };
                return (
                  <React.Fragment key={String(t._id)}>
                    <tr
                      style={{ background: bg, cursor: 'pointer', borderTop: isFirst && i > 0 ? '2px solid #cbd5e0' : undefined }}
                      onClick={() => toggleExpand(key)}
                    >
                      <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{dateStr}</td>
                      <td>{clientNameByProperty[`${(t.clientid || '').toLowerCase()}|${(t.user || '').toLowerCase()}`] || '—'}</td>
                      <td>{t.clientid}</td>
                      <td>{t.user}</td>
                      <td>{t.timing}</td>
                      <td>{t['return']}</td>
                      <td>{t.searchtime}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 13 }}>{formatTs(t.pctimestamp)}</td>
                      <td style={{ textAlign: 'center', fontWeight: totalShifts > 0 ? 600 : 400, color: totalShifts > 0 ? '#276749' : '#a0aec0' }}>
                        {totalShifts > 0 ? totalShifts : '—'}
                      </td>
                      {isFirst && (
                        <td rowSpan={count} style={{ textAlign: 'center', verticalAlign: 'middle', background: bg }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                            <span style={{ background: drops.length ? '#c6f6d5' : '#e2e8f0', color: drops.length ? '#276749' : '#718096', borderRadius: 10, padding: '2px 8px', fontSize: 12, whiteSpace: 'nowrap' }}>
                              {drops.length} {isExp ? '▲' : '▼'}
                            </span>
                            {photosMap[key] && (
                              <a
                                href={(process.env.NODE_ENV === 'development' ? 'http://localhost:3000' : '') + photosMap[key]}
                                target="_blank"
                                rel="noreferrer"
                                style={{ fontSize: 14, lineHeight: 1, textDecoration: 'none' }}
                                onMouseEnter={e => { e.stopPropagation(); setPhotoPreview({ url: photosMap[key], x: e.clientX, y: e.clientY }); }}
                                onMouseMove={e => setPhotoPreview(p => p ? { ...p, x: e.clientX, y: e.clientY } : p)}
                                onMouseLeave={() => setPhotoPreview(null)}
                                onClick={e => e.stopPropagation()}
                              >📷</a>
                            )}
                          </div>
                        </td>
                      )}
                      <td style={{ textAlign: 'center' }}>
                        <button
                          className="btn btn-danger btn-sm"
                          title="Delete this timing record"
                          onClick={e => { e.stopPropagation(); deleteTiming(t); }}
                          disabled={deletingId === String(t._id)}
                        >×</button>
                      </td>
                    </tr>
                    {isLast && isExp && (
                      <tr style={{ background: '#f0fff4' }}>
                        <td colSpan={11} style={{ padding: '10px 20px 14px' }}>
                          {drops.length > 0 && (
                            <table style={{ fontSize: 13, marginBottom: 10, width: 'auto' }}>
                              <thead>
                                <tr>
                                  <th style={{ textAlign: 'left', paddingRight: 20, paddingBottom: 4 }}>Date</th>
                                  <th style={{ textAlign: 'left', paddingRight: 20, paddingBottom: 4 }}>Meal Period</th>
                                  <th style={{ textAlign: 'left', paddingRight: 20, paddingBottom: 4 }}>Start</th>
                                  <th style={{ textAlign: 'left', paddingRight: 20, paddingBottom: 4 }}>End</th>
                                  <th style={{ textAlign: 'left', paddingRight: 20, paddingBottom: 4 }}>Available</th>
                                  <th></th>
                                </tr>
                              </thead>
                              <tbody>
                                {drops.map((d, j) => (
                                  <tr key={j} style={{ background: editingIdx[key] === j ? '#fefcbf' : undefined }}>
                                    <td style={{ paddingRight: 20 }}>{d.date}</td>
                                    <td style={{ paddingRight: 20 }}>{d.mealPeriod}</td>
                                    <td style={{ paddingRight: 20 }}>{d.startTime || '—'}</td>
                                    <td style={{ paddingRight: 20 }}>{d.endTime || '—'}</td>
                                    <td style={{ paddingRight: 20, fontWeight: 600 }}>{d.count}</td>
                                    <td style={{ display: 'flex', gap: 4 }}>
                                      <button className="btn btn-secondary btn-sm" onClick={e => { e.stopPropagation(); startEdit(key, j); }} disabled={saving[key]}>Edit</button>
                                      <button className="btn btn-danger btn-sm" onClick={e => { e.stopPropagation(); removeShift(key, j); }} disabled={saving[key]}>×</button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }} onClick={e => e.stopPropagation()}>
                            {photosMap[key] ? (
                              <>
                                <a href={(process.env.NODE_ENV === 'development' ? 'http://localhost:3000' : '') + photosMap[key]} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: '#2b6cb0' }}>📷 View Photo</a>
                                <label style={{ fontSize: 12, color: '#718096', cursor: 'pointer' }}>
                                  Replace
                                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => e.target.files[0] && handlePhotoUpload(key, e.target.files[0])} />
                                </label>
                              </>
                            ) : (
                              <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer', marginBottom: 0 }}>
                                {uploadingPhoto[key] ? 'Uploading...' : '📷 Upload Photo'}
                                <input type="file" accept="image/*" style={{ display: 'none' }} disabled={uploadingPhoto[key]} onChange={e => e.target.files[0] && handlePhotoUpload(key, e.target.files[0])} />
                              </label>
                            )}
                          </div>
                          {(adding[key] || editingIdx[key] != null) ? (
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
                              <input type="date" value={draft.date} onChange={e => setDrafts(ds => ({ ...ds, [key]: { ...(ds[key] || {}), date: e.target.value } }))} style={{ width: 140 }} />
                              <select value={draft.mealPeriod} onChange={e => setDrafts(ds => ({ ...ds, [key]: { ...(ds[key] || {}), mealPeriod: e.target.value } }))} style={{ width: 140 }}>
                                {MEAL_PERIODS.map(m => <option key={m} value={m}>{m}</option>)}
                              </select>
                              <select value={draft.startTime || ''} onChange={e => setDrafts(ds => ({ ...ds, [key]: { ...(ds[key] || {}), startTime: e.target.value } }))} style={{ width: 110 }}>
                                <option value="">Start (opt)</option>
                                {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                              </select>
                              <select value={draft.endTime || ''} onChange={e => setDrafts(ds => ({ ...ds, [key]: { ...(ds[key] || {}), endTime: e.target.value } }))} style={{ width: 110 }}>
                                <option value="">End (opt)</option>
                                {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                              </select>
                              <input type="number" min="0" placeholder="# shifts" value={draft.count} onChange={e => setDrafts(ds => ({ ...ds, [key]: { ...(ds[key] || {}), count: e.target.value } }))} style={{ width: 90 }} />
                              <button className="btn btn-primary btn-sm" onClick={() => addShift(key)} disabled={saving[key] || !draft.date || !draft.count}>
                                {saving[key] ? 'Saving...' : editingIdx[key] != null ? 'Save' : 'Add'}
                              </button>
                              <button className="btn btn-secondary btn-sm" onClick={e => { e.stopPropagation(); cancelEdit(key); }}>Cancel</button>
                            </div>
                          ) : (
                            <button className="btn btn-primary btn-sm" onClick={e => { e.stopPropagation(); setAdding(a => ({ ...a, [key]: true })); setDrafts(ds => ({ ...ds, [key]: { date: '', mealPeriod: 'Breakfast', startTime: '', endTime: '', count: '1' } })); }}>+ Add</button>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {photoPreview && (
        <div style={{ position: 'fixed', left: photoPreview.x - 8, top: 20, transform: 'translateX(-100%)', zIndex: 9999, pointerEvents: 'none', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.2)', padding: 4 }}>
          <img src={(process.env.NODE_ENV === 'development' ? 'http://localhost:3000' : '') + photoPreview.url} alt="preview" style={{ maxHeight: 'calc(100vh - 40px)', maxWidth: 500, width: 'auto', display: 'block', borderRadius: 4 }} />
        </div>
      )}
    </div>
  );
}

// ── Logs Tab ──────────────────────────────────────────────────────────────────
// Log times are stored in UTC; the venues (and the people reading this) are all
// Pacific, so every date/time shown or filtered here is converted to Pacific.
const LOGS_TZ = 'America/Los_Angeles';

function tzOffsetMs(date, timeZone) {
  const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
  const tzDate = new Date(date.toLocaleString('en-US', { timeZone }));
  return tzDate.getTime() - utcDate.getTime();
}

// 'YYYY-MM-DD' Pacific calendar date -> UTC Date for midnight of that day in Pacific
function pacificMidnightUTC(dateStr) {
  const approx = new Date(`${dateStr}T00:00:00.000Z`);
  return new Date(approx.getTime() - tzOffsetMs(approx, LOGS_TZ));
}

function pacificDateStr(date) {
  return date.toLocaleDateString('en-CA', { timeZone: LOGS_TZ }); // YYYY-MM-DD
}

function fmtPacific(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return ts;
  const datePart = d.toLocaleDateString('en-CA', { timeZone: LOGS_TZ });
  const timePart = d.toLocaleTimeString('en-GB', { timeZone: LOGS_TZ });
  return `${datePart}  ${timePart}`;
}

function LogsTab() {
  const [options, setOptions] = useState({ clientids: [], users: [] });
  const [range, setRange] = useState('today'); // 'today' | 'yesterday' | '7' | 'custom'
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientid, setClientid] = useState('');
  const [user, setUser] = useState('');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewing, setViewing] = useState(null); // { loading, message }
  const [clients, setClients] = useState([]);

  useEffect(() => { api.getLogOptions().then(setOptions).catch(console.error); }, []);
  useEffect(() => { api.getClients().then(setClients).catch(console.error); }, []);

  // Logs are keyed by login (user), not the billing client's name — look it up.
  const clientNameByUser = useMemo(() => {
    const map = {};
    clients.forEach(c => {
      (c.properties || []).forEach(p => {
        if (p.user) map[p.user.toLowerCase()] = c.clientName;
      });
    });
    return map;
  }, [clients]);

  const clientNames = useMemo(
    () => clients.filter(c => (c.properties || []).length).map(c => c.clientName).sort((a, b) => a.localeCompare(b)),
    [clients]
  );

  // With a client picked, the Property and User ID lists narrow to that client's
  // own properties (and, once a property is picked, the logins on it).
  const { propertyOptions, userOptions } = useMemo(() => {
    if (!clientName) return { propertyOptions: options.clientids, userOptions: options.users };
    const props = (clients.find(c => c.clientName === clientName)?.properties || []).filter(p => p.clientid && p.user);
    const uniq = arr => [...new Set(arr)].sort((a, b) => a.localeCompare(b));
    return {
      propertyOptions: uniq(props.map(p => p.clientid)),
      userOptions: uniq(props.filter(p => !clientid || p.clientid.toLowerCase() === clientid.toLowerCase()).map(p => p.user))
    };
  }, [clientName, clientid, clients, options]);

  function pickClient(name) {
    setClientName(name);
    setClientid('');
    setUser('');
  }

  function pickProperty(cid) {
    setClientid(cid);
    // Drop a login that isn't on the newly picked property for this client.
    if (clientName && cid && user) {
      const props = clients.find(c => c.clientName === clientName)?.properties || [];
      const ok = props.some(p => p.clientid?.toLowerCase() === cid.toLowerCase() && p.user?.toLowerCase() === user.toLowerCase());
      if (!ok) setUser('');
    }
  }

  const { start, end } = useMemo(() => {
    const dayMs = 24 * 60 * 60 * 1000;
    const todayStart = pacificMidnightUTC(pacificDateStr(new Date()));
    if (range === 'today') {
      return { start: todayStart.toISOString(), end: new Date(todayStart.getTime() + dayMs).toISOString() };
    }
    if (range === 'yesterday') {
      return { start: new Date(todayStart.getTime() - dayMs).toISOString(), end: todayStart.toISOString() };
    }
    if (range === '7') {
      return { start: new Date(todayStart.getTime() - 6 * dayMs).toISOString(), end: new Date(todayStart.getTime() + dayMs).toISOString() };
    }
    if (customStart && customEnd) {
      return { start: pacificMidnightUTC(customStart).toISOString(), end: new Date(pacificMidnightUTC(customEnd).getTime() + dayMs).toISOString() };
    }
    return { start: undefined, end: undefined };
  }, [range, customStart, customEnd]);

  useEffect(() => {
    if (range === 'custom' && (!customStart || !customEnd)) { setLogs([]); return; }
    setLoading(true);
    api.getLogs({ clientName, clientid, user, start, end })
      .then(setLogs)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [range, customStart, customEnd, clientName, clientid, user, start, end]);

  async function openLog(id) {
    setViewing({ loading: true, message: '' });
    try {
      const full = await api.getLog(id);
      setViewing({ loading: false, message: full.message || '(no message)' });
    } catch (err) {
      setViewing({ loading: false, message: `Error: ${err.message}` });
    }
  }

  const RANGE_LABELS = { today: 'Today', yesterday: 'Yesterday', '7': 'Last 7 Days', custom: 'Date Range' };

  return (
    <div>
      <div className="card" style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {['today', 'yesterday', '7', 'custom'].map(r => (
            <button
              key={r}
              className={`toggle-btn${range === r ? ' active' : ''}`}
              onClick={() => setRange(r)}
            >{RANGE_LABELS[r]}</button>
          ))}
        </div>

        {range === 'custom' && (
          <>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>From</label>
              <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} style={{ width: 150 }} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>To</label>
              <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} style={{ width: 150 }} />
            </div>
          </>
        )}

        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Client</label>
          <select value={clientName} onChange={e => pickClient(e.target.value)} style={{ minWidth: 140 }}>
            <option value="">All Clients</option>
            {clientNames.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>

        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Property</label>
          <select value={clientid} onChange={e => pickProperty(e.target.value)} style={{ minWidth: 140 }}>
            <option value="">All Properties</option>
            {propertyOptions.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>User ID</label>
          <select value={user} onChange={e => setUser(e.target.value)} style={{ minWidth: 140 }}>
            <option value="">All Users</option>
            {userOptions.map(u => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>

        {(clientName || clientid || user) && (
          <button className="btn btn-sm btn-secondary" onClick={() => { setClientName(''); setClientid(''); setUser(''); }}>Clear Filters</button>
        )}

        <span style={{ color: '#718096', fontSize: 13, marginLeft: 'auto' }}>
          {loading ? 'Loading...' : `${logs.length} log${logs.length !== 1 ? 's' : ''}`}
        </span>
      </div>

      {!loading && logs.length === 0 && (
        <div className="card" style={{ color: '#718096' }}>
          {range === 'custom' && (!customStart || !customEnd) ? 'Select a start and end date.' : 'No logs found for this period.'}
        </div>
      )}

      {logs.length > 0 && (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table style={{ minWidth: 900 }}>
            <thead>
              <tr>
                <th></th>
                <th style={{ whiteSpace: 'nowrap' }}>Time (PT)</th>
                <th>Property</th>
                <th>Client</th>
                <th>User ID</th>
                <th>Search Time</th>
                <th title="Remote = started from the website; Local = from the PC's own page; — = too old to tell">Started</th>
                <th>Booking Window</th>
                <th>Criteria</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l, i) => (
                <tr key={String(l._id)} style={{ background: i % 2 === 0 ? '#ffffff' : '#f7f8fa' }}>
                  <td><button className="btn btn-secondary btn-sm" onClick={() => openLog(l._id)}>View</button></td>
                  <td style={{ whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: 12 }}>{fmtPacific(l.updatedAt)}</td>
                  <td>{l.clientid}</td>
                  <td>{clientNameByUser[String(l.user).toLowerCase()] || '—'}</td>
                  <td>{l.user}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{l.searchtime || '—'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{l.startedVia ? <RunSourceBadge via={l.startedVia} by={l.startedBy} /> : <span style={{ color: '#a0aec0' }} title="Too old to tell (website commands are kept 7 days)">—</span>}</td>
                  <td style={{ whiteSpace: 'nowrap', fontSize: 12, color: '#718096' }}>{l.start && l.end ? `${l.start} – ${l.end}` : '—'}</td>
                  <td style={{ fontSize: 12, color: '#718096' }}>{(l.shiftCriteria || []).length} target{(l.shiftCriteria || []).length !== 1 ? 's' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {viewing && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
          onClick={() => setViewing(null)}
        >
          <div
            style={{ background: '#1a1a2e', color: '#e2e8f0', borderRadius: 8, maxWidth: 900, width: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #2d3748' }}>
              <strong style={{ fontSize: 14 }}>Log Detail</strong>
              <button className="btn btn-secondary btn-sm" onClick={() => setViewing(null)}>Close</button>
            </div>
            <div className="log-detail-body" style={{ padding: 16, overflow: 'auto', fontFamily: 'monospace', lineHeight: 1.6 }}>
              {viewing.loading
                ? 'Loading...'
                : <div dangerouslySetInnerHTML={{ __html: viewing.message }} />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Send Email Tab ────────────────────────────────────────────────────────────
function SendEmailTab() {
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState('');

  async function handleSend(e) {
    e.preventDefault();
    setSending(true);
    setMsg('');
    try {
      await api.sendEmail(to, subject, message);
      setMsg('Email sent successfully.');
      setTo('');
      setSubject('');
      setMessage('');
    } catch (err) {
      setMsg(`Error: ${err.message}`);
    } finally {
      setSending(false);
    }
  }

  return (
    <div>
      <div className="card">
        <div className="card-title">Send Email</div>
        <p style={{ fontSize: 13, color: '#718096', marginBottom: 16 }}>
          Sends from <strong>Alerts@bqtexpress.com</strong> via Brevo. Replies go to <strong>bqtexpress@gmail.com</strong>, which is also blind-copied on every message.
        </p>
        {msg && <div className={`alert ${msg.startsWith('Error') ? 'alert-error' : 'alert-success'}`}>{msg}</div>}
        <form onSubmit={handleSend}>
          <div className="form-group">
            <label>Recipient</label>
            <input
              type="email"
              placeholder="client@example.com"
              value={to}
              onChange={e => setTo(e.target.value)}
              required
              style={{ maxWidth: 360 }}
            />
          </div>
          <div className="form-group">
            <label>Subject</label>
            <input
              type="text"
              placeholder="Subject"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              style={{ maxWidth: 480 }}
            />
          </div>
          <div className="form-group">
            <label>Message</label>
            <textarea
              placeholder="Type your message here..."
              value={message}
              onChange={e => setMessage(e.target.value)}
              required
              rows={8}
              style={{ width: '100%', maxWidth: 640, resize: 'vertical' }}
            />
          </div>
          <button className="btn btn-primary" type="submit" disabled={sending}>
            {sending ? 'Sending...' : 'Send Email'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Version Tab ───────────────────────────────────────────────────────────────
function VersionTab() {
  const [version, setVersion] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  async function handlePush() {
    if (!version.trim()) return;
    if (!window.confirm(`Push version "${version.trim()}" to ALL users? They will be blocked from older versions.`)) return;
    setLoading(true);
    setMsg('');
    try {
      const res = await api.updateAllUsersVersion(version.trim());
      setMsg(`Done — ${res.updated} user(s) updated to ${version.trim()}.`);
    } catch (err) {
      setMsg(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <div className="card">
        <div className="card-title">Master Version Update</div>
        <p style={{ fontSize: 13, color: '#718096', marginBottom: 16 }}>
          Sets the <strong>version</strong> field on every user account at once. Use this to block old app versions — users on a different version will be denied access.
        </p>
        {msg && <div className={`alert ${msg.startsWith('Error') ? 'alert-error' : 'alert-success'}`}>{msg}</div>}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Required Version</label>
            <input
              value={version}
              onChange={e => setVersion(e.target.value)}
              placeholder="e.g. 6.2.0"
              style={{ width: 160 }}
            />
          </div>
          <button
            className="btn btn-primary"
            style={{ alignSelf: 'flex-end' }}
            onClick={handlePush}
            disabled={loading || !version.trim()}
          >
            {loading ? 'Pushing...' : 'Push to All Users'}
          </button>
        </div>
      </div>
      <AppUpdatesSection />
    </div>
  );
}

function toAppUpdateForm(doc) {
  return {
    latestVersion: doc.latestVersion || '',
    notes: doc.notes || '',
    active: !!doc.active,
    files: Object.entries(doc.files || {}).map(([key, url]) => ({ key, url: url || '' })),
    testVersion: doc.testVersion || '',
    testFiles: Object.entries(doc.testFiles || {}).map(([key, url]) => ({ key, url: url || '' })),
  };
}

function AppUpdatesSection() {
  const [docs, setDocs] = useState([]);
  const [forms, setForms] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getAppUpdates();
      setDocs(data);
      setForms(Object.fromEntries(data.map(d => [d._id, toAppUpdateForm(d)])));
    } catch (err) {
      setMsg(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function setField(id, field, value) {
    setForms(f => ({ ...f, [id]: { ...f[id], [field]: value } }));
  }

  // list = 'files' (stable release) or 'testFiles' (test build for admin slots).
  function setFile(id, idx, field, value, list = 'files') {
    setForms(f => ({
      ...f,
      [id]: { ...f[id], [list]: f[id][list].map((row, i) => i === idx ? { ...row, [field]: value } : row) },
    }));
  }

  function addFile(id, list = 'files') {
    setForms(f => ({ ...f, [id]: { ...f[id], [list]: [...f[id][list], { key: '', url: '' }] } }));
  }

  function removeFile(id, idx, list = 'files') {
    setForms(f => ({ ...f, [id]: { ...f[id], [list]: f[id][list].filter((_, i) => i !== idx) } }));
  }

  // Copies the test build into the stable fields and clears the test, in the form only;
  // it goes live for every machine when you press Save.
  function promoteTest(id) {
    setForms(f => ({ ...f, [id]: { ...f[id], latestVersion: f[id].testVersion, files: f[id].testFiles.map(r => ({ ...r })), testVersion: '', testFiles: [] } }));
    setMsg('Test build copied into Latest Version and Files. Press Save to release it to all machines.');
  }

  const toFileMap = rows => {
    const out = {};
    rows.forEach(r => { if (r.key.trim()) out[r.key.trim()] = r.url.trim(); });
    return out;
  };

  async function handleSave(doc) {
    const form = forms[doc._id];
    if (!form.latestVersion.trim()) { setMsg('Error: Latest version is required.'); return; }
    const keys = form.files.map(r => r.key.trim()).filter(Boolean);
    if (new Set(keys).size !== keys.length) { setMsg('Error: Duplicate file keys.'); return; }
    const testKeys = form.testFiles.map(r => r.key.trim()).filter(Boolean);
    if (new Set(testKeys).size !== testKeys.length) { setMsg('Error: Duplicate test file keys.'); return; }
    setSaving(doc._id);
    setMsg('');
    try {
      const updated = await api.updateAppUpdate(doc._id, {
        latestVersion: form.latestVersion.trim(),
        notes: form.notes,
        active: form.active,
        files: toFileMap(form.files),
        testVersion: form.testVersion.trim(),
        testFiles: toFileMap(form.testFiles),
      });
      setDocs(ds => ds.map(d => d._id === updated._id ? updated : d));
      setForms(f => ({ ...f, [updated._id]: toAppUpdateForm(updated) }));
      setMsg(`Saved ${updated.app} — latest version ${updated.latestVersion}.`);
    } catch (err) {
      setMsg(`Error: ${err.message}`);
    } finally {
      setSaving(null);
    }
  }

  function renderFiles(id, form, list) {
    return (
      <table style={{ width: '100%', marginTop: 6, marginBottom: 8 }}>
        <thead>
          <tr>
            <th style={{ width: 110 }}>Key</th>
            <th>Download URL</th>
            <th style={{ width: 70 }}></th>
          </tr>
        </thead>
        <tbody>
          {form[list].map((row, i) => (
            <tr key={i}>
              <td>
                <input value={row.key} onChange={e => setFile(id, i, 'key', e.target.value, list)} placeholder="49152" style={{ width: 90 }} />
              </td>
              <td>
                <input value={row.url} onChange={e => setFile(id, i, 'url', e.target.value, list)} placeholder="https://..." style={{ width: '100%' }} />
              </td>
              <td>
                <button className="btn btn-sm btn-danger" onClick={() => removeFile(id, i, list)}>Remove</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  return (
    <div className="card">
      <div className="card-title">App Updates</div>
      <p style={{ fontSize: 13, color: '#718096', marginBottom: 16 }}>
        Edits the <strong>AppUpdates</strong> collection used by the desktop app updater.
      </p>
      {msg && <div className={`alert ${msg.startsWith('Error') ? 'alert-error' : 'alert-success'}`}>{msg}</div>}
      {loading && <p>Loading...</p>}
      {!loading && docs.length === 0 && <p style={{ fontSize: 13, color: '#718096' }}>No records found.</p>}
      {docs.map(doc => {
        const form = forms[doc._id];
        if (!form) return null;
        return (
          <div key={doc._id} style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: 16, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <strong>{doc.app}</strong>
              <span style={{ fontSize: 12, color: '#718096' }}>
                Last updated: {doc.updatedAt ? new Date(doc.updatedAt).toLocaleString() : '—'}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 12 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Latest Version</label>
                <input
                  value={form.latestVersion}
                  onChange={e => setField(doc._id, 'latestVersion', e.target.value)}
                  placeholder="e.g. 6.1.13"
                  style={{ width: 160 }}
                />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, paddingBottom: 8 }}>
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={e => setField(doc._id, 'active', e.target.checked)}
                />
                Active
              </label>
            </div>
            <div className="form-group">
              <label>Notes</label>
              <textarea
                value={form.notes}
                onChange={e => setField(doc._id, 'notes', e.target.value)}
                rows={3}
                style={{ width: '100%' }}
              />
            </div>
            <label style={{ fontSize: 14, fontWeight: 600 }}>Files</label>
            {renderFiles(doc._id, form, 'files')}
            <div style={{ borderTop: '1px dashed #cbd5e0', marginTop: 14, paddingTop: 12, marginBottom: 12 }}>
              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 6 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Test Version</label>
                  <input
                    value={form.testVersion}
                    onChange={e => setField(doc._id, 'testVersion', e.target.value)}
                    placeholder="empty = off"
                    style={{ width: 160 }}
                  />
                </div>
                {form.testVersion.trim() && (
                  <button className="btn btn-sm btn-secondary" style={{ marginBottom: 6 }} onClick={() => promoteTest(doc._id)}>
                    Promote test to latest
                  </button>
                )}
              </div>
              <p style={{ fontSize: 12, color: '#718096', margin: '0 0 6px' }}>
                Only installed on admin-owned slots, one at a time, with the Fleet tab's <strong>Test</strong> button. Machines never see it as an available update.
              </p>
              <label style={{ fontSize: 14, fontWeight: 600 }}>Test Files</label>
              {renderFiles(doc._id, form, 'testFiles')}
              <button className="btn btn-sm btn-secondary" onClick={() => addFile(doc._id, 'testFiles')}>+ Add Test File</button>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-secondary" onClick={() => addFile(doc._id)}>+ Add File</button>
              <button className="btn btn-secondary" onClick={() => setForms(f => ({ ...f, [doc._id]: toAppUpdateForm(doc) }))} disabled={saving === doc._id}>
                Reset
              </button>
              <button className="btn btn-primary" onClick={() => handleSave(doc)} disabled={saving === doc._id}>
                {saving === doc._id ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Recent Bookings Tab ───────────────────────────────────────────────────────
// Fallback only — used when the matching LogsSummary record has already aged out
// (90-day retention). Mirrors jobs/syncLogsSummary.js's extractTiming.
function extractTiming(searchtime) {
  if (typeof searchtime !== 'string' || !searchtime) return null;
  const parts = searchtime.split(':');
  if (parts.length < 2) return null;
  return parts.slice(-2).join(':');
}

function RecentBookingsTab() {
  const [bills, setBills] = useState([]);
  const [logRecords, setLogRecords] = useState([]);
  const [dateRange, setDateRange] = useState('7');   // 'today' | '7' | '30'
  const [filterClient, setFilterClient] = useState('');
  const [filterProperty, setFilterProperty] = useState('');
  const [filterTag, setFilterTag] = useState('');
  // Shift-date range (YYYY-MM-DD, same format as shdate), applied on top of the booked-on range.
  const [shiftFrom, setShiftFrom] = useState('');
  const [shiftTo, setShiftTo] = useState('');
  // Until the end date is picked by hand it mirrors the start date (single-day filter).
  const [shiftToManual, setShiftToManual] = useState(false);
  const shiftDateActive = !!(shiftFrom || shiftTo);

  function handleShiftFromChange(value) {
    setShiftFrom(value);
    // Follow the start date unless the end was chosen by hand — or the hand-picked end
    // is now before the start, which would leave an empty range.
    if (!shiftToManual || !shiftTo || (value && shiftTo < value)) {
      setShiftTo(value);
      setShiftToManual(false);
    }
  }

  function handleShiftToChange(value) {
    setShiftTo(value);
    setShiftToManual(!!value);
  }

  function clearShiftDates() {
    setShiftFrom('');
    setShiftTo('');
    setShiftToManual(false);
  }

  useEffect(() => { api.getBills().then(setBills).catch(console.error); }, []);
  useEffect(() => { api.getTimings().then(setLogRecords).catch(() => {}); }, []);

  // Real Timing/Return come from the matching LogsSummary record (same clientid+user
  // +searchtime as this shift's booking run) — Return is derived from that record's
  // actual pctimestamp (the PC server time), not from SelfSchAssignTime.
  // searchtime alone isn't a unique key: its jittered ms can coincide across different
  // days for the same user/property, so the date (from pctimestamp) must be part of the
  // key or an unrelated day's record silently overwrites the real one.
  const logMap = useMemo(() => {
    const map = {};
    logRecords.forEach(r => {
      const date = typeof r.pctimestamp === 'string' ? r.pctimestamp.split('T')[0] : '';
      map[`${r.clientid}|${r.user}|${r.searchtime}|${date}`] = r;
    });
    return map;
  }, [logRecords]);

  // Flatten all shifts from all bills, attach client context
  const allShifts = useMemo(() => {
    const result = [];
    bills.forEach(bill => {
      (bill.shifts || []).forEach(s => {
        if (s.SelfSchAssignTime) {
          result.push({ ...s, clientName: bill.clientName, billingType: bill.billingType, billId: bill._id });
        }
      });
    });

    result.forEach(s => {
      const date = s.SelfSchAssignTime ? s.SelfSchAssignTime.split('T')[0] : '';
      const rec = s.searchtime ? logMap[`${s.property}|${s.user}|${s.searchtime}|${date}`] : null;
      s.timing = rec ? rec.timing : extractTiming(s.searchtime);
      s.returnVal = rec ? rec.return : null;
    });

    // Sort by booked-on day (newest first); within a day, keep each client's shifts together,
    // ordering clients by their earliest timing that day, then shifts by timing.
    const timingMs = t => {
      if (!t) return Infinity;
      const [sec, ms] = String(t).split(':');
      return (parseInt(sec, 10) || 0) * 1000 + (parseInt(ms, 10) || 0);
    };
    const day = s => s.SelfSchAssignTime.split('T')[0];
    const bestTiming = {};
    result.forEach(s => {
      const k = `${day(s)}|${s.clientName}`;
      bestTiming[k] = Math.min(bestTiming[k] ?? Infinity, timingMs(s.timing));
    });
    const cmpNum = (x, y) => (x === y ? 0 : x < y ? -1 : 1);
    return result.sort((a, b) =>
      day(b).localeCompare(day(a))
      || cmpNum(bestTiming[`${day(a)}|${a.clientName}`], bestTiming[`${day(b)}|${b.clientName}`])
      || (a.clientName || '').localeCompare(b.clientName || '')
      || cmpNum(timingMs(a.timing), timingMs(b.timing))
      || b.SelfSchAssignTime.localeCompare(a.SelfSchAssignTime));
  }, [bills, logMap]);

  const filtered = useMemo(() => {
    const now = Date.now();
    // SelfSchAssignTime is Pacific local time with no zone suffix, so "today" must be the Pacific date
    const todayPST = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
    return allShifts.filter(s => {
      if (dateRange === 'today') return s.SelfSchAssignTime.startsWith(todayPST);
      const ageMs = now - new Date(s.SelfSchAssignTime).getTime();
      return ageMs <= parseInt(dateRange) * 24 * 60 * 60 * 1000;
    })
    .filter(s => !shiftDateActive || (!!s.shdate && (!shiftFrom || s.shdate >= shiftFrom) && (!shiftTo || s.shdate <= shiftTo)))
    .filter(s => !filterClient || s.clientName === filterClient)
    .filter(s => !filterProperty || s.property === filterProperty)
    .filter(s => !filterTag || s.normalizedTag === filterTag);
  }, [allShifts, dateRange, shiftDateActive, shiftFrom, shiftTo, filterClient, filterProperty, filterTag]);

  // Per-shift revenue for the current filter. Monthly clients are excluded — their
  // shifts carry no per-shift price. Canceled and no-charge shifts are excluded too:
  // they are never billed, so counting them would overstate what was earned.
  const perShiftTotals = useMemo(() => {
    const t = { billed: 0, paid: 0, unpaid: 0, count: 0, skipped: 0, monthly: 0 };
    filtered.forEach(sh => {
      if (sh.billingType !== 'perShift') { t.monthly++; return; }
      if (sh.canceled || sh.noCharge) { t.skipped++; return; }
      const rate = sh.rate || 0;
      t.billed += rate;
      t.count++;
      if (sh.paid) t.paid += rate; else t.unpaid += rate;
    });
    const r2 = n => Math.round(n * 100) / 100;
    return { ...t, billed: r2(t.billed), paid: r2(t.paid), unpaid: r2(t.unpaid) };
  }, [filtered]);

  const clientNames = useMemo(() => [...new Set(allShifts.map(s => s.clientName))].sort(), [allShifts]);
  const properties = useMemo(() => [...new Set(allShifts.map(s => s.property).filter(Boolean))].sort(), [allShifts]);
  const tags = useMemo(() => [...new Set(allShifts.map(s => s.normalizedTag).filter(Boolean))].sort(), [allShifts]);

  function fmtBookedOn(dt) {
    if (!dt) return '—';
    const d = new Date(dt);
    const date = `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
    const h = d.getHours(), m = String(d.getMinutes()).padStart(2, '0');
    const ampm = h >= 12 ? 'PM' : 'AM';
    return `${date} ${h % 12 || 12}:${m} ${ampm}`;
  }

  function fmtShiftDate(d) {
    if (!d) return '—';
    const [y, mo, day] = d.split('-');
    return `${parseInt(mo)}/${parseInt(day)}/${y}`;
  }

  function fmtTime(t) {
    if (t == null) return '';
    const s = String(t).padStart(4, '0');
    return `${s.slice(0, 2)}:${s.slice(2)}`;
  }

  const rangeLabel = { today: 'Today', '7': 'Last 7 Days', '30': 'Last 30 Days' };

  return (
    <div>
      {/* Controls */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
        {/* Date range buttons */}
        <div style={{ display: 'flex', gap: 4 }}>
          {['today', '7', '30'].map(r => (
            <button
              key={r}
              className={`btn btn-sm ${dateRange === r ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setDateRange(r)}
            >
              {rangeLabel[r]}
            </button>
          ))}
        </div>

        {/* Client filter */}
        <select
          value={filterClient}
          onChange={e => setFilterClient(e.target.value)}
          style={{ padding: '4px 8px', fontSize: 13, borderRadius: 4, border: '1px solid #e2e8f0' }}
        >
          <option value="">All Clients</option>
          {clientNames.map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        {/* Property filter */}
        <select
          value={filterProperty}
          onChange={e => setFilterProperty(e.target.value)}
          style={{ padding: '4px 8px', fontSize: 13, borderRadius: 4, border: '1px solid #e2e8f0' }}
        >
          <option value="">All Properties</option>
          {properties.map(p => <option key={p} value={p}>{p}</option>)}
        </select>

        {/* Tag filter */}
        <select
          value={filterTag}
          onChange={e => setFilterTag(e.target.value)}
          style={{ padding: '4px 8px', fontSize: 13, borderRadius: 4, border: '1px solid #e2e8f0' }}
        >
          <option value="">All Tags</option>
          {tags.map(t => <option key={t} value={t}>{t}</option>)}
        </select>

        {/* Shift date filter */}
        <div style={{ display: 'flex', gap: 4, alignItems: 'center', fontSize: 13 }}>
          <span style={{ color: '#718096' }}>Shift date</span>
          <input
            type="date"
            value={shiftFrom}
            onChange={e => handleShiftFromChange(e.target.value)}
            style={{ padding: '3px 6px', fontSize: 13, borderRadius: 4, border: `1px solid ${shiftDateActive ? '#3182ce' : '#e2e8f0'}` }}
          />
          <span style={{ color: '#718096' }}>to</span>
          <input
            type="date"
            value={shiftTo}
            min={shiftFrom || undefined}
            onChange={e => handleShiftToChange(e.target.value)}
            style={{ padding: '3px 6px', fontSize: 13, borderRadius: 4, border: `1px solid ${shiftDateActive ? '#3182ce' : '#e2e8f0'}` }}
          />
        </div>

        <span style={{ fontSize: 13, color: '#718096', marginLeft: 4 }}>
          {filtered.length} shift{filtered.length !== 1 ? 's' : ''}
        </span>

        {perShiftTotals.count > 0 && (
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, color: '#718096' }}>Per-shift total</span>
            <strong style={{ fontSize: 18, color: '#276749' }}>{fmt(perShiftTotals.billed)}</strong>
            <span style={{ fontSize: 12, color: '#718096' }}>
              {fmt(perShiftTotals.paid)} collected
              {perShiftTotals.unpaid > 0 && <> · <span style={{ color: '#c53030', fontWeight: 600 }}>{fmt(perShiftTotals.unpaid)} outstanding</span></>}
            </span>
          </span>
        )}

        {(filterClient || filterProperty || filterTag || shiftDateActive) && (
          <button className="btn btn-sm btn-secondary" onClick={() => { setFilterClient(''); setFilterProperty(''); setFilterTag(''); clearShiftDates(); }}>
            Clear Filters
          </button>
        )}
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <p style={{ color: '#aaa', fontSize: 13 }}>No bookings in this period.</p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ whiteSpace: 'nowrap' }}>Booked On</th>
                <th>Timing</th>
                <th>Return</th>
                <th style={{ whiteSpace: 'nowrap' }}>Assigned Time</th>
                <th>Client</th>
                <th>Property</th>
                <th>User</th>
                <th style={{ whiteSpace: 'nowrap' }}>Shift Date</th>
                <th>Time</th>
                <th>Tag</th>
                <th>Cost</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s, i) => {
                const statusClass = s.canceled ? 'badge-unpaid' : s.billingType === 'perMonth' ? 'badge-nocharge' : s.paid ? 'badge-paid' : s.clientMarkedPaid ? 'badge-partial' : 'badge-unpaid';
                const statusLabel = s.canceled ? 'canceled' : s.billingType === 'perMonth' ? 'Monthly' : s.paid ? 'paid' : s.clientMarkedPaid ? 'pending' : 'unpaid';
                const rowStyle = s.canceled ? { background: '#f9f9f9', color: '#aaa' } : {};
                return (
                  <tr key={`${s.billId}-${s.shiftId || i}`} style={rowStyle}>
                    <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{fmtBookedOn(s.SelfSchAssignTime)}</td>
                    <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{s.timing || '—'}</td>
                    <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{s.returnVal || '—'}</td>
                    <td style={{ whiteSpace: 'nowrap', fontSize: 11, color: '#aaa' }}>{s.SelfSchAssignTime || '—'}</td>
                    <td style={{ whiteSpace: 'nowrap', fontWeight: 500 }}>{s.clientName}</td>
                    <td className="cell-trunc">{s.property}</td>
                    <td>{s.user}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtShiftDate(s.shdate)}</td>
                    <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                      {fmtTime(s.tmstart)}{fmtTime(s.tmend) ? `–${fmtTime(s.tmend)}` : ''}
                    </td>
                    <td className="cell-trunc" style={{ fontSize: 12 }}>{s.normalizedTag}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {s.billingType === 'perShift' ? fmt(s.rate) : <span style={{ color: '#718096', fontStyle: 'italic' }}>Monthly</span>}
                    </td>
                    <td><span className={`badge ${statusClass}`}>{statusLabel}</span></td>
                  </tr>
                );
              })}
            </tbody>
            {/* Per-shift revenue for the selected period and filters. Monthly, canceled
                and no-charge shifts contribute nothing and are called out below. */}
            <tfoot>
              <tr style={{ borderTop: '2px solid #cbd5e0' }}>
                <td colSpan={10} style={{ textAlign: 'right', padding: '10px 8px', fontWeight: 700 }}>
                  Per-shift total — {perShiftTotals.count} billable shift{perShiftTotals.count !== 1 ? 's' : ''}
                </td>
                <td style={{ whiteSpace: 'nowrap', padding: '10px 8px', fontWeight: 700, fontSize: 15 }}>
                  {fmt(perShiftTotals.billed)}
                </td>
                <td />
              </tr>
              <tr>
                <td colSpan={10} style={{ textAlign: 'right', padding: '0 8px 10px', fontSize: 12, color: '#718096' }}>
                  Collected
                </td>
                <td style={{ whiteSpace: 'nowrap', padding: '0 8px 10px', fontSize: 12, color: '#276749', fontWeight: 600 }}>
                  {fmt(perShiftTotals.paid)}
                </td>
                <td />
              </tr>
              {perShiftTotals.unpaid > 0 && (
                <tr>
                  <td colSpan={10} style={{ textAlign: 'right', padding: '0 8px 10px', fontSize: 12, color: '#718096' }}>
                    Still outstanding
                  </td>
                  <td style={{ whiteSpace: 'nowrap', padding: '0 8px 10px', fontSize: 12, color: '#c53030', fontWeight: 600 }}>
                    {fmt(perShiftTotals.unpaid)}
                  </td>
                  <td />
                </tr>
              )}
              {(perShiftTotals.monthly > 0 || perShiftTotals.skipped > 0) && (
                <tr>
                  <td colSpan={12} style={{ textAlign: 'right', padding: '0 8px 10px', fontSize: 11, color: '#a0aec0' }}>
                    Not counted:
                    {perShiftTotals.monthly > 0 && ` ${perShiftTotals.monthly} monthly-client shift${perShiftTotals.monthly !== 1 ? 's' : ''}`}
                    {perShiftTotals.monthly > 0 && perShiftTotals.skipped > 0 && ','}
                    {perShiftTotals.skipped > 0 && ` ${perShiftTotals.skipped} canceled / no-charge`}
                  </td>
                </tr>
              )}
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

// Bot-run createdAt arrives as "MM/DD/YYYY,THH:mm:ss-7" — already Pacific, so
// just reshape it rather than re-parsing it as a real timestamp.
function fmtRunCreated(raw) {
  if (!raw) return '—';
  const m = String(raw).match(/^(\d{2})\/(\d{2})\/(\d{4}),T(\d{2}:\d{2}:\d{2})/);
  return m ? `${m[3]}-${m[1]}-${m[2]}  ${m[4]}` : String(raw);
}

// "HH:MM:SS:ms" -> seconds since midnight (fractional), for numeric sort.
function searchTimeSecs(st) {
  if (!st) return null;
  const p = String(st).split(':');
  const secs = parseInt(p[0] || 0, 10) * 3600 + parseInt(p[1] || 0, 10) * 60 + parseInt(p[2] || 0, 10);
  return secs + parseInt(p[3] || 0, 10) / 1000;
}

// Where a run was started: 'remote' = the website (by 'admin' or a client), 'local' =
// the PC's own page. Worked out on the backend (utils/runSource.js).
function RunSourceBadge({ via, by }) {
  if (via === 'remote') {
    return (
      <span className="badge" style={{ background: '#ebf4ff', color: '#2c5282' }} title="Started from the website">
        Remote{by ? ` · ${by === 'admin' ? 'Admin' : by}` : ''}
      </span>
    );
  }
  if (via === 'local') return <span className="badge badge-nocharge" title="Started from the PC's own BQTExpress page">Local</span>;
  return null;
}

function ActiveRunsTab() {
  const [options, setOptions] = useState({ clientIDs: [], userNames: [] });
  const [clientID, setClientID] = useState('');
  const [userName, setUserName] = useState('');
  const [status, setStatus] = useState('all'); // 'active' | 'closed' | 'all'
  const [runs, setRuns] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [expanded, setExpanded] = useState({}); // runId -> bool
  const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc' | null (server order)

  useEffect(() => { api.getActiveRunOptions().then(setOptions).catch(console.error); }, []);
  useEffect(() => { api.getClients().then(setClients).catch(console.error); }, []);

  useEffect(() => {
    setLoading(true);
    api.getActiveRuns({ clientID, userName, status: status === 'all' ? '' : status })
      .then(rows => { setRuns(rows); setExpanded({}); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [clientID, userName, status, reloadKey]);

  // Runs are keyed by login (userName) + property (clientID), not by the billing
  // client's name — look that name up so it's readable without memorizing logins.
  const clientNameByUser = useMemo(() => {
    const map = {};
    clients.forEach(c => {
      (c.properties || []).forEach(p => {
        if (p.user) map[p.user.toLowerCase()] = c.clientName;
      });
    });
    return map;
  }, [clients]);

  const sortedRuns = useMemo(() => {
    if (!sortDir) return runs;
    const withSecs = runs.map(r => ({ r, secs: searchTimeSecs(r.searchTime) }));
    withSecs.sort((a, b) => {
      if (a.secs === null && b.secs === null) return 0;
      if (a.secs === null) return 1;   // missing search times sort last either way
      if (b.secs === null) return -1;
      return sortDir === 'asc' ? a.secs - b.secs : b.secs - a.secs;
    });
    return withSecs.map(x => x.r);
  }, [runs, sortDir]);

  function toggleSort() {
    setSortDir(d => d === 'asc' ? 'desc' : d === 'desc' ? null : 'asc');
  }

  const STATUS_LABELS = { active: 'Active', closed: 'Closed', all: 'All' };

  return (
    <div>
      <div className="card" style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {['active', 'closed', 'all'].map(s => (
            <button
              key={s}
              className={`toggle-btn${status === s ? ' active' : ''}`}
              onClick={() => setStatus(s)}
            >{STATUS_LABELS[s]}</button>
          ))}
        </div>

        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Client ID</label>
          <select value={clientID} onChange={e => setClientID(e.target.value)} style={{ minWidth: 140 }}>
            <option value="">All Clients</option>
            {options.clientIDs.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Username</label>
          <select value={userName} onChange={e => setUserName(e.target.value)} style={{ minWidth: 140 }}>
            <option value="">All Users</option>
            {options.userNames.map(u => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>

        {(clientID || userName) && (
          <button className="btn btn-sm btn-secondary" onClick={() => { setClientID(''); setUserName(''); }}>Clear Filters</button>
        )}

        <button className="btn btn-sm btn-secondary" onClick={() => setReloadKey(k => k + 1)}>Refresh</button>

        <span style={{ color: '#718096', fontSize: 13, marginLeft: 'auto' }}>
          {loading ? 'Loading...' : `${runs.length} run${runs.length !== 1 ? 's' : ''}`}
        </span>
      </div>

      {!loading && runs.length === 0 && (
        <div className="card" style={{ color: '#718096' }}>No runs found.</div>
      )}

      {runs.length > 0 && (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table style={{ minWidth: 1220 }}>
            <thead>
              <tr>
                <th>Status</th>
                <th>Client</th>
                <th>Client ID</th>
                <th>Username</th>
                <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={toggleSort} title="Sort by search time">
                  Search Time {sortDir === 'asc' ? '▲' : sortDir === 'desc' ? '▼' : ''}
                </th>
                <th>Hostname</th>
                <th>Started</th>
                <th>Booking Window</th>
                <th>Criteria</th>
                <th style={{ whiteSpace: 'nowrap' }}>Created (PT)</th>
                <th>Override</th>
                <th>Overrode</th>
                <th>Closed Reason</th>
              </tr>
            </thead>
            <tbody>
              {sortedRuns.map((r, i) => {
                const bg = i % 2 === 0 ? '#ffffff' : '#f7f8fa';
                const isOpen = !!expanded[String(r._id)];
                return (
                <React.Fragment key={String(r._id)}>
                <tr style={{ background: bg }}>
                  <td>
                    <span className={`badge ${r.active ? 'badge-paid' : 'badge-nocharge'}`}>
                      {r.active ? 'Active' : 'Closed'}
                    </span>
                  </td>
                  <td>{clientNameByUser[String(r.userName).toLowerCase()] || '—'}</td>
                  <td>{r.clientID}</td>
                  <td>{r.userName}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.searchTime || '—'}</td>
                  <td style={{ fontSize: 12 }}>{r.hostname || '—'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}><RunSourceBadge via={r.startedVia} by={r.startedBy} /></td>
                  <td style={{ whiteSpace: 'nowrap', fontSize: 12, color: '#718096' }}>
                    {r.start && r.end ? `${r.start} – ${r.end}` : '—'}
                  </td>
                  <td style={{ fontSize: 12, color: '#718096' }}>
                    {(r.shiftCriteria || []).length === 0
                      ? <span style={{ color: '#a0aec0' }}>0 targets</span>
                      : (
                        <button
                          className="btn btn-secondary btn-sm"
                          style={{ whiteSpace: 'nowrap' }}
                          onClick={() => setExpanded(e => ({ ...e, [String(r._id)]: !e[String(r._id)] }))}
                        >
                          {isOpen ? '▾' : '▸'} {r.shiftCriteria.length} target{r.shiftCriteria.length !== 1 ? 's' : ''}
                        </button>
                      )}
                  </td>
                  <td style={{ whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: 12 }}>{fmtRunCreated(r.createdAt)}</td>
                  <td>
                    {r.startedAsOverride
                      ? <span className="badge badge-warn">Override</span>
                      : <span style={{ color: '#a0aec0' }}>—</span>}
                  </td>
                  <td style={{ fontSize: 12, color: '#718096' }}>
                    {r.overrode
                      ? `${r.overrode.host || '?'}${r.overrode.searchTime ? ` @ ${r.overrode.searchTime}` : ''}`
                      : '—'}
                  </td>
                  <td style={{ fontSize: 12, color: '#718096' }}>{r.closedReason || '—'}</td>
                </tr>
                {isOpen && (
                  <tr style={{ background: bg }}>
                    <td colSpan={13} style={{ padding: '4px 12px 12px 40px' }}>
                      <CriteriaDetail criteria={r.shiftCriteria} />
                    </td>
                  </tr>
                )}
                </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Shift Requests ────────────────────────────────────────────────────────────
// What clients have asked us to book, grouped by client then property. Running is
// set by hand here, or automatically when an active bot run targets the request.
// onChange lets the dashboard refresh its header bell after a mark/delete here.
function RequestsTab({ onChange }) {
  const [status, setStatus] = useState('all'); // 'requested' | 'running' | 'all'
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    api.getShiftRequests(status === 'all' ? '' : status)
      .then(rows => { setRequests(rows); setError(''); onChange?.(); })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [status, reloadKey]);

  async function toggleRunning(r) {
    try {
      await api.setShiftRequestRunning(r._id, !r.running);
      setReloadKey(k => k + 1);
    } catch (err) { setError(err.message); }
  }

  async function remove(r) {
    if (!window.confirm(`Delete ${r.clientName}'s ${fmtShiftType(r)} request on ${fmtRequestDate(r.shdate)}?`)) return;
    try {
      await api.deleteShiftRequest(r._id);
      setReloadKey(k => k + 1);
    } catch (err) { setError(err.message); }
  }

  // clientName -> clientid -> rows (server returns entry order: top = client's priority)
  const grouped = useMemo(() => {
    const map = {};
    requests.forEach(r => {
      map[r.clientName] = map[r.clientName] || {};
      (map[r.clientName][r.clientid] = map[r.clientName][r.clientid] || []).push(r);
    });
    return Object.keys(map).sort((a, b) => a.localeCompare(b)).map(name => [name, map[name]]);
  }, [requests]);

  const today = todayPacific();

  return (
    <div>
      {error && <div className="alert alert-error">{error}</div>}
      <div className="card" style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {[['requested', 'Requested'], ['running', 'Running'], ['all', 'All']].map(([s, label]) => (
            <button key={s} className={`toggle-btn${status === s ? ' active' : ''}`} onClick={() => setStatus(s)}>{label}</button>
          ))}
        </div>
        <button className="btn btn-sm btn-secondary" onClick={() => setReloadKey(k => k + 1)}>Refresh</button>
        <span style={{ color: '#718096', fontSize: 13, marginLeft: 'auto' }}>
          {loading ? 'Loading...' : `${requests.length} request${requests.length !== 1 ? 's' : ''}`}
        </span>
      </div>

      {!loading && requests.length === 0 && (
        <div className="card" style={{ color: '#718096' }}>No requests.</div>
      )}

      {grouped.map(([clientName, byProperty]) => (
        <div key={clientName} className="card" style={{ marginBottom: 10 }}>
          <div className="card-title">{clientName}</div>
          {Object.keys(byProperty).sort((a, b) => a.localeCompare(b)).map(property => (
            <div key={property} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#4a5568', margin: '6px 0' }}>{property}</div>
              <div style={{ overflowX: 'auto' }}>
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Shift</th>
                      <th>Time</th>
                      <th>Status</th>
                      <th style={{ whiteSpace: 'nowrap' }}>Requested</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {byProperty[property].map(r => (
                      <tr key={r._id} style={r.shdate < today ? { color: '#a0aec0' } : undefined}>
                        <td style={{ whiteSpace: 'nowrap' }}>{fmtRequestDate(r.shdate)}</td>
                        <td>{fmtShiftType(r)}</td>
                        <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{fmtTimeRange(r)}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <span className={`badge ${r.running ? 'badge-paid' : 'badge-warn'}`}>
                            {r.running ? 'Running' : 'Requested'}
                          </span>
                          {r.running && r.runningSource === 'auto' && (
                            <span style={{ fontSize: 11, color: '#718096', marginLeft: 6 }} title="Matched an active run">auto</span>
                          )}
                        </td>
                        <td style={{ whiteSpace: 'nowrap', fontSize: 12, color: '#718096' }}>
                          {new Date(r.createdAt).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                        </td>
                        <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                          <button
                            className={`btn btn-sm ${r.running ? 'btn-secondary' : 'btn-primary'}`}
                            onClick={() => toggleRunning(r)}
                          >
                            {r.running ? 'Unmark' : 'Mark Running'}
                          </button>{' '}
                          <button className="btn btn-danger btn-sm" onClick={() => remove(r)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────────────
// ── Fleet (remote control of the booking bots) ────────────────────────────────
// Lists each running bot slot (machine:port) and runs the full Start form against a
// chosen PC + port. Credentials come from a saved BotProfile; the Chrome profile is
// one the admin saved for that machine. The slot's exe claims the queued RunCommand
// and launches it (server.js poller). Mirrors the local form's venue + shift options.
const FLEET_VENUES = [
  { value: 'Aria', label: 'Aria' },
  { value: 'Bellagio', label: 'Bellagio' },
  { value: 'Caesars', label: 'Caesars' },
  { value: 'tcolvbanquets', label: 'Cosmopolitan' },
  { value: 'Lvvenetianexpocc', label: 'Expo' },
  { value: 'FontaineBleauLV', label: 'FontaineBleau' },
  { value: 'mgmmandalay', label: 'MandalayBay' },
  { value: 'Mgmgrand', label: 'MGM' },
  { value: 'Lvsands', label: 'Venetian' },
  { value: 'Wynnlv', label: 'Wynn' }
];

const FLEET_SHIFT_OPTIONS = {
  Aria: [
    { value: 'din', label: 'Dinner/Reception (ARIA)' },
    { value: 'lun', label: 'Lunch (ARIA)' },
    { value: 'bft', label: 'Breakfast (ARIA)' },
    { value: 'cb', label: 'Coffee Break (ARIA)' },
    { value: '', label: 'Any Shift (ARIA)' }
  ],
  Bellagio: [
    { value: 'din', label: 'Dinner/Reception (Bellagio)' },
    { value: 'lun', label: 'Lunch (Bellagio)' },
    { value: 'bft', label: 'Breakfast (Bellagio)' },
    { value: 'cb', label: 'Coffee Break (Bellagio)' },
    { value: '', label: 'Any Shift (Bellagio)' }
  ],
  Caesars: [
    { value: 'din', label: 'Dinner (Caesars)' },
    { value: 'lun', label: 'Lunch (Caesars)' },
    { value: 'BF', label: 'Breakfast (Caesars)' },
    { value: 'CB', label: 'Coffee Break (Caesars)' },
    { value: '', label: 'Any Shift (Caesars)' }
  ],
  FontaineBleauLV: [
    { value: 'rec', label: 'Reception (FontaineBleauLV)' },
    { value: 'din', label: 'Dinner (FontaineBleauLV)' },
    { value: 'lun', label: 'Lunch (FontaineBleauLV)' },
    { value: 'Breakfast', label: 'Breakfast (FontaineBleauLV)' },
    { value: 'Coff', label: 'Coffee Break (FontaineBleauLV)' },
    { value: '', label: 'Any Shift (FontaineBleauLV)' }
  ],
  mgmmandalay: [
    { value: 'rec', label: 'Reception (mgmmandalay)' },
    { value: 'din', label: 'Dinner (mgmmandalay)' },
    { value: 'lun', label: 'Lunch (mgmmandalay)' },
    { value: 'Bft', label: 'Breakfast (mgmmandalay)' },
    { value: 'CB', label: 'Coffee Break (mgmmandalay)' },
    { value: 'CC', label: 'Cash Concession (mgmmandalay)' },
    { value: '', label: 'Any Shift (mgmmandalay)' }
  ],
  Mgmgrand: [
    { value: 'rec', label: 'Reception (Mgmgrand)' },
    { value: 'din', label: 'Dinner (Mgmgrand)' },
    { value: 'lun', label: 'Lunch (Mgmgrand)' },
    { value: 'Breakfast', label: 'Breakfast (Mgmgrand)' },
    { value: 'CB', label: 'Coffee Break (Mgmgrand)' },
    { value: '', label: 'Any Shift (Mgmgrand)' }
  ],
  Lvsands: [
    { value: 'din', label: 'Dinner (Lvsands) Anyshift with Dinner BLD/LD' },
    { value: 'lun', label: 'Lunch (Lvsands) Anyshift with Lunch BLD/LD/BL' },
    { value: 'Breakfast', label: 'Breakfast (Lvsands) Anyshift with Breakfast BLD/BL' },
    { value: 'CB', label: 'Coffee Break (Lvsands)' },
    { value: '1:Breakfast', label: 'Breakfast Only (Lvsands)' },
    { value: '4:Lunch', label: 'Lunch Only (Lvsands)' },
    { value: '6:Dinner', label: 'Dinner Only (Lvsands)' },
    { value: '3:B-L-D', label: 'Breakfast/Lunch/Dinner (Lvsands)' },
    { value: '5:L-D', label: 'Lunch/Dinner (Lvsands)' },
    { value: '2:B-L', label: 'Breakfast/Lunch (Lvsands)' },
    { value: '', label: 'Any Shift (Lvsands)' }
  ],
  Lvvenetianexpocc: [
    { value: 'din', label: 'Dinner (Lvvenetianexpocc)' },
    { value: 'lun', label: 'Lunch (Lvvenetianexpocc)' },
    { value: 'BK', label: 'Breakfast (Lvvenetianexpocc)' },
    { value: 'CB', label: 'Coffee Break (Lvvenetianexpocc)' },
    { value: '', label: 'Any Shift (Lvvenetianexpocc)' }
  ],
  tcolvbanquets: [
    { value: 'rec', label: 'Reception (tcolvbanquets)' },
    { value: 'din', label: 'Dinner (tcolvbanquets)' },
    { value: 'lun', label: 'Lunch (tcolvbanquets)' },
    { value: 'Br', label: 'Breakfast (tcolvbanquets)' },
    { value: 'CB', label: 'Coffee Break (tcolvbanquets)' },
    { value: '', label: 'Any Shift (tcolvbanquets)' }
  ],
  Wynnlv: [
    { value: 'rec', label: 'Reception (Wynnlv)' },
    { value: 'din', label: 'Dinner (Wynnlv)' },
    { value: 'lun', label: 'Lunch (Wynnlv)' },
    { value: 'BRKT', label: 'Breakfast (Wynnlv)' },
    { value: 'CB', label: 'Coffee Break (Wynnlv)' },
    { value: '', label: 'Any Shift (Wynnlv)' }
  ]
};

const FLEET_BOOKING_DELAYS = ['1500', '1400', '1350', '1300', '1250', '1200', '1100', '1000', '900', '800', '700', '600'];
// Booking Delay follows the search time's ss:ms (mirrors server.js): 59:550 and above -> 1400,
// anything earlier -> 1250. A manual pick holds until the time is changed again.
const fleetBookingDelayFor = (seconds, milliseconds) => {
  const ss = parseInt(seconds, 10) || 0;
  const ms = parseInt(milliseconds, 10) || 0;
  return ss > 59 || (ss === 59 && ms >= 550) ? '1400' : '1250';
};
const FLEET_SKIP_LATE = [
  { value: '0', label: 'No (Default)' },
  { value: '500', label: 'Yes after 0.5s' },
  { value: '1000', label: 'Yes after 1s' },
  { value: '1500', label: 'Yes after 1.5s' },
  { value: '2000', label: 'Yes after 2s' },
  { value: '2500', label: 'Yes after 2.5s' },
  { value: '3000', label: 'Yes after 3s' },
  { value: '4000', label: 'Yes after 4s' },
  { value: '5000', label: 'Yes after 5s' },
  { value: '6000', label: 'Yes after 6s' },
  { value: '7000', label: 'Yes after 7s' },
  { value: '8000', label: 'Yes after 8s' }
];

// A Chrome folder can't run on two ports of one machine within 13 minutes (the bot's
// profile lock; backend utils/fleetChecks.js has the same rule and wording).
const FLEET_CHROME_OVERLAP_MIN = 13;
const fleetStMin = s => { const [h, m] = String(s || '').split(':').map(Number); return (h || 0) * 60 + (m || 0); };
const fleetHHMM = s => String(s || '').split(':').slice(0, 2).map(x => String(x).padStart(2, '0')).join(':');
// The other-port use of this folder (p.inUse from GET /slots) that clashes with hours:minutes, or undefined.
function fleetChromeClash(p, hours, minutes) {
  const mine = (parseInt(hours, 10) || 0) * 60 + (parseInt(minutes, 10) || 0);
  return ((p && p.inUse) || []).find(u => Math.abs(mine - fleetStMin(u.searchTime)) <= FLEET_CHROME_OVERLAP_MIN);
}
const fleetChromeClashMsg = (dir, use) =>
  `Cannot proceed — Chrome profile "${dir}" is already being used on port ${use.port} of this machine at ${fleetHHMM(use.searchTime)}, within ${FLEET_CHROME_OVERLAP_MIN} minutes of this run's search time. Select another Chrome profile to start the run.`;

function blankFleetCriteria() {
  return { ShTagSetCdName: '', custom: '', shdate: '', tmstartOperator: 'equalTo', tmstart: '', tmend: '', SiteName: '' };
}

// Required fields a target shift row is missing, as labels (empty = complete).
function critMissing(c) {
  const out = [];
  if (!c.shdate) out.push('a date');
  if (c.ShTagSetCdName === 'other' && !String(c.custom || '').trim()) out.push('a custom tag');
  return out.length ? out : null;
}

// Each target shift box gets its own tint so its fields read as one group.
const FLEET_CRIT_COLORS = [
  { bg: '#ebf8ff', border: '#63b3ed', text: '#2b6cb0' },
  { bg: '#f0fff4', border: '#68d391', text: '#276749' },
  { bg: '#faf5ff', border: '#b794f4', text: '#6b46c1' },
  { bg: '#fffaf0', border: '#f6ad55', text: '#c05621' },
  { bg: '#fff5f7', border: '#f687b3', text: '#b83280' },
  { bg: '#e6fffa', border: '#4fd1c5', text: '#2c7a7b' }
];

// Red asterisk after a required field's label.
const ReqMark = () => <span style={{ color: '#e53e3e' }} aria-hidden="true"> *</span>;

// 12-hour labels (value stays 24h, zero-padded) to match the local form's dropdown.
const FLEET_HOURS = Array.from({ length: 24 }, (_, i) => {
  const h12 = i % 12 === 0 ? 12 : i % 12;
  return { value: String(i).padStart(2, '0'), label: `${h12} ${i < 12 ? 'AM' : 'PM'}` };
});

// Date-range + auto-adjust rules, mirrored from server.js (getMaxDateRangeDays /
// applyTargetShiftDates): ARIA caps at 7 days, everything else at 31. When target
// shift dates are set, Start = earliest and End = earliest+5 (extended to cover the
// latest target), clamped to the per-venue window.
function fleetMaxRange(venue) {
  return String(venue || '').toUpperCase() === 'ARIA' ? 7 : 31;
}
function fleetAddDays(dateStr, n) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + n);
  const p = x => String(x).padStart(2, '0');
  return `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())}`;
}
function fleetToday() {
  const p = x => String(x).padStart(2, '0');
  const d = new Date();
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
function fleetAutoDates(criteria, venue) {
  const dates = criteria.map(c => c.shdate).filter(v => /^\d{4}-\d{2}-\d{2}$/.test(v)).sort();
  if (!dates.length) return null;
  const earliest = dates[0], latest = dates[dates.length - 1];
  let end = fleetAddDays(earliest, 5);
  if (latest > end) end = latest;
  const maxEnd = fleetAddDays(earliest, fleetMaxRange(venue) - 1);
  if (end > maxEnd) end = maxEnd;
  return { startDate: earliest, endDate: end };
}

// Accept a venue as its code ("Lvsands") or friendly label ("Venetian").
function fleetVenueValue(v) {
  const s = String(v || '').trim();
  const byVal = FLEET_VENUES.find(x => x.value.toLowerCase() === s.toLowerCase());
  if (byVal) return byVal.value;
  const byLabel = FLEET_VENUES.find(x => x.label.toLowerCase() === s.toLowerCase());
  return byLabel ? byLabel.value : s;
}

// Parse a pasted credential list — JSON array (incl. the local profiles.json shape) or
// CSV lines: profileName, venue, username, password, pin, email. Returns normalized rows.
function parseFleetImport(text) {
  const t = String(text || '').trim();
  if (!t) return [];
  const norm = r => ({
    profileName: r.profileName, clientID: fleetVenueValue(r.clientID), userName: r.userName,
    passWord: r.passWord, pin: r.pin, emailTo: r.emailTo
  });
  if (t[0] === '[' || t[0] === '{') {
    const j = JSON.parse(t);
    return (Array.isArray(j) ? j : [j]).map(norm);
  }
  const out = [];
  t.split(/\r?\n/).map(l => l.trim()).filter(Boolean).forEach((line, idx) => {
    if (idx === 0 && /user\s*name|client\s*id|profile\s*name/i.test(line)) return; // header row
    const c = line.split(',').map(x => x.trim());
    out.push(norm({ profileName: c[0], clientID: c[1], userName: c[2], passWord: c[3], pin: c[4], emailTo: c[5] }));
  });
  return out;
}

// Default (admin) data adapter. The client portal passes a portal-scoped adapter so the
// same component serves both; admin-only UI (Owner column, assign, profile-owner) is
// gated by isAdmin.
const ADMIN_FLEET_API = {
  getSlots: api.getSlots, getBotProfiles: api.getBotProfiles, createBotProfile: api.createBotProfile,
  updateBotProfile: api.updateBotProfile, deleteBotProfile: api.deleteBotProfile, revealBotProfile: api.revealBotProfile,
  bulkBotProfiles: api.bulkBotProfiles, createChromeProfile: api.createChromeProfile, deleteChromeProfile: api.deleteChromeProfile,
  startRun: api.startFleetRun, stopRun: api.stopFleetRun, getRuns: api.getFleetRuns, getLiveLog: api.getFleetLiveLog,
  updateSlot: api.updateFleetSlot, // admin only — the portal adapter has none, which hides the Update UI
  forgetSlot: api.forgetFleetSlot // admin only — drop a closed slot from the list
};

// Compare dotted versions ("7.0.10" > "7.0.9"). Missing parts count as 0.
function compareVersions(a, b) {
  const pa = String(a || '').split('.').map(n => parseInt(n, 10) || 0);
  const pb = String(b || '').split('.').map(n => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    if ((pa[i] || 0) !== (pb[i] || 0)) return (pa[i] || 0) > (pb[i] || 0) ? 1 : -1;
  }
  return 0;
}

// The bot's log lines are HTML (colored <span>s, trailing <br>) and the EXE's local page
// renders them as such. Render the same way here, but only keep simple formatting tags
// and their style/color, so nothing else in a log line can run or load in the page.
const LOG_TAGS = new Set(['SPAN', 'B', 'STRONG', 'I', 'EM', 'U', 'FONT', 'SMALL', 'BR']);
function sanitizeLogHtml(html) {
  const doc = new DOMParser().parseFromString(`<div>${String(html).replace(/(<br\s*\/?>\s*)+$/i, '')}</div>`, 'text/html');
  const clean = node => {
    Array.from(node.childNodes).forEach(child => {
      if (child.nodeType === 3) return; // text
      if (child.nodeType !== 1 || !LOG_TAGS.has(child.tagName)) {
        child.replaceWith(doc.createTextNode(child.textContent || ''));
        return;
      }
      Array.from(child.attributes).forEach(a => {
        const keep = (a.name === 'style' || a.name === 'color') && !/url\(|expression|javascript:/i.test(a.value);
        if (!keep) child.removeAttribute(a.name);
      });
      clean(child);
    });
  };
  const root = doc.body.firstChild;
  clean(root);
  return root.innerHTML;
}

// Delayed log of one slot's current/last run. The bot uploads new lines every ~5s
// (pausing ±10s around the search time); this polls every 10s, only while open and
// the browser tab is visible, and only receives lines it doesn't have yet.
function FleetLogPanel({ A, instanceId, onClose }) {
  const [lines, setLines] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [waiting, setWaiting] = useState(false); // new run hasn't uploaded yet
  const [err, setErr] = useState('');
  const cursor = useRef({ runId: '', seq: 0 });

  useEffect(() => {
    let alive = true;
    let inFlight = false;
    cursor.current = { runId: '', seq: 0 };
    setLines([]); setLoaded(false); setUpdatedAt(null);
    async function poll() {
      if (inFlight || document.visibilityState !== 'visible') return;
      inFlight = true;
      try {
        const r = await A.getLiveLog({ instanceId, runId: cursor.current.runId, after: cursor.current.seq });
        if (!alive) return;
        cursor.current = { runId: r.runId || '', seq: r.seq || 0 };
        setLines(prev => (r.reset ? r.lines : r.lines.length ? prev.concat(r.lines).slice(-500) : prev));
        setUpdatedAt(r.updatedAt);
        setWaiting(!!r.waiting);
        setLoaded(true);
        setErr('');
      } catch (e) {
        if (alive) setErr(e.message);
      } finally { inFlight = false; }
    }
    poll();
    const id = setInterval(poll, 10000);
    const onVis = () => { if (document.visibilityState === 'visible') poll(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { alive = false; clearInterval(id); document.removeEventListener('visibilitychange', onVis); };
  }, [A, instanceId]);

  return (
    <div style={{ marginTop: 12, border: '1px solid #e2e8f0', borderRadius: 6, padding: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <strong>Run Log</strong>
        {updatedAt && <span style={{ fontSize: 12, color: '#718096' }}>last update {new Date(updatedAt).toLocaleTimeString()}</span>}
        <button className="btn btn-sm btn-secondary" onClick={onClose} style={{ marginLeft: 'auto' }}>Close</button>
      </div>
      <div style={{ fontSize: 12, color: '#b7791f', margin: '6px 0' }}>
        Not live — delayed by up to 30 seconds (it also pauses briefly around the search time and catches up right after). Newest at the top.
      </div>
      {err && <div style={{ fontSize: 12, color: '#c53030' }}>{err}</div>}
      <div style={{ maxHeight: 360, overflowY: 'auto', background: '#f7fafc', color: '#1a202c', border: '1px solid #e2e8f0', fontFamily: 'monospace', fontSize: 12, padding: 8, borderRadius: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {!loaded ? 'Loading…'
          : waiting ? 'This run has started — waiting for its first log upload…'
          : lines.length === 0 ? 'No log yet for this slot.'
          : lines.slice().reverse().map(l => <div key={l.s} dangerouslySetInnerHTML={{ __html: sanitizeLogHtml(l.m) }} />)}
      </div>
    </div>
  );
}

export function FleetTab({ A = ADMIN_FLEET_API, isAdmin = true } = {}) {
  const [slots, setSlots] = useState([]);
  const [selected, setSelected] = useState('');          // instanceId
  const [profiles, setProfiles] = useState([]);
  const [profilesLoaded, setProfilesLoaded] = useState(false); // first load done (a client may have none)
  const [unmatchedLogin, setUnmatchedLogin] = useState(null);  // restored run's login with no saved website profile
  const [profileId, setProfileId] = useState('');
  const [userDataDir, setUserDataDir] = useState('');
  const [form, setForm] = useState({
    // Default to today / today+5 when there's no target shift (mirrors server.js);
    // adding target shifts re-derives these via fleetAutoDates.
    startDate: fleetToday(), endDate: fleetAddDays(fleetToday(), 5), hours: '00', minutes: '59', seconds: '59', milliseconds: '800',
    InBetweenShift: fleetBookingDelayFor('59', '800'), maxSearchAttempts: '2', multipleShift: 'No', maxShiftsToBook: '1', luxor: 'No',
    skipFirstCriteriaOnReload: 'No', skipFirstIfLate: '0', emailEnabled: true, emailTo: '', startTomorrow: false
  });
  const [criteria, setCriteria] = useState([]); // none by default — add a target shift to book; leave empty to just test timings
  const [runs, setRuns] = useState([]);
  const [clients, setClients] = useState([]);
  const [editingOwner, setEditingOwner] = useState(null); // instanceId whose owner is being edited
  const [ownerDraft, setOwnerDraft] = useState('admin'); // 'admin' | a client _id | 'inherit' (port only)
  const [ownerScope, setOwnerScope] = useState('port'); // 'port' (this port only) | 'machine' (all its ports)
  const [ownerFilter, setOwnerFilter] = useState('admin'); // 'admin' | 'all' | a client _id (admin view only)
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'online' | 'offline' (closed EXEs, from KnownSlots)
  const [showRuns, setShowRuns] = useState(false); // Recent Commands collapsed by default
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  // Slots whose start command is queued but not yet showing as running:
  // { [instanceId]: { commandId, since } }. Cleared once the slot reports running,
  // the command fails, or it times out.
  const [starting, setStarting] = useState({});
  // Same idea for Stop: { [instanceId]: { commandId, since } } until the slot reports idle.
  const [stopping, setStopping] = useState({});
  // Remote updates in flight: { [instanceId]: { commandId, since, from } } until the slot
  // reports a version other than `from`, the command fails, or it times out.
  const [updating, setUpdating] = useState({});
  const [showLog, setShowLog] = useState(false); // delayed Run Log panel for the selected slot
  const [showCritErrors, setShowCritErrors] = useState(false); // red-outline missing target-shift fields after a blocked Start
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [profForm, setProfForm] = useState({ profileName: '', clientID: 'Aria', userName: '', passWord: '', pin: '', emailTo: '', ownerClientId: '' });
  const [editingId, setEditingId] = useState(null); // profile being edited, or null when creating
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState('');
  const [importMsg, setImportMsg] = useState('');
  const [newChrome, setNewChrome] = useState('');
  const [showChromeForm, setShowChromeForm] = useState(false);
  const [revealed, setRevealed] = useState(null); // decrypted { userName, passWord, pin } for the selected profile
  const [pastModal, setPastModal] = useState(null); // { resolve } while the "search time already passed" choice is open

  const loadSlots = useCallback(() => { A.getSlots().then(setSlots).catch(console.error); }, []);
  const loadProfiles = useCallback(() => {
    A.getBotProfiles().then(p => { setProfiles(p); setProfilesLoaded(true); }).catch(console.error);
  }, []);
  const loadRuns = useCallback(() => { A.getRuns().then(setRuns).catch(console.error); }, []);

  useEffect(() => { loadSlots(); loadProfiles(); loadRuns(); if (isAdmin) api.getClients().then(setClients).catch(() => {}); }, [loadSlots, loadProfiles, loadRuns, isAdmin]);
  // Poll faster while a run is starting/stopping so the label flips promptly.
  const anyStarting = Object.keys(starting).length > 0;
  const anyStopping = Object.keys(stopping).length > 0;
  const anyUpdating = Object.keys(updating).length > 0;
  useEffect(() => {
    const id = setInterval(() => { loadSlots(); loadRuns(); }, (anyStarting || anyStopping) ? 2000 : anyUpdating ? 4000 : 8000);
    return () => clearInterval(id);
  }, [loadSlots, loadRuns, anyStarting, anyStopping, anyUpdating]);

  // Resolve pending updates: slot back online on a new version → done; command failed →
  // show why; >5 min → give up (download + close + reopen + first heartbeat takes ~1 min).
  useEffect(() => {
    if (!anyUpdating) return;
    const next = { ...updating };
    let changed = false;
    const done = [];
    for (const [instanceId, p] of Object.entries(updating)) {
      const slot = slots.find(s => s.instanceId === instanceId);
      const cmd = p.commandId && runs.find(r => r._id === p.commandId);
      if (slot && slot.online && slot.version && slot.version !== p.from) {
        delete next[instanceId]; changed = true;
        done.push(`${slot.label || slot.hostname} :${slot.port} → ${slot.version}`);
      } else if (cmd && cmd.status === 'failed') {
        delete next[instanceId]; changed = true;
        setMsg(`Update failed on port ${slot ? slot.port : instanceId}: ${(cmd.result && cmd.result.message) || 'unknown error'}`);
      } else if (Date.now() - p.since > 5 * 60 * 1000) {
        delete next[instanceId]; changed = true;
        setMsg(`Port ${slot ? slot.port : instanceId} did not come back on a new version within 5 minutes — check that PC (the app may not have reopened).`);
      }
    }
    if (changed) setUpdating(next);
    if (done.length) setMsg(`Updated: ${done.join(', ')}`);
  }, [slots, runs, updating, anyUpdating]);

  // Resolve pending stops: no longer running → done; command failed → show why; >60s → give up.
  useEffect(() => {
    if (!anyStopping) return;
    const next = { ...stopping };
    let changed = false;
    for (const [instanceId, p] of Object.entries(stopping)) {
      const slot = slots.find(s => s.instanceId === instanceId);
      const cmd = p.commandId && runs.find(r => r._id === p.commandId);
      if (slot && slot.status !== 'running') {
        delete next[instanceId]; changed = true;
      } else if (cmd && cmd.status === 'failed') {
        delete next[instanceId]; changed = true;
        setMsg((cmd.result && cmd.result.message) || 'Stop failed.');
      } else if (Date.now() - p.since > 60 * 1000) {
        delete next[instanceId]; changed = true;
        setMsg('The slot did not report stopped within 60 seconds — check that the bot PC is online.');
      }
    }
    if (changed) setStopping(next);
  }, [slots, runs, stopping, anyStopping]);

  // Resolve pending starts: running → done; command failed → show why; >90s → give up.
  useEffect(() => {
    if (!anyStarting) return;
    const next = { ...starting };
    let changed = false;
    for (const [instanceId, p] of Object.entries(starting)) {
      const slot = slots.find(s => s.instanceId === instanceId);
      const cmd = runs.find(r => r._id === p.commandId);
      if (slot && slot.status === 'running') {
        delete next[instanceId]; changed = true;
      } else if (cmd && cmd.status === 'failed') {
        delete next[instanceId]; changed = true;
        setMsg((cmd.result && (cmd.result.message || (cmd.result.conflict && 'Account already running on another machine'))) || 'Run failed to start.');
      } else if (Date.now() - p.since > 90 * 1000) {
        delete next[instanceId]; changed = true;
        setMsg('The slot did not report running within 90 seconds — check that the bot PC is online.');
      }
    }
    if (changed) setStarting(next);
  }, [slots, runs, starting, anyStarting]);

  // Show the selected profile's (decrypted) username/password, read-only, so the
  // operator can confirm which login a run will use.
  useEffect(() => {
    if (!profileId) { setRevealed(null); return; }
    A.revealBotProfile(profileId).then(r => {
      setRevealed(r);
      // The notification email comes from the profile — prefill it on selection, unless
      // this selection came from restoring a live run (keep the email that run used).
      const restored = restoredEmailRef.current;
      restoredEmailRef.current = null;
      setForm(f => ({ ...f, emailTo: restored != null ? restored : (r.emailTo || '') }));
    }).catch(() => setRevealed(null));
  }, [profileId]);

  const selectedSlot = useMemo(() => slots.find(s => s.instanceId === selected) || null, [slots, selected]);

  // Admin view: filter the slot table by owner. Client options are only the clients
  // that currently own at least one machine.
  const slotOwnerKey = s => (s.ownerType === 'client' && s.portalClientId ? String(s.portalClientId) : 'admin');
  const ownerOptions = useMemo(() => {
    const byId = {};
    slots.forEach(s => { if (slotOwnerKey(s) !== 'admin') byId[slotOwnerKey(s)] = s.ownerName; });
    return Object.entries(byId).sort((a, b) => String(a[1]).localeCompare(String(b[1])));
  }, [slots]);
  const visibleSlots = useMemo(
    () => slots
      .filter(s => !isAdmin || ownerFilter === 'all' || slotOwnerKey(s) === ownerFilter)
      .filter(s => statusFilter === 'all' || (statusFilter === 'online') === !!s.online),
    [slots, ownerFilter, statusFilter, isAdmin]
  );
  // If the filtered client no longer owns any machine, fall back to Admin.
  useEffect(() => {
    if (slots.length && ownerFilter !== 'admin' && ownerFilter !== 'all' && !ownerOptions.some(([id]) => id === ownerFilter)) setOwnerFilter('admin');
  }, [slots, ownerFilter, ownerOptions]);
  const selectedProfile = useMemo(() => profiles.find(p => p._id === profileId) || null, [profiles, profileId]);
  const venue = selectedProfile ? selectedProfile.clientID : '';
  const shiftOpts = FLEET_SHIFT_OPTIONS[venue] || [];
  const enableMulti = venue === 'Lvsands' || venue === 'Lvvenetianexpocc';
  const enableLuxor = venue === 'mgmmandalay';

  // Two PCs can share a hostname (default Windows names). Slots are still distinct —
  // identity is the random machineId, not the name — but show a short machineId tail on
  // rows whose label is used by more than one machine, so they're tellable apart.
  const dupLabels = useMemo(() => {
    const byLabel = {};
    slots.forEach(s => {
      const key = s.label || s.hostname || '';
      (byLabel[key] = byLabel[key] || new Set()).add(s.machineId);
    });
    return new Set(Object.keys(byLabel).filter(k => byLabel[k].size > 1));
  }, [slots]);

  // While the selected slot is running, the whole run form is disabled — only Stop stays live.
  const uiDisabled = !!selectedSlot && selectedSlot.status === 'running';

  // Chrome folders another port on this machine is running with within 13 minutes of the
  // form's search time are left out of the dropdown. A pick made before the time was
  // changed stays shown (fallback option) and doStart blocks it with a message.
  const allChrome = (selectedSlot && selectedSlot.chromeProfiles) || [];
  const availableChrome = uiDisabled ? allChrome : allChrome.filter(p => !fleetChromeClash(p, form.hours, form.minutes));
  const busyChromeCount = allChrome.length - availableChrome.length;
  // An idle slot with no folder picked defaults to one no other port is using at all.
  // Once per slot (autoChromeRef), so clearing the pick on purpose doesn't refill it.
  // A folder in use elsewhere can still be picked by hand if its time doesn't clash.
  const autoChromeRef = useRef(new Set());
  useEffect(() => {
    if (!selected || uiDisabled || userDataDir || autoChromeRef.current.has(selected)) return;
    const free = allChrome.find(p => !(p.inUse && p.inUse.length));
    if (!free) return;
    autoChromeRef.current.add(selected);
    setUserDataDir(free.userDataDir);
  }, [selected, uiDisabled, userDataDir, allChrome]);
  // If the search time changes (or another port starts) so the picked folder now clashes,
  // switch to an unused folder, else any non-clashing one. With none left the pick stays
  // and doStart blocks it.
  const [chromeSwitchNote, setChromeSwitchNote] = useState('');
  useEffect(() => {
    if (uiDisabled || !userDataDir) return;
    const use = fleetChromeClash(allChrome.find(p => p.userDataDir === userDataDir), form.hours, form.minutes);
    if (!use) return;
    const next = allChrome.find(p => !(p.inUse && p.inUse.length))
      || allChrome.find(p => !fleetChromeClash(p, form.hours, form.minutes));
    if (!next) return;
    setChromeSwitchNote(`Switched from ${userDataDir} — it's in use on port ${use.port} at ${fleetHHMM(use.searchTime)}.`);
    setUserDataDir(next.userDataDir);
  }, [uiDisabled, userDataDir, allChrome, form.hours, form.minutes]);
  useEffect(() => { setChromeSwitchNote(''); }, [selected]);

  // The start command behind the selected slot's live run, if it was started from the
  // website. Start commands stay 'started' after the run ends, so it must also match the
  // account the slot reports and the run's start time (both stamped by the same PC clock).
  const liveCommand = useMemo(() => {
    const cur = selectedSlot && selectedSlot.status === 'running' && selectedSlot.currentRun;
    if (!cur) return null;
    const curStart = cur.startedAt ? new Date(cur.startedAt).getTime() : NaN;
    return runs.find(r => r.instanceId === selectedSlot.instanceId && r.action === 'start' && r.status === 'started'
      && r.payload && r.payload.clientID === cur.clientID && r.payload.userName === cur.userName
      && r.startedAt && !(Math.abs(new Date(r.startedAt).getTime() - curStart) > 2 * 60 * 1000)) || null;
  }, [selectedSlot, runs]);

  // What the live run used: the website's start command when there is one (it carries the
  // credential profile), else the settings the bot itself publishes on its slot
  // (`runConfig`, password/PIN stripped) — which covers runs started from the local form.
  const liveSource = useMemo(() => {
    if (liveCommand) return { key: liveCommand._id, payload: liveCommand.payload, botProfileId: liveCommand.botProfileId };
    const rc = selectedSlot && selectedSlot.status === 'running' && selectedSlot.runConfig;
    if (rc) return { key: `local:${selectedSlot.instanceId}:${rc.startedAt}`, payload: rc, botProfileId: null };
    return null;
  }, [liveCommand, selectedSlot]);

  // Fill the (disabled) form with that run's config once per run, so reopening the page
  // shows exactly what is running. Not re-applied on every poll; left in place after the
  // run ends so it can be re-run or tweaked.
  const restoredCmdRef = useRef(null);
  const restoredEmailRef = useRef(null); // keeps the run's emailTo when the profile reveal prefills it
  useEffect(() => {
    if (!liveSource || !profilesLoaded || restoredCmdRef.current === liveSource.key) return;
    restoredCmdRef.current = liveSource.key;
    const p = liveSource.payload;
    const opts = FLEET_SHIFT_OPTIONS[p.clientID] || [];
    const bp = profiles.find(x => x._id === liveSource.botProfileId)
      || profiles.find(x => x.clientID === p.clientID && x.userName === p.userName);
    // Only needed when the profile actually changes (that's what triggers the reveal prefill).
    if (bp && bp._id !== profileId) { restoredEmailRef.current = p.emailTo || ''; setProfileId(bp._id); }
    // No saved website profile for this login (e.g. a local run with local credentials):
    // clear the dropdown so it doesn't show some other account, and say which login it was.
    if (!bp) setProfileId('');
    setUnmatchedLogin(bp ? null : { clientID: p.clientID, userName: p.userName });
    setUserDataDir(p.userDataDir || '');
    setForm(f => ({
      ...f,
      startDate: p.startDate || f.startDate, endDate: p.endDate || f.endDate,
      hours: p.hours, minutes: p.minutes, seconds: p.seconds, milliseconds: p.milliseconds,
      InBetweenShift: String(p.InBetweenShift), maxSearchAttempts: String(p.maxSearchAttempts),
      multipleShift: p.multipleShift || 'No', maxShiftsToBook: String(p.maxShiftsToBook || '1'), luxor: p.luxor || 'No',
      skipFirstCriteriaOnReload: p.skipFirstCriteriaOnReload || 'No', skipFirstIfLate: String(p.skipFirstIfLate || '0'),
      emailEnabled: !!p.emailEnabled, emailTo: p.emailTo || ''
    }));
    setCriteria((p.shiftCriteria || []).map(c => {
      const tag = c.ShTagSetCdName == null ? '' : String(c.ShTagSetCdName);
      const known = opts.some(o => o.value === tag);
      return {
        ...blankFleetCriteria(),
        ShTagSetCdName: known ? tag : 'other', custom: known ? '' : tag,
        shdate: c.shdate || '', tmstartOperator: c.tmstartOperator || 'equalTo',
        tmstart: c.tmstart != null ? String(c.tmstart) : '', tmend: c.tmend != null ? String(c.tmend) : '',
        SiteName: c.SiteName || ''
      };
    }));
  }, [liveSource, profiles, profilesLoaded, profileId]);

  // The run form is one shared state, so keep a per-slot draft: switching slots saves
  // the current form under the old slot and brings back the new slot's (e.g. a stopped
  // run's settings stay with its port while you look at another). In-memory only.
  const draftsRef = useRef({});
  function selectSlot(id) {
    if (id === selected) return;
    if (selected) draftsRef.current[selected] = { profileId, userDataDir, form, criteria, unmatchedLogin };
    const d = draftsRef.current[id];
    setSelected(id);
    restoredCmdRef.current = null; // a running slot re-restores from its live command
    if (d) {
      if (d.profileId && d.profileId !== profileId) restoredEmailRef.current = d.form.emailTo;
      setProfileId(d.profileId);
      setUserDataDir(d.userDataDir);
      setForm(d.form);
      setCriteria(d.criteria);
      setUnmatchedLogin(d.unmatchedLogin || null);
    } else {
      setUserDataDir('');
      setUnmatchedLogin(null);
    }
  }

  // Credential dropdown: alphabetical by profile name, shaded by the NAME PREFIX (first
  // word of the profile name). Every profile that starts with the same name shares a
  // color and the next name flips to the other shade — e.g. "Neth Aria"/"Neth MGM" one
  // color, "Ian Aria"/"Ian MGM" the next — for easy scanning.
  const profileGroupKey = p => String(p.profileName || '').trim().split(/\s+/)[0].toLowerCase();
  const profileColoring = useMemo(() => {
    const sorted = [...profiles].sort((a, b) =>
      String(a.profileName || '').toLowerCase().localeCompare(String(b.profileName || '').toLowerCase()));
    const colorByGroup = {};
    let idx = -1;
    sorted.forEach(p => {
      const key = profileGroupKey(p);
      if (!(key in colorByGroup)) { idx += 1; colorByGroup[key] = idx % 2 === 0 ? '#eef2f7' : '#ffffff'; }
    });
    return { sorted, colorByGroup };
  }, [profiles]);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  // Changing seconds/ms re-derives the Booking Delay
  const setSearchTimeF = (k, v) => setForm(f => {
    const next = { ...f, [k]: v };
    return { ...next, InBetweenShift: fleetBookingDelayFor(next.seconds, next.milliseconds) };
  });
  const setCrit = (i, k, v) => setCriteria(cs => cs.map((c, j) => (j === i ? { ...c, [k]: v } : c)));
  const openPicker = e => { try { if (e.target.showPicker) e.target.showPicker(); } catch (err) { /* not supported */ } };

  // Mirrors the local form: if the chosen search time has already passed today, ask
  // whether to run it today anyway, schedule it for tomorrow, or cancel.
  function askPastTime() { return new Promise(resolve => setPastModal({ resolve })); }
  function pickPast(choice) { if (pastModal) pastModal.resolve(choice); setPastModal(null); }

  // Changing a row's shift type or date clears that row's time/room fields, and a date
  // change re-derives Start/End from all target dates — same behavior as the local form.
  function onCritType(i, v) {
    setCriteria(cs => cs.map((c, j) => (j === i
      ? { ...c, ShTagSetCdName: v, custom: v === 'other' ? c.custom : '', tmstart: '', tmend: '', SiteName: '', tmstartOperator: 'equalTo' }
      : c)));
  }
  function onCritDate(i, v) {
    setCriteria(cs => {
      const next = cs.map((c, j) => (j === i ? { ...c, shdate: v, tmstart: '', tmend: '', SiteName: '', tmstartOperator: 'equalTo' } : c));
      const d = fleetAutoDates(next, venue);
      if (d) setForm(f => ({ ...f, startDate: d.startDate, endDate: d.endDate }));
      return next;
    });
  }

  function startNewProfile() {
    if (showProfileForm && !editingId) { setShowProfileForm(false); return; }
    setEditingId(null);
    setProfForm({ profileName: '', clientID: 'Aria', userName: '', passWord: '', pin: '', emailTo: '', ownerClientId: '' });
    setShowProfileForm(true);
  }

  function startEditProfile() {
    if (!selectedProfile) return;
    const r = revealed || {};
    setEditingId(profileId);
    setProfForm({
      profileName: selectedProfile.profileName || '',
      clientID: selectedProfile.clientID || 'Aria',
      userName: (r.userName != null ? r.userName : selectedProfile.userName) || '',
      passWord: r.passWord || '',
      pin: r.pin || '',
      emailTo: r.emailTo || '',
      ownerClientId: selectedProfile.ownerClientId || ''
    });
    setShowProfileForm(true);
  }

  async function saveProfile() {
    if (!profForm.profileName || !profForm.userName) { setMsg('Profile needs a name and username.'); return; }
    try {
      if (editingId) {
        await A.updateBotProfile(editingId, profForm);
        setShowProfileForm(false);
        setEditingId(null);
        await loadProfiles();
        if (profileId) {
          A.revealBotProfile(profileId).then(r => { setRevealed(r); setForm(f => ({ ...f, emailTo: r.emailTo || '' })); }).catch(() => {});
        }
        setMsg('Profile updated.');
      } else {
        const created = await A.createBotProfile(profForm);
        setShowProfileForm(false);
        setProfForm({ profileName: '', clientID: 'Aria', userName: '', passWord: '', pin: '', emailTo: '', ownerClientId: '' });
        await loadProfiles();
        setProfileId(created._id);
        setMsg('Profile saved.');
      }
    } catch (e) { setMsg(e.message); }
  }

  async function deleteProfile() {
    if (!profileId || !window.confirm('Delete this credential profile?')) return;
    try { await A.deleteBotProfile(profileId); setProfileId(''); await loadProfiles(); setMsg('Profile deleted.'); }
    catch (e) { setMsg(e.message); }
  }

  async function doImport() {
    setImportMsg('');
    let rows;
    try { rows = parseFleetImport(importText); }
    catch (e) { setImportMsg('Could not parse — check the CSV/JSON format: ' + e.message); return; }
    if (!rows.length) { setImportMsg('Nothing to import.'); return; }
    try {
      const r = await A.bulkBotProfiles(rows);
      setImportMsg(`Created ${r.created}, skipped ${r.skipped} duplicate(s).` + (r.errors && r.errors.length ? ' Issues: ' + r.errors.join('; ') : ''));
      await loadProfiles();
      if (r.created) setImportText('');
    } catch (e) { setImportMsg(e.message); }
  }

  async function addChromeProfile() {
    if (!selectedSlot) return;
    const path = newChrome.trim();
    if (!path) { setMsg('Enter the folder path (e.g. C:\\1).'); return; }
    try {
      // No separate label — the path is the identity (backend defaults label to it).
      await A.createChromeProfile({ machineId: selectedSlot.machineId, userDataDir: path });
      setNewChrome('');
      await loadSlots();
      setMsg('Chrome folder saved.');
    } catch (e) { setMsg(e.message); }
  }

  async function forgetSlot(s) {
    if (!window.confirm(`Remove ${s.label || s.hostname} :${s.port} from the list?\n\nIts owner and Chrome folders are kept. It reappears the next time its EXE is opened.`)) return;
    try {
      await A.forgetSlot(s.instanceId);
      if (selected === s.instanceId) setSelected('');
      await loadSlots();
    } catch (e) { setMsg(e.message); }
  }

  async function removeChromeProfile(id) {
    try { await A.deleteChromeProfile(id); await loadSlots(); } catch (e) { setMsg(e.message); }
  }

  async function doStart() {
    setMsg('');
    if (!selectedSlot) { setMsg('Pick a slot first.'); return; }
    if (!profileId) { setMsg('Pick a credential profile.'); return; }
    if (!userDataDir) { setMsg('Pick a Chrome profile for this machine.'); return; }
    const chromeUse = fleetChromeClash(allChrome.find(p => p.userDataDir === userDataDir), form.hours, form.minutes);
    if (chromeUse) { setMsg(fleetChromeClashMsg(userDataDir, chromeUse)); return; }
    if (!form.startDate || !form.endDate) { setMsg('Start and end dates are required.'); return; }
    const sDate = new Date(form.startDate), eDate = new Date(form.endDate);
    if (eDate < sDate) { setMsg('Start date cannot be after end date.'); return; }
    const maxDays = fleetMaxRange(venue);
    const rangeDays = Math.floor((eDate - sDate) / 86400000) + 1;
    if (rangeDays > maxDays) { setMsg(`Date range cannot be more than ${maxDays} days for ${venue}.`); return; }

    // Every target shift row needs a date (required on the local form too), and "Other…"
    // needs its custom tag. Previously dateless rows were silently dropped, turning the
    // run into a no-target timings test.
    const missing = criteria.findIndex(critMissing);
    setShowCritErrors(missing !== -1);
    if (missing !== -1) {
      setMsg(`Target shift #${missing + 1} is missing ${critMissing(criteria[missing]).join(' and ')} — fill it in or remove the row.`);
      return;
    }

    // Hard block (like the local form): every target shift date must fall inside the
    // Start–End range, or the run can't cover it. YYYY-MM-DD strings compare directly.
    const outside = criteria.find(c => c.shdate && (c.shdate < form.startDate || c.shdate > form.endDate));
    if (outside) {
      setMsg(`Target shift date ${outside.shdate} is outside the Start–End range (${form.startDate} to ${form.endDate}). Fix the dates or the target before starting.`);
      return;
    }

    // Past-search-time choice, mirroring the local form (no persistent checkbox).
    let startTomorrow = false;
    const chosen = new Date();
    chosen.setHours(parseInt(form.hours, 10), parseInt(form.minutes, 10), parseInt(form.seconds, 10), parseInt(form.milliseconds, 10));
    if (chosen.getTime() <= Date.now()) {
      const choice = await askPastTime();
      if (choice === 'cancel') return;
      startTomorrow = choice === 'tomorrow';
    }

    const payload = {
      clientID: venue,
      userDataDir,
      startDate: form.startDate, endDate: form.endDate,
      hours: form.hours, minutes: form.minutes, seconds: form.seconds, milliseconds: form.milliseconds,
      InBetweenShift: form.InBetweenShift, maxSearchAttempts: form.maxSearchAttempts,
      captchaType: 'localCaptcha', userScriptStartDelay: '0',
      multipleShift: enableMulti ? form.multipleShift : 'No',
      maxShiftsToBook: enableMulti ? form.maxShiftsToBook : '1',
      luxor: enableLuxor ? form.luxor : 'No',
      emailEnabled: form.emailEnabled, emailTo: form.emailTo,
      skipFirstIfLate: form.skipFirstIfLate, skipFirstCriteriaOnReload: form.skipFirstCriteriaOnReload,
      startTomorrow,
      shiftCriteria: criteria.map(c => ({
        ShTagSetCdName: c.ShTagSetCdName === 'other' ? c.custom : c.ShTagSetCdName,
        shdate: c.shdate,
        tmstart: c.tmstart,
        tmend: c.tmend,
        SiteName: c.SiteName ? c.SiteName.trim() : undefined,
        tmstartOperator: c.tmstartOperator
      }))
    };
    setBusy(true);
    try {
      const cmd = await A.startRun({ instanceId: selectedSlot.instanceId, botProfileId: profileId, payload });
      setStarting(s => ({ ...s, [selectedSlot.instanceId]: { commandId: cmd && cmd._id, since: Date.now() } }));
      setMsg('Run queued — the slot picks it up within a few seconds.');
      loadRuns();
    } catch (e) { setMsg(e.message); }
    finally { setBusy(false); }
  }

  async function stopSlot(instanceId) {
    if (stopping[instanceId]) return;
    // Show "Stopping…" right away; the command id is filled in once it's queued.
    setStopping(s => ({ ...s, [instanceId]: { commandId: null, since: Date.now() } }));
    try {
      const cmd = await A.stopRun(instanceId);
      setStopping(s => (s[instanceId] ? { ...s, [instanceId]: { ...s[instanceId], commandId: cmd && cmd._id } } : s));
      setMsg('Stop queued.');
      loadRuns();
    } catch (e) {
      setStopping(s => { const n = { ...s }; delete n[instanceId]; return n; });
      setMsg(e.message);
    }
  }

  // Remote update (admin only). The latest version comes from the active AppUpdates doc.
  const latestVersion = (slots.find(s => s.latestVersion) || {}).latestVersion || null;
  const isOutdated = s => !!latestVersion && compareVersions(latestVersion, s.version) > 0;
  const canUpdateNow = s => !!A.updateSlot && isOutdated(s) && s.canRemoteUpdate && s.online && s.status !== 'running' && !updating[s.instanceId] && !starting[s.instanceId];
  // Test build (App Updates → Test version): offered only on admin-owned slots whose build
  // understands it (canTestUpdate). "Update all idle" never uses it.
  const testVersion = (slots.find(s => s.testVersion) || {}).testVersion || null;
  const canTestNow = s => !!A.updateSlot && !!testVersion && testVersion !== s.version && s.ownerType === 'admin' && s.canTestUpdate && s.online && s.status !== 'running' && !updating[s.instanceId] && !starting[s.instanceId];

  async function updateSlots(list, channel) {
    const target = channel === 'test' ? `test ${testVersion}` : latestVersion;
    const queued = [];
    const errors = [];
    for (const s of list) {
      try {
        const cmd = await A.updateSlot(s.instanceId, channel);
        queued.push(s);
        setUpdating(u => ({ ...u, [s.instanceId]: { commandId: cmd && cmd._id, since: Date.now(), from: s.version } }));
      } catch (e) { errors.push(`port ${s.port}: ${e.message}`); }
    }
    loadRuns();
    if (errors.length) setMsg(`Some updates were not queued — ${errors.join('; ')}`);
    else if (queued.length) setMsg(`Update queued for ${queued.length} slot${queued.length > 1 ? 's' : ''}. Each app will close and reopen on ${target}.`);
  }

  function confirmUpdate(list, channel) {
    if (!list.length) return;
    const names = list.map(s => `${s.label || s.hostname} :${s.port}`).join('\n');
    const target = channel === 'test' ? `TEST version ${testVersion}` : latestVersion;
    if (window.confirm(`Update to ${target}?\n\n${names}\n\nEach app closes and reopens by itself (about a minute). Running slots are never updated.`)) updateSlots(list, channel);
  }

  // Assign one port (scope 'port') or the whole machine to Admin or a client. value is
  // 'admin', a client _id, or 'inherit' (port only: drop its own owner, use the machine's).
  async function assignOwner(s, scope, value) {
    try {
      const ownerType = value === 'admin' || value === 'inherit' ? value : 'client';
      await api.setDeviceBinding(s.machineId, ownerType, ownerType === 'client' ? value : null, scope, scope === 'port' ? s.instanceId : undefined);
      await loadSlots();
      setMsg('Assigned.');
    } catch (e) { setMsg(e.message); }
  }

  return (
    <div>
      {/* Success messages stay as a calm banner; anything else (blocked/error/validation)
          is a centered pop-up that must be dismissed, so it can't be missed. */}
      {msg && (/^(Profile (saved|updated|deleted)\.|Chrome folder saved\.|Run queued|Stop queued\.|Assigned\.|Update queued|Updated:)/.test(msg)
        ? <div className="card" style={{ color: '#2f855a' }}>{msg}</div>
        : (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}
            onClick={() => setMsg('')}>
            <div className="card" role="alertdialog" aria-modal="true" onClick={e => e.stopPropagation()}
              style={{ maxWidth: 420, margin: 16, border: '2px solid #fc8181', background: '#fff5f5' }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', color: '#c53030', fontWeight: 600 }}>
                <span style={{ fontSize: 22, lineHeight: 1.1 }}>⚠️</span><span>{msg}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
                <button className="btn btn-primary" autoFocus onClick={() => setMsg('')}>OK</button>
              </div>
            </div>
          </div>
        ))}

      {pastModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="card" style={{ maxWidth: 360, margin: 16 }}>
            <h3 style={{ marginTop: 0 }}>Search time already passed</h3>
            <p style={{ color: '#4a5568' }}>The search time you chose is earlier than right now. Run it today anyway, or schedule it for tomorrow?</p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn btn-sm btn-primary" onClick={() => pickPast('now')}>Start today</button>
              <button className="btn btn-sm btn-secondary" onClick={() => pickPast('tomorrow')}>Start tomorrow</button>
              <button className="btn btn-sm btn-danger" onClick={() => pickPast('cancel')}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h3 style={{ margin: 0 }}>Fleet</h3>
          <button className="btn btn-sm btn-secondary" onClick={loadSlots}>Refresh</button>
          {isAdmin && (
            <select value={ownerFilter} onChange={e => setOwnerFilter(e.target.value)} style={{ fontSize: 13 }}>
              <option value="admin">Admin</option>
              {ownerOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
              <option value="all">All owners</option>
            </select>
          )}
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ fontSize: 13 }}>
            <option value="all">Online + offline</option>
            <option value="online">Online</option>
            <option value="offline">Offline</option>
          </select>
          {A.updateSlot && visibleSlots.some(canUpdateNow) && (
            <button className="btn btn-sm btn-primary" onClick={() => confirmUpdate(visibleSlots.filter(canUpdateNow))}>
              Update all idle ({visibleSlots.filter(canUpdateNow).length}) to {latestVersion}
            </button>
          )}
          <span style={{ marginLeft: 'auto', color: '#718096', fontSize: 13 }}>
            {visibleSlots.length}{visibleSlots.length !== slots.length ? ` of ${slots.length}` : ''} slot{slots.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="table-wrap" style={{ marginTop: 12 }}>
          <table>
            <thead><tr><th></th><th>Machine</th><th>Port</th>{isAdmin && <th>Owner</th>}{A.updateSlot && <th>Version</th>}<th>Status</th><th>Running</th><th></th></tr></thead>
            <tbody>
              {visibleSlots.map(s => (
                <tr key={s.instanceId} style={{ background: selected === s.instanceId ? '#ebf8ff' : undefined, cursor: 'pointer' }} onClick={() => selectSlot(s.instanceId)}>
                  <td><input type="radio" readOnly checked={selected === s.instanceId} /></td>
                  <td>
                    {s.label || s.hostname}
                    {dupLabels.has(s.label || s.hostname || '') && <span style={{ color: '#a0aec0' }}> #{String(s.machineId).slice(0, 6)}</span>}
                    {' '}<span title={s.online ? 'online' : 'offline'} style={{ color: s.online ? '#38a169' : '#a0aec0' }}>●</span>
                  </td>
                  <td>{s.port}</td>
                  {isAdmin && (
                  <td onClick={e => e.stopPropagation()}>
                    {editingOwner === s.instanceId ? (
                      <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                        <select value={ownerScope} onChange={e => { setOwnerScope(e.target.value); if (e.target.value === 'machine' && ownerDraft === 'inherit') setOwnerDraft('admin'); }} style={{ fontSize: 12 }}>
                          <option value="port">This port only</option>
                          <option value="machine">Whole machine</option>
                        </select>
                        <select value={ownerDraft} onChange={e => setOwnerDraft(e.target.value)} style={{ fontSize: 12, maxWidth: 150 }}>
                          {ownerScope === 'port' && <option value="inherit">Same as machine ({s.machineOwnerName || 'Admin'})</option>}
                          <option value="admin">Admin</option>
                          {clients.map(c => <option key={c._id} value={c._id}>{c.clientName}</option>)}
                        </select>
                        <button className="btn btn-sm btn-primary" onClick={async () => { await assignOwner(s, ownerScope, ownerDraft); setEditingOwner(null); }}>Save</button>
                        <button className="btn btn-sm btn-secondary" onClick={() => setEditingOwner(null)}>Cancel</button>
                      </span>
                    ) : (
                      <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                        <span style={{ fontSize: 12 }}>{s.ownerName || 'Admin'}</span>
                        {s.ownerScope === 'port' && <span title="Assigned to this port only; the machine's other ports keep their own owner." style={{ fontSize: 11, color: '#718096' }}>(this port)</span>}
                        <button className="btn btn-sm btn-secondary" onClick={() => {
                          setEditingOwner(s.instanceId);
                          setOwnerScope('port');
                          setOwnerDraft(s.ownerScope !== 'port' ? 'inherit' : s.ownerType === 'client' && s.portalClientId ? s.portalClientId : 'admin');
                        }}>Edit</button>
                      </span>
                    )}
                  </td>
                  )}
                  {A.updateSlot && (
                  <td onClick={e => e.stopPropagation()} style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                    {s.version || '?'}
                    {updating[s.instanceId] ? <span style={{ color: '#b7791f' }}> · updating…</span> : <>
                      {isOutdated(s) && (
                        canUpdateNow(s) ? <> <button className="btn btn-sm btn-primary" onClick={() => confirmUpdate([s])}>Update</button></>
                        : !s.canRemoteUpdate ? <span title="This version can't be updated from the website. Update it on the PC once; later versions can." style={{ color: '#a0aec0' }}> · update on PC</span>
                        : s.status === 'running' ? <span title="Updates wait until the run ends." style={{ color: '#a0aec0' }}> · after run</span>
                        : <span style={{ color: '#a0aec0' }}> · {latestVersion} available</span>
                      )}
                      {canTestNow(s) && <> <button className="btn btn-sm btn-secondary" title="Install the App Updates test build on this slot only." onClick={() => confirmUpdate([s], 'test')}>Test {testVersion}</button></>}
                    </>}
                  </td>
                  )}
                  <td>{!s.online && !starting[s.instanceId] && !updating[s.instanceId]
                    ? <span className="badge" title={s.lastSeen ? `Last seen ${new Date(s.lastSeen).toLocaleString()}` : 'EXE closed'} style={{ background: '#edf2f7', color: '#718096' }}>offline</span>
                    : <span className={`badge ${s.status === 'running' || starting[s.instanceId] || updating[s.instanceId] ? 'badge-warn' : 'badge-paid'}`}>{s.status === 'running' && stopping[s.instanceId] ? 'stopping…' : s.status !== 'running' && starting[s.instanceId] ? 'starting…' : updating[s.instanceId] ? 'updating…' : s.status}</span>}</td>
                  <td style={{ fontSize: 12, color: '#718096' }}>
                    {s.currentRun ? <>
                      {s.runSource && <><RunSourceBadge via={s.runSource.startedVia} by={s.runSource.startedBy} />{' '}</>}
                      {`${s.currentRun.userDataDir || '(chrome?)'} · ${s.currentRun.clientID}/${s.currentRun.userName} @ ${s.currentRun.searchTime}`}
                    </> : '—'}
                  </td>
                  <td>
                    {s.status === 'running' && <button className="btn btn-sm btn-danger" disabled={!!stopping[s.instanceId]} style={{ opacity: stopping[s.instanceId] ? 0.5 : 1 }} onClick={(e) => { e.stopPropagation(); stopSlot(s.instanceId); }}>{stopping[s.instanceId] ? 'Stopping…' : 'Stop'}</button>}
                    {s.status === 'offline' && A.forgetSlot && <button className="btn btn-sm btn-secondary" title="Remove this closed slot from the list. It comes back on its own the next time its EXE is opened." onClick={(e) => { e.stopPropagation(); forgetSlot(s); }}>Forget</button>}
                  </td>
                </tr>
              ))}
              {slots.length === 0 && <tr><td colSpan={6 + (isAdmin ? 1 : 0) + (A.updateSlot ? 1 : 0)} style={{ color: '#718096' }}>No bots yet. Start a BQTExpress instance on a PC.</td></tr>}
              {slots.length > 0 && visibleSlots.length === 0 && <tr><td colSpan={6 + (isAdmin ? 1 : 0) + (A.updateSlot ? 1 : 0)} style={{ color: '#718096' }}>No slots match these filters.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {selectedSlot && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Start a run — {selectedSlot.label || selectedSlot.hostname} · port {selectedSlot.port}</h3>

          <fieldset disabled={uiDisabled} style={{ border: 0, margin: 0, padding: 0, minInlineSize: 0, opacity: uiDisabled ? 0.55 : 1 }}>
          <div className="form-group">
            <label>Credential Profile<ReqMark /></label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <select value={profileId} onChange={e => setProfileId(e.target.value)} style={{ minWidth: 240 }}>
                <option value="">Select a profile…</option>
                {profileColoring.sorted.map(p => (
                  <option key={p._id} value={p._id} style={{ backgroundColor: profileColoring.colorByGroup[profileGroupKey(p)] }}>
                    {p.profileName} ({p.clientID}/{p.userName})
                  </option>
                ))}
              </select>
              <button className="btn btn-sm btn-secondary" onClick={startNewProfile}>{showProfileForm && !editingId ? 'Close' : '+ New Profile'}</button>
              {profileId && <button className="btn btn-sm btn-secondary" onClick={startEditProfile}>Edit</button>}
              {profileId && <button className="btn btn-sm btn-danger" onClick={deleteProfile}>Delete</button>}
              <button className="btn btn-sm btn-secondary" onClick={() => setShowImport(v => !v)}>{showImport ? 'Close Import' : 'Import list'}</button>
            </div>
            {unmatchedLogin && !profileId && (
              <div style={{ fontSize: 12, color: '#b7791f', marginTop: 4 }}>
                This run used {unmatchedLogin.clientID} / {unmatchedLogin.userName}, which has no saved website profile (e.g. started from the PC with its local login). Pick or create a profile for it to start it from here.
              </div>
            )}
          </div>

          {showProfileForm && (
            <div className="card" style={{ background: '#f7f8fa' }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>{editingId ? 'Edit profile' : 'New profile'}</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <input placeholder="Profile name *" value={profForm.profileName} onChange={e => setProfForm(f => ({ ...f, profileName: e.target.value }))} />
                <select value={profForm.clientID} onChange={e => setProfForm(f => ({ ...f, clientID: e.target.value }))}>
                  {FLEET_VENUES.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                </select>
                <input placeholder="Username *" value={profForm.userName} onChange={e => setProfForm(f => ({ ...f, userName: e.target.value }))} />
                <input placeholder="Password" value={profForm.passWord} onChange={e => setProfForm(f => ({ ...f, passWord: e.target.value }))} />
                <input placeholder="PIN" value={profForm.pin} onChange={e => setProfForm(f => ({ ...f, pin: e.target.value }))} style={{ width: 80 }} />
                <input placeholder="Email (optional)" value={profForm.emailTo} onChange={e => setProfForm(f => ({ ...f, emailTo: e.target.value }))} />
                {isAdmin && (
                  <select value={profForm.ownerClientId || ''} onChange={e => setProfForm(f => ({ ...f, ownerClientId: e.target.value }))} title="Who owns this profile">
                    <option value="">Owner: Admin / global</option>
                    {clients.map(c => <option key={c._id} value={c._id}>Owner: {c.clientName}</option>)}
                  </select>
                )}
                <button className="btn btn-sm btn-primary" onClick={saveProfile}>Save</button>
              </div>
            </div>
          )}

          {showImport && (
            <div className="card" style={{ background: '#f7f8fa' }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Import credential profiles</div>
              <div style={{ fontSize: 12, color: '#718096', marginBottom: 6 }}>
                Paste CSV lines — <code>profileName, venue, username, password, pin, email</code> — one per line, or a JSON array. Your <code>profiles.json</code> content works as-is. Duplicates (same venue + username) are skipped.
              </div>
              <textarea
                value={importText}
                onChange={e => setImportText(e.target.value)}
                rows={6}
                style={{ width: '100%', fontFamily: 'monospace', fontSize: 12 }}
                placeholder={'Aria Morning, Aria, myuser, mypass, 1234, me@example.com\n— or —\n[{"clientID":"Aria","userName":"myuser","passWord":"mypass","pin":"1234"}]'}
              />
              <div style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button className="btn btn-sm btn-primary" onClick={doImport}>Parse &amp; Save</button>
                {importMsg && <span style={{ fontSize: 12, color: '#2b6cb0' }}>{importMsg}</span>}
              </div>
            </div>
          )}

          {selectedProfile && revealed && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Username</label>
                  <input value={revealed.userName || ''} disabled style={{ background: '#edf0f5' }} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Password</label>
                  <input value={revealed.passWord || ''} disabled style={{ background: '#edf0f5' }} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>PIN</label>
                  <input value={revealed.pin || ''} disabled style={{ background: '#edf0f5', width: 90 }} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Email</label>
                  <input value={revealed.emailTo || ''} disabled style={{ background: '#edf0f5' }} />
                </div>
              </div>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10, fontWeight: 'normal', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.emailEnabled} onChange={e => setF('emailEnabled', e.target.checked)} style={{ width: 'auto', margin: 0 }} />
                Send email notifications
              </label>
            </div>
          )}

          </fieldset>

          {/* Outside the disabled fieldset: Chrome folders are per machine, so they can be
              added/removed while this port runs — only the run's own pick stays locked. */}
          <div className="form-group">
            <label>Chrome Profile (this machine)<ReqMark /></label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <select value={userDataDir} onChange={e => { setUserDataDir(e.target.value); setChromeSwitchNote(''); }} disabled={uiDisabled} style={{ minWidth: 240, opacity: uiDisabled ? 0.55 : 1 }}>
                <option value="">Select a Chrome folder…</option>
                {availableChrome.map(p => (
                  <option key={p._id || p.userDataDir} value={p.userDataDir}>
                    {p.userDataDir}
                    {!uiDisabled && p.inUse && p.inUse.length ? ` — in use on ${p.inUse.map(u => `port ${u.port} at ${fleetHHMM(u.searchTime)}`).join(', ')}` : ''}
                  </option>
                ))}
                {/* A restored run may use a folder no longer in this machine's list — still show it. */}
                {userDataDir && !availableChrome.some(p => p.userDataDir === userDataDir) && <option value={userDataDir}>{userDataDir}</option>}
              </select>
              <button className="btn btn-sm btn-secondary" onClick={() => setShowChromeForm(v => !v)}>{showChromeForm ? 'Close' : '+ Add / Manage'}</button>
            </div>
            {!uiDisabled && chromeSwitchNote && (
              <div style={{ fontSize: 12, color: '#b26a00', marginTop: 4 }}>{chromeSwitchNote}</div>
            )}
            {!uiDisabled && busyChromeCount > 0 && (
              <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                {busyChromeCount === 1 ? '1 folder is' : `${busyChromeCount} folders are`} hidden — in use on another port of this machine within 13 minutes of this search time.
              </div>
            )}
            {showChromeForm && (
              <div className="card" style={{ background: '#f7f8fa' }}>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <input placeholder="Folder path (e.g. C:\1)" value={newChrome} onChange={e => setNewChrome(e.target.value)} style={{ minWidth: 260 }} />
                  <button className="btn btn-sm btn-primary" onClick={addChromeProfile}>Save</button>
                </div>
                {(selectedSlot.chromeProfiles || []).length > 0 && (
                  <div style={{ marginTop: 8, fontSize: 12, color: '#718096' }}>
                    {selectedSlot.chromeProfiles.map(p => (
                      <div key={p._id || p.userDataDir} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <span style={{ fontFamily: 'monospace' }}>{p.userDataDir}</span>
                        {p._id && <button className="btn btn-sm btn-danger" onClick={() => removeChromeProfile(p._id)}>✕</button>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <fieldset disabled={uiDisabled} style={{ border: 0, margin: 0, padding: 0, minInlineSize: 0, opacity: uiDisabled ? 0.55 : 1 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="form-group" style={{ minWidth: 150, flex: '0 0 auto' }}><label>Start Date<ReqMark /></label><input type="date" value={form.startDate} onChange={e => setF('startDate', e.target.value)} onClick={openPicker} onFocus={openPicker} style={{ maxWidth: '100%' }} /></div>
            <div className="form-group" style={{ minWidth: 150, flex: '0 0 auto' }}><label>End Date<ReqMark /></label><input type="date" value={form.endDate} onChange={e => setF('endDate', e.target.value)} onClick={openPicker} onFocus={openPicker} style={{ maxWidth: '100%' }} /></div>
            <div className="form-group" style={{ minWidth: 110, flex: '0 0 auto' }}><label>Max Attempts</label><input type="number" value={form.maxSearchAttempts} onChange={e => setF('maxSearchAttempts', e.target.value)} style={{ width: 70 }} /></div>
            <div className="form-group" style={{ minWidth: 120, flex: '0 0 auto' }}><label>Booking Delay</label>
              <select value={form.InBetweenShift} onChange={e => setF('InBetweenShift', e.target.value)} style={{ maxWidth: '100%' }}>
                {FLEET_BOOKING_DELAYS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div className="form-group"><label>Skip 1st on Early Search</label>
              <select value={form.skipFirstCriteriaOnReload} onChange={e => setF('skipFirstCriteriaOnReload', e.target.value)}><option>No</option><option>Yes</option></select>
            </div>
            <div className="form-group"><label>Skip 1st on Late Return</label>
              <select value={form.skipFirstIfLate} onChange={e => setF('skipFirstIfLate', e.target.value)}>
                {FLEET_SKIP_LATE.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            {enableMulti && <>
              <div className="form-group"><label>Multi-Shift</label><select value={form.multipleShift} onChange={e => setF('multipleShift', e.target.value)}><option>No</option><option>Yes</option></select></div>
              <div className="form-group"><label># Shifts</label><select value={form.maxShiftsToBook} onChange={e => setF('maxShiftsToBook', e.target.value)}>{[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}</select></div>
            </>}
            {enableLuxor && <div className="form-group"><label>Luxor</label><select value={form.luxor} onChange={e => setF('luxor', e.target.value)}><option>No</option><option>Yes</option></select></div>}
          </div>

          <div className="form-group">
            <label>Search Time<ReqMark /></label>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center', fontSize: 13 }}>
              <select value={form.hours} onChange={e => setF('hours', e.target.value)} style={{ width: 96 }}>
                {FLEET_HOURS.map(h => <option key={h.value} value={h.value}>{h.label}</option>)}
              </select>
              <span>:</span>
              <select value={form.minutes} onChange={e => setF('minutes', e.target.value)} style={{ width: 56 }}>{Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0')).map(m => <option key={m} value={m}>{m}</option>)}</select>
              <span>:</span>
              <select value={form.seconds} onChange={e => setSearchTimeF('seconds', e.target.value)} style={{ width: 56 }}>{Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0')).map(s => <option key={s} value={s}>{s}</option>)}</select>
              <span>:</span>
              <input value={form.milliseconds} onChange={e => setSearchTimeF('milliseconds', e.target.value)} style={{ width: 46 }} maxLength={3} />
            </div>
          </div>

          <div className="form-group">
            <label>Target Shifts</label>
            {criteria.map((c, i) => {
              const lbl = { display: 'inline-flex', flexDirection: 'column', fontSize: 11, color: '#718096' };
              const bad = showCritErrors ? { border: '2px solid #e53e3e' } : null;
              const tint = FLEET_CRIT_COLORS[i % FLEET_CRIT_COLORS.length];
              return (
              <div key={i} style={{ marginBottom: 12, padding: 8, background: tint.bg, border: `1px solid ${tint.border}`, borderLeft: `5px solid ${tint.border}`, borderRadius: 6 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: tint.text, marginBottom: 6 }}>Target Shift #{i + 1}</div>
                {/* Row 1 — shift type + date */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 8 }}>
                  <label style={lbl}>
                    Shift type
                    <select value={c.ShTagSetCdName} onChange={e => onCritType(i, e.target.value)}>
                      <option value="">Shift type…</option>
                      {shiftOpts.map((o, k) => <option key={k} value={o.value}>{o.label}</option>)}
                      <option value="other">Other…</option>
                    </select>
                  </label>
                  {c.ShTagSetCdName === 'other' && (
                    <label style={lbl}>
                      <span>Custom tag<ReqMark /></span>
                      <input className="fleet-hint" placeholder="Custom tag" value={c.custom} onChange={e => setCrit(i, 'custom', e.target.value)} style={{ width: 120, ...(!String(c.custom || '').trim() && bad) }} />
                    </label>
                  )}
                  <label style={lbl}>
                    <span>Date<ReqMark /></span>
                    <input type="date" value={c.shdate} onChange={e => onCritDate(i, e.target.value)} onClick={openPicker} onFocus={openPicker} style={!c.shdate ? bad || undefined : undefined} />
                  </label>
                  <button className="btn btn-sm btn-danger" onClick={() => setCriteria(cs => cs.filter((_, j) => j !== i))} style={{ marginLeft: 'auto' }}>✕</button>
                </div>
                {/* Row 2 — operator + start / end / room */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <label style={lbl}>
                    Start
                    <span style={{ display: 'inline-flex', gap: 4 }}>
                      <select value={c.tmstartOperator} onChange={e => setCrit(i, 'tmstartOperator', e.target.value)} style={{ width: 54 }}><option value="equalTo">=</option><option value="equalToOrGreater">&gt;=</option></select>
                      <input className="fleet-hint" placeholder="e.g. 900" value={c.tmstart} onChange={e => setCrit(i, 'tmstart', e.target.value)} style={{ width: 70 }} />
                    </span>
                  </label>
                  <label style={lbl}>
                    End
                    <input className="fleet-hint" placeholder="e.g. 1700" value={c.tmend} onChange={e => setCrit(i, 'tmend', e.target.value)} style={{ width: 80 }} />
                  </label>
                  <label style={lbl}>
                    Room
                    <input className="fleet-hint" placeholder="e.g. Pinyon Ball 6,7,8" value={c.SiteName} onChange={e => setCrit(i, 'SiteName', e.target.value)} style={{ width: 150 }} />
                  </label>
                </div>
              </div>
              );
            })}
            {criteria.length === 0 && (
              <div style={{ color: '#718096', fontSize: 13, marginBottom: 6 }}>
                No target shift — the run will just test timings. Add one to actually book.
              </div>
            )}
            <button className="btn btn-sm btn-secondary" onClick={() => setCriteria(cs => [...cs, blankFleetCriteria()])}>+ Add Target Shift</button>
          </div>
          </fieldset>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              className="btn btn-primary"
              disabled={busy || uiDisabled || !selectedSlot.online || !!starting[selectedSlot.instanceId] || !!updating[selectedSlot.instanceId]}
              style={{ opacity: (busy || uiDisabled || !selectedSlot.online || starting[selectedSlot.instanceId] || updating[selectedSlot.instanceId]) ? 0.5 : 1 }}
              onClick={doStart}
            >{busy ? 'Queuing…' : starting[selectedSlot.instanceId] ? 'Starting…' : updating[selectedSlot.instanceId] ? 'Updating…' : 'Start Run'}</button>
            {selectedSlot.status === 'running' && (
              <button className="btn btn-danger" disabled={!!stopping[selectedSlot.instanceId]} style={{ opacity: stopping[selectedSlot.instanceId] ? 0.5 : 1 }} onClick={() => stopSlot(selectedSlot.instanceId)}>{stopping[selectedSlot.instanceId] ? 'Stopping…' : 'Stop Run'}</button>
            )}
            {selectedSlot.status === 'running' && (
              <button className="btn btn-secondary" onClick={() => setShowLog(v => !v)}>{showLog ? 'Hide Log' : 'View Log'}</button>
            )}
            {selectedSlot.status === 'running' && (
              <span style={{ fontSize: 12, color: '#718096' }}>This slot is already running — stop it to start a new run.</span>
            )}
            {!selectedSlot.online && selectedSlot.status !== 'running' && (
              <span style={{ fontSize: 12, color: '#718096' }}>This slot is offline — you can still set its owner, Chrome folders and profiles. Open its EXE on the PC to start a run.</span>
            )}
          </div>
          {/* Live viewing only — the panel disappears when the run ends (finished runs are in the Logs tab). */}
          {showLog && selectedSlot.status === 'running' && <FleetLogPanel A={A} instanceId={selectedSlot.instanceId} onClose={() => setShowLog(false)} />}
        </div>
      )}

      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-sm btn-secondary" onClick={() => setShowRuns(v => !v)} style={{ minWidth: 28 }}>{showRuns ? '▾' : '▸'}</button>
          <h3 style={{ margin: 0, cursor: 'pointer' }} onClick={() => setShowRuns(v => !v)}>Recent Commands</h3>
          {showRuns && <button className="btn btn-sm btn-secondary" onClick={loadRuns}>Refresh</button>}
          {!showRuns && runs.length > 0 && <span style={{ fontSize: 12, color: '#718096' }}>{runs.length} shown</span>}
        </div>
        {showRuns && (
        <div className="table-wrap" style={{ marginTop: 12 }}>
          <table>
            <thead><tr><th>When</th><th>Slot</th><th>Action</th><th>Status</th><th>Detail</th></tr></thead>
            <tbody>
              {runs.map(r => (
                <tr key={r._id}>
                  <td style={{ fontSize: 12 }}>{new Date(r.createdAt).toLocaleString()}</td>
                  <td style={{ fontSize: 12, fontFamily: 'monospace' }}>{r.instanceId}</td>
                  <td>{r.action}</td>
                  <td><span className={`badge ${r.status === 'failed' ? 'badge-nocharge' : r.status === 'started' ? 'badge-warn' : 'badge-paid'}`}>{r.status}</span></td>
                  <td style={{ fontSize: 12, color: '#718096' }}>
                    {r.result ? (r.result.message || (r.result.conflict ? 'Account already running on another machine' : '')) : (r.payload && r.payload.clientID ? r.payload.clientID : '')}
                  </td>
                </tr>
              ))}
              {runs.length === 0 && <tr><td colSpan={5} style={{ color: '#718096' }}>No commands yet.</td></tr>}
            </tbody>
          </table>
        </div>
        )}
      </div>
    </div>
  );
}

const TABS = ['Bills', 'Bookings', 'Requests', 'Clients', 'Unidentified', 'Pricing', 'Commissions', 'Timings', 'Preshift', 'Logs', 'Active Runs', 'Fleet', 'Email', 'Version'];

export default function AdminDashboard() {
  const [tab, setTab] = useState('Bills');
  const [unidCount, setUnidCount] = useState(0);
  const navigate = useNavigate();
  const tabsRef = useRef(null);

  // The tab strip scrolls sideways on a phone, so a tab tapped near the edge can end
  // up half off-screen. Pull the selected one into view. block:'nearest' keeps this
  // from scrolling the page itself.
  useEffect(() => {
    const el = tabsRef.current?.querySelector('.tab.active');
    if (el?.scrollIntoView) el.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
  }, [tab]);

  useEffect(() => {
    api.getUnidentified().then(data => {
      setUnidCount(data.reduce((s, d) => s + d.shifts.length, 0));
    }).catch(() => {});
  }, [tab]);

  // Header bell: requests not marked running yet, for today or later (past-date
  // ones linger until cleanup but there's nothing left to book). Polled so a new
  // request shows up without a page refresh.
  const [pendingRequests, setPendingRequests] = useState(0);
  const refreshPendingRequests = useCallback(() => {
    const today = todayPacific();
    api.getShiftRequests('requested')
      .then(rows => setPendingRequests(rows.filter(r => r.shdate >= today).length))
      .catch(() => {});
  }, []);
  useEffect(() => { refreshPendingRequests(); }, [tab, refreshPendingRequests]);
  useEffect(() => {
    const id = setInterval(refreshPendingRequests, 60 * 1000);
    return () => clearInterval(id);
  }, [refreshPendingRequests]);

  function logout() { localStorage.clear(); navigate('/admin/login'); }

  return (
    <>
      <header className="app-header">
        <h1>Shift Billing — Admin</h1>
        <div className="header-actions">
          {pendingRequests > 0 && (
            <button
              className="request-bell"
              onClick={() => setTab('Requests')}
              title={`${pendingRequests} request${pendingRequests === 1 ? '' : 's'} not running yet`}
              aria-label={`${pendingRequests} request${pendingRequests === 1 ? '' : 's'} not running yet`}
            >
              🔔<span className="unid-badge">{pendingRequests}</span>
            </button>
          )}
          <button className="btn btn-secondary btn-sm" onClick={logout}>Sign Out</button>
        </div>
      </header>
      <div className="page">
        {/* Phone: a dropdown. Twelve tabs never fit a phone strip, and scrolling to
            reach one is worse than picking it from a list. CSS swaps which of these
            two is shown — see .tab-select / .tabs in index.css. */}
        <select
          className="tab-select"
          value={tab}
          onChange={e => setTab(e.target.value)}
          aria-label="Section"
        >
          {TABS.map(t => (
            <option key={t} value={t}>
              {t}{t === 'Unidentified' && unidCount > 0 ? ` (${unidCount})` : ''}
            </option>
          ))}
        </select>

        {/* Desktop: the full tab strip. */}
        <div className="tabs" ref={tabsRef}>
          {TABS.map(t => (
            <button key={t} className={`tab${tab === t ? ' active' : ''}`} onClick={() => setTab(t)}>
              {t}
              {t === 'Unidentified' && unidCount > 0 && <span className="unid-badge">{unidCount}</span>}
            </button>
          ))}
        </div>
        {tab === 'Bills' && <BillsTab />}
        {tab === 'Bookings' && <RecentBookingsTab />}
        {tab === 'Requests' && <RequestsTab onChange={refreshPendingRequests} />}
        {tab === 'Clients' && <ClientsTab />}
        {tab === 'Unidentified' && <UnidentifiedTab />}
        {tab === 'Pricing' && <PricingTab />}
        {tab === 'Commissions' && <CommissionsTab />}
        {tab === 'Timings' && <TimingsTab />}
        {tab === 'Preshift' && <PreshiftTab />}
        {tab === 'Logs' && <LogsTab />}
        {tab === 'Active Runs' && <ActiveRunsTab />}
        {tab === 'Fleet' && <FleetTab />}
        {tab === 'Email' && <SendEmailTab />}
        {tab === 'Version' && <VersionTab />}
      </div>
    </>
  );
}
